import { createRequire } from "module";
import { Agent, isSupportedLanguageModel, tryGenerateWithJsonFallback, tryStreamWithJsonFallback } from "@mastra/core/agent";
import { InMemoryStore } from "@mastra/core/storage";
import { Memory } from "@mastra/memory";
import { exec, execFile, spawn } from "child_process";
import { copyFile, mkdir, mkdtemp, readFile, readdir, rm, stat, writeFile } from "fs/promises";
import { basename, dirname, extname, isAbsolute, join, relative, resolve } from "path";
import { createTool } from "@mastra/core/tools";
import ignore from "ignore";
import { z } from "zod";
import { existsSync, readFileSync } from "fs";
import { promisify } from "util";
import { ModelRouterLanguageModel } from "@mastra/core/llm";
import { tmpdir } from "os";
import { openai } from "@ai-sdk/openai";
import { toStandardSchema } from "@mastra/core/schema";
import { createStep, createWorkflow } from "@mastra/core/workflows";
import { standardSchemaToJSONSchema } from "@mastra/schema-compat/schema";
import * as z4 from "zod/v4";
import { z as z$1 } from "zod/v4";
import { ZodFirstPartyTypeKind } from "zod/v3";
//#region src/types.ts
const UNIT_KINDS = [
	"mcp-server",
	"tool",
	"workflow",
	"agent",
	"integration",
	"network",
	"other"
];
const TemplateUnitSchema = z.object({
	kind: z.enum(UNIT_KINDS),
	id: z.string(),
	file: z.string()
});
z.object({
	slug: z.string(),
	ref: z.string().optional(),
	description: z.string().optional(),
	units: z.array(TemplateUnitSchema)
});
const AgentBuilderInputSchema = z.object({
	repo: z.string().describe("Git URL or local path of the template repo"),
	ref: z.string().optional().describe("Tag/branch/commit to checkout (defaults to main/master)"),
	slug: z.string().optional().describe("Slug for branch/scripts; defaults to inferred from repo"),
	targetPath: z.string().optional().describe("Project path to merge into; defaults to current directory"),
	variables: z.record(z.string(), z.string()).optional().describe("Environment variables to set in .env file")
});
z.object({
	slug: z.string(),
	commitSha: z.string(),
	templateDir: z.string(),
	units: z.array(TemplateUnitSchema)
});
const CopiedFileSchema = z.object({
	source: z.string(),
	destination: z.string(),
	unit: z.object({
		kind: z.enum(UNIT_KINDS),
		id: z.string()
	})
});
const ConflictSchema = z.object({
	unit: z.object({
		kind: z.enum(UNIT_KINDS),
		id: z.string()
	}),
	issue: z.string(),
	sourceFile: z.string(),
	targetFile: z.string()
});
const FileCopyInputSchema = z.object({
	orderedUnits: z.array(TemplateUnitSchema),
	templateDir: z.string(),
	commitSha: z.string(),
	slug: z.string(),
	targetPath: z.string().optional(),
	variables: z.record(z.string(), z.string()).optional()
});
const FileCopyResultSchema = z.object({
	success: z.boolean(),
	copiedFiles: z.array(CopiedFileSchema),
	conflicts: z.array(ConflictSchema),
	message: z.string(),
	error: z.string().optional()
});
const ConflictResolutionSchema = z.object({
	unit: z.object({
		kind: z.enum(UNIT_KINDS),
		id: z.string()
	}),
	issue: z.string(),
	resolution: z.string()
});
const IntelligentMergeInputSchema = z.object({
	conflicts: z.array(ConflictSchema),
	copiedFiles: z.array(CopiedFileSchema),
	templateDir: z.string(),
	commitSha: z.string(),
	slug: z.string(),
	targetPath: z.string().optional(),
	branchName: z.string().optional()
});
const IntelligentMergeResultSchema = z.object({
	success: z.boolean(),
	applied: z.boolean(),
	message: z.string(),
	conflictsResolved: z.array(ConflictResolutionSchema),
	error: z.string().optional()
});
const ValidationResultsSchema = z.object({
	valid: z.boolean(),
	errorsFixed: z.number(),
	remainingErrors: z.number(),
	errors: z.array(z.any()).optional()
});
const ValidationFixInputSchema = z.object({
	commitSha: z.string(),
	slug: z.string(),
	targetPath: z.string().optional(),
	templateDir: z.string(),
	orderedUnits: z.array(TemplateUnitSchema),
	copiedFiles: z.array(CopiedFileSchema),
	conflictsResolved: z.array(ConflictResolutionSchema).optional(),
	maxIterations: z.number().optional().default(5)
});
const ValidationFixResultSchema = z.object({
	success: z.boolean(),
	applied: z.boolean(),
	message: z.string(),
	validationResults: ValidationResultsSchema,
	error: z.string().optional()
});
const ApplyResultSchema = z.object({
	success: z.boolean(),
	applied: z.boolean(),
	branchName: z.string().optional(),
	message: z.string(),
	validationResults: ValidationResultsSchema.optional(),
	error: z.string().optional(),
	errors: z.array(z.string()).optional(),
	stepResults: z.object({
		cloneSuccess: z.boolean().optional(),
		analyzeSuccess: z.boolean().optional(),
		discoverSuccess: z.boolean().optional(),
		orderSuccess: z.boolean().optional(),
		prepareBranchSuccess: z.boolean().optional(),
		packageMergeSuccess: z.boolean().optional(),
		installSuccess: z.boolean().optional(),
		copySuccess: z.boolean().optional(),
		mergeSuccess: z.boolean().optional(),
		validationSuccess: z.boolean().optional(),
		filesCopied: z.number(),
		conflictsSkipped: z.number(),
		conflictsResolved: z.number()
	}).optional()
});
const CloneTemplateResultSchema = z.object({
	templateDir: z.string(),
	commitSha: z.string(),
	slug: z.string(),
	success: z.boolean().optional(),
	error: z.string().optional(),
	targetPath: z.string().optional()
});
const PackageAnalysisSchema = z.object({
	name: z.string().optional(),
	version: z.string().optional(),
	description: z.string().optional(),
	dependencies: z.record(z.string(), z.string()).optional(),
	devDependencies: z.record(z.string(), z.string()).optional(),
	peerDependencies: z.record(z.string(), z.string()).optional(),
	scripts: z.record(z.string(), z.string()).optional(),
	success: z.boolean().optional(),
	error: z.string().optional()
});
const DiscoveryResultSchema = z.object({
	units: z.array(TemplateUnitSchema),
	success: z.boolean().optional(),
	error: z.string().optional()
});
const OrderedUnitsSchema = z.object({
	orderedUnits: z.array(TemplateUnitSchema),
	success: z.boolean().optional(),
	error: z.string().optional()
});
const PackageMergeInputSchema = z.object({
	commitSha: z.string(),
	slug: z.string(),
	targetPath: z.string().optional(),
	packageInfo: PackageAnalysisSchema
});
const PackageMergeResultSchema = z.object({
	success: z.boolean(),
	applied: z.boolean(),
	message: z.string(),
	error: z.string().optional()
});
const InstallInputSchema = z.object({ targetPath: z.string().optional().describe("Path to the project to install packages in") });
const InstallResultSchema = z.object({
	success: z.boolean(),
	error: z.string().optional()
});
const PrepareBranchInputSchema = z.object({
	slug: z.string(),
	commitSha: z.string().optional(),
	targetPath: z.string().optional()
});
const PrepareBranchResultSchema = z.object({
	branchName: z.string(),
	success: z.boolean().optional(),
	error: z.string().optional()
});
//#endregion
//#region src/utils.ts
const exec$1 = promisify(exec);
const execFile$1 = promisify(execFile);
function isInWorkspaceSubfolder(cwd) {
	try {
		if (!existsSync(resolve(cwd, "package.json"))) return false;
		let currentDir = cwd;
		let previousDir = "";
		while (currentDir !== previousDir && currentDir !== "/") {
			previousDir = currentDir;
			currentDir = dirname(currentDir);
			if (currentDir === cwd) continue;
			console.info(`Checking for workspace indicators in: ${currentDir}`);
			if (existsSync(resolve(currentDir, "pnpm-workspace.yaml"))) return true;
			const parentPackageJson = resolve(currentDir, "package.json");
			if (existsSync(parentPackageJson)) try {
				if (JSON.parse(readFileSync(parentPackageJson, "utf-8")).workspaces) return true;
			} catch {}
			if (existsSync(resolve(currentDir, "lerna.json"))) return true;
		}
		return false;
	} catch (error) {
		console.warn(`Error in workspace detection: ${error}`);
		return false;
	}
}
function spawn$1(command, args, options) {
	return new Promise((resolve, reject) => {
		const childProcess = spawn(command, args, {
			stdio: "inherit",
			...options
		});
		childProcess.on("error", (error) => {
			reject(error);
		});
		childProcess.on("close", (code) => {
			if (code === 0) resolve(void 0);
			else reject(/* @__PURE__ */ new Error(`Command failed with exit code ${code}`));
		});
	});
}
async function isGitInstalled() {
	try {
		await spawnWithOutput("git", ["--version"], {});
		return true;
	} catch {
		return false;
	}
}
async function isInsideGitRepo(cwd) {
	try {
		if (!await isGitInstalled()) return false;
		const { stdout } = await spawnWithOutput("git", ["rev-parse", "--is-inside-work-tree"], { cwd });
		return stdout.trim() === "true";
	} catch {
		return false;
	}
}
function spawnWithOutput(command, args, options) {
	return new Promise((resolvePromise, rejectPromise) => {
		const childProcess = spawn(command, args, { ...options });
		let stdout = "";
		let stderr = "";
		childProcess.on("error", (error) => {
			rejectPromise(error);
		});
		childProcess.stdout?.on("data", (chunk) => {
			process.stdout.write(chunk);
			stdout += chunk?.toString?.() ?? String(chunk);
		});
		childProcess.stderr?.on("data", (chunk) => {
			stderr += chunk?.toString?.() ?? String(chunk);
			process.stderr.write(chunk);
		});
		childProcess.on("close", (code) => {
			if (code === 0) resolvePromise({
				stdout,
				stderr,
				code: code ?? 0
			});
			else {
				const err = new Error(stderr || `Command failed: ${command} ${args.join(" ")}`);
				err.code = code;
				rejectPromise(err);
			}
		});
	});
}
async function spawnSWPM(cwd, command, packageNames) {
	try {
		console.info("Running install command with swpm");
		await spawn$1(createRequire(import.meta.filename).resolve("swpm"), [command, ...packageNames], { cwd });
		return;
	} catch (e) {
		console.warn("Failed to run install command with swpm", e);
	}
	try {
		let packageManager;
		if (existsSync(resolve(cwd, "pnpm-lock.yaml"))) packageManager = "pnpm";
		else if (existsSync(resolve(cwd, "yarn.lock"))) packageManager = "yarn";
		else packageManager = "npm";
		let nativeCommand = command === "add" ? "add" : command === "install" ? "install" : command;
		const args = [nativeCommand];
		if (nativeCommand === "install") {
			const inWorkspace = isInWorkspaceSubfolder(cwd);
			if (packageManager === "pnpm") {
				args.push("--force");
				if (inWorkspace) args.push("--ignore-workspace");
			} else if (packageManager === "npm") {
				args.push("--yes");
				if (inWorkspace) args.push("--ignore-workspaces");
			}
		}
		args.push(...packageNames);
		console.info(`Falling back to ${packageManager} ${args.join(" ")}`);
		await spawn$1(packageManager, args, { cwd });
		return;
	} catch (e) {
		console.warn(`Failed to run install command with native package manager: ${e}`);
	}
	throw new Error(`Failed to run install command with swpm and native package managers`);
}
function kindWeight(kind) {
	const idx = UNIT_KINDS.indexOf(kind);
	return idx === -1 ? UNIT_KINDS.length : idx;
}
async function fetchMastraTemplates() {
	try {
		return await (await fetch("https://mastra.ai/api/templates.json")).json();
	} catch (error) {
		throw new Error(`Failed to fetch Mastra templates: ${error instanceof Error ? error.message : String(error)}`);
	}
}
async function getMastraTemplate(slug) {
	const templates = await fetchMastraTemplates();
	const template = templates.find((t) => t.slug === slug);
	if (!template) throw new Error(`Template "${slug}" not found. Available templates: ${templates.map((t) => t.slug).join(", ")}`);
	return template;
}
async function logGitState(targetPath, label) {
	try {
		if (!await isInsideGitRepo(targetPath)) return;
		const gitStatusResult = await git(targetPath, "status", "--porcelain");
		const gitLogResult = await git(targetPath, "log", "--oneline", "-3");
		const gitCountResult = await git(targetPath, "rev-list", "--count", "HEAD");
		console.info(`📊 Git state ${label}:`);
		console.info("Status:", gitStatusResult.stdout.trim() || "Clean working directory");
		console.info("Recent commits:", gitLogResult.stdout.trim());
		console.info("Total commits:", gitCountResult.stdout.trim());
	} catch (gitError) {
		console.warn(`Could not get git state ${label}:`, gitError);
	}
}
async function git(cwd, ...args) {
	const { stdout, stderr } = await spawnWithOutput("git", args, { cwd });
	return {
		stdout: stdout ?? "",
		stderr: stderr ?? ""
	};
}
async function gitClone(repo, destDir, cwd) {
	await git(cwd ?? process.cwd(), "clone", repo, destDir);
}
async function gitCheckoutRef(cwd, ref) {
	if (!await isInsideGitRepo(cwd)) return;
	await git(cwd, "checkout", ref);
}
async function gitRevParse(cwd, rev) {
	if (!await isInsideGitRepo(cwd)) return "";
	const { stdout } = await git(cwd, "rev-parse", rev);
	return stdout.trim();
}
async function gitAddFiles(cwd, files) {
	if (!files || files.length === 0) return;
	if (!await isInsideGitRepo(cwd)) return;
	await git(cwd, "add", ...files);
}
async function gitAddAll(cwd) {
	if (!await isInsideGitRepo(cwd)) return;
	await git(cwd, "add", ".");
}
async function gitHasStagedChanges(cwd) {
	if (!await isInsideGitRepo(cwd)) return false;
	const { stdout } = await git(cwd, "diff", "--cached", "--name-only");
	return stdout.trim().length > 0;
}
async function gitCommit(cwd, message, opts) {
	try {
		if (!await isInsideGitRepo(cwd)) return false;
		if (opts?.skipIfNoStaged) {
			if (!await gitHasStagedChanges(cwd)) return false;
		}
		const args = [
			"commit",
			"-m",
			message
		];
		if (opts?.allowEmpty) args.push("--allow-empty");
		await git(cwd, ...args);
		return true;
	} catch (e) {
		const msg = e instanceof Error ? e.message : String(e);
		if (/nothing to commit/i.test(msg) || /no changes added to commit/i.test(msg)) return false;
		throw e;
	}
}
async function gitAddAndCommit(cwd, message, files, opts) {
	try {
		if (!await isInsideGitRepo(cwd)) return false;
		if (files && files.length > 0) await gitAddFiles(cwd, files);
		else await gitAddAll(cwd);
		return gitCommit(cwd, message, opts);
	} catch (e) {
		console.error(`Failed to add and commit files: ${e instanceof Error ? e.message : String(e)}`);
		return false;
	}
}
async function gitCheckoutBranch(branchName, targetPath) {
	try {
		if (!await isInsideGitRepo(targetPath)) return;
		await git(targetPath, "checkout", "-b", branchName);
		console.info(`Created new branch: ${branchName}`);
	} catch (error) {
		if ((error instanceof Error ? error.message : String(error)).includes("already exists")) try {
			await git(targetPath, "checkout", branchName);
			console.info(`Switched to existing branch: ${branchName}`);
		} catch {
			const uniqueBranchName = `${branchName}-${Date.now().toString().slice(-6)}`;
			await git(targetPath, "checkout", "-b", uniqueBranchName);
			console.info(`Created unique branch: ${uniqueBranchName}`);
		}
		else throw error;
	}
}
async function backupAndReplaceFile(sourceFile, targetFile) {
	const backupFile = `${targetFile}.backup-${Date.now()}`;
	await copyFile(targetFile, backupFile);
	console.info(`📦 Created backup: ${basename(backupFile)}`);
	await copyFile(sourceFile, targetFile);
	console.info(`🔄 Replaced file with template version (backup created)`);
}
async function renameAndCopyFile(sourceFile, targetFile) {
	let counter = 1;
	let uniqueTargetFile = targetFile;
	const baseName = basename(targetFile, extname(targetFile));
	const extension = extname(targetFile);
	const directory = dirname(targetFile);
	while (existsSync(uniqueTargetFile)) {
		uniqueTargetFile = resolve(directory, `${baseName}.template-${counter}${extension}`);
		counter++;
	}
	await copyFile(sourceFile, uniqueTargetFile);
	console.info(`📝 Copied with unique name: ${basename(uniqueTargetFile)}`);
	return uniqueTargetFile;
}
const isValidMastraLanguageModel = (model) => {
	return model && typeof model === "object" && typeof model.modelId === "string";
};
const resolveTargetPath = (inputData, requestContext) => {
	if (inputData.targetPath) return inputData.targetPath;
	const contextPath = requestContext.get("targetPath");
	if (contextPath) return contextPath;
	const envRoot = process.env.MASTRA_PROJECT_ROOT?.trim();
	if (envRoot) return envRoot;
	const cwd = process.cwd();
	const parent = dirname(cwd);
	const grand = dirname(parent);
	if (basename(cwd) === "output" && basename(parent) === ".mastra") return grand;
	return cwd;
};
const mergeGitignoreFiles = (targetContent, templateContent, templateSlug) => {
	const targetLines = targetContent.replace(/\r\n/g, "\n").split("\n");
	const templateLines = templateContent.replace(/\r\n/g, "\n").split("\n");
	const existingEntries = /* @__PURE__ */ new Set();
	for (const line of targetLines) {
		const trimmed = line.trim();
		if (trimmed && !trimmed.startsWith("#")) {
			const normalized = trimmed.replace(/^\.\//, "").replace(/\\/g, "/");
			existingEntries.add(normalized);
		}
	}
	const newEntries = [];
	for (const line of templateLines) {
		const trimmed = line.trim();
		if (trimmed && !trimmed.startsWith("#")) {
			const normalized = trimmed.replace(/^\.\//, "").replace(/\\/g, "/");
			if (!existingEntries.has(normalized)) {
				const isNegation = normalized.startsWith("!");
				const basePath = isNegation ? normalized.slice(1) : normalized;
				if (!(isNegation ? existingEntries.has(basePath) : existingEntries.has("!" + basePath))) newEntries.push(trimmed);
				else console.info(`⚠ Skipping conflicting .gitignore rule: ${trimmed} (conflicts with existing rule)`);
			}
		}
	}
	if (newEntries.length === 0) return targetContent;
	const result = [...targetLines];
	const lastLine = result[result.length - 1];
	if (result.length > 0 && lastLine && lastLine.trim() !== "") result.push("");
	result.push(`# Added by template: ${templateSlug}`);
	result.push(...newEntries);
	return result.join("\n");
};
const mergeEnvFiles = (targetContent, templateVariables, templateSlug) => {
	const targetLines = targetContent.replace(/\r\n/g, "\n").split("\n");
	const existingVars = /* @__PURE__ */ new Set();
	for (const line of targetLines) {
		const trimmed = line.trim();
		if (trimmed && !trimmed.startsWith("#")) {
			const equalIndex = trimmed.indexOf("=");
			if (equalIndex > 0) {
				const varName = trimmed.substring(0, equalIndex).trim();
				existingVars.add(varName);
			}
		}
	}
	const newVars = [];
	for (const [key, value] of Object.entries(templateVariables)) if (!existingVars.has(key)) newVars.push({
		key,
		value
	});
	else console.info(`⚠ Skipping existing environment variable: ${key} (already exists in .env)`);
	if (newVars.length === 0) return targetContent;
	const result = [...targetLines];
	const lastLine = result[result.length - 1];
	if (result.length > 0 && lastLine && lastLine.trim() !== "") result.push("");
	result.push(`# Added by template: ${templateSlug}`);
	for (const { key, value } of newVars) result.push(`${key}=${value}`);
	return result.join("\n");
};
const detectAISDKVersion = async (projectPath) => {
	try {
		const packageJsonPath = join(projectPath, "package.json");
		if (!existsSync(packageJsonPath)) {
			console.info("No package.json found, defaulting to v2");
			return "v2";
		}
		const packageContent = await readFile(packageJsonPath, "utf-8");
		const packageJson = JSON.parse(packageContent);
		const allDeps = {
			...packageJson.dependencies,
			...packageJson.devDependencies,
			...packageJson.peerDependencies
		};
		for (const pkg of [
			"@ai-sdk/openai",
			"@ai-sdk/anthropic",
			"@ai-sdk/google",
			"@ai-sdk/groq",
			"@ai-sdk/xai"
		]) {
			const version = allDeps[pkg];
			if (version) {
				const versionMatch = version.match(/(\d+)/);
				if (versionMatch) {
					const majorVersion = parseInt(versionMatch[1]);
					if (majorVersion >= 2) {
						console.info(`Detected ${pkg} v${majorVersion} -> using v2 specification`);
						return "v2";
					} else {
						console.info(`Detected ${pkg} v${majorVersion} -> using v1 specification`);
						return "v1";
					}
				}
			}
		}
		console.info("No AI SDK version detected, defaulting to v2");
		return "v2";
	} catch (error) {
		console.warn(`Failed to detect AI SDK version: ${error instanceof Error ? error.message : String(error)}`);
		return "v2";
	}
};
const createModelInstance = async (provider, modelId, version = "v2") => {
	try {
		const providerFn = version === `v1` ? { v1: {
			openai: async () => {
				const { openai } = await import("@ai-sdk/openai");
				return openai(modelId);
			},
			anthropic: async () => {
				const { anthropic } = await import("@ai-sdk/anthropic");
				return anthropic(modelId);
			},
			groq: async () => {
				const { groq } = await import("@ai-sdk/groq");
				return groq(modelId);
			},
			xai: async () => {
				const { xai } = await import("@ai-sdk/xai");
				return xai(modelId);
			},
			google: async () => {
				const { google } = await import("@ai-sdk/google");
				return google(modelId);
			}
		} }[version][provider] : () => new ModelRouterLanguageModel(`${provider}/${modelId}`);
		if (!providerFn) {
			console.error(`Unsupported provider: ${provider}`);
			return null;
		}
		const modelInstance = await providerFn();
		console.info(`Created ${provider} model instance (${version}): ${modelId}`);
		return modelInstance;
	} catch (error) {
		console.error(`Failed to create model instance: ${error instanceof Error ? error.message : String(error)}`);
		return null;
	}
};
const resolveModel = async ({ requestContext, defaultModel = "openai/gpt-4.1", projectPath }) => {
	const modelFromContext = requestContext.get("model");
	if (modelFromContext) {
		console.info("Using model from request context");
		if (isValidMastraLanguageModel(modelFromContext)) return modelFromContext;
		throw new Error("Invalid model provided. Model must be a MastraLanguageModel instance (e.g., openai(\"gpt-4\"), anthropic(\"claude-3-5-sonnet\"), etc.)");
	}
	const selectedModel = requestContext.get("selectedModel");
	if (selectedModel?.provider && selectedModel?.modelId && projectPath) {
		console.info(`Resolving selected model: ${selectedModel.provider}/${selectedModel.modelId}`);
		const version = await detectAISDKVersion(projectPath);
		const modelInstance = await createModelInstance(selectedModel.provider, selectedModel.modelId, version);
		if (modelInstance) {
			requestContext.set("model", modelInstance);
			return modelInstance;
		}
	}
	console.info("Using default model");
	return typeof defaultModel === `string` ? new ModelRouterLanguageModel(defaultModel) : defaultModel;
};
//#endregion
//#region src/defaults.ts
var AgentBuilderDefaults = class AgentBuilderDefaults {
	static DEFAULT_INSTRUCTIONS = (projectPath) => `You are a Mastra Expert Agent, specialized in building production-ready AI applications using the Mastra framework. You excel at creating agents, tools, workflows, and complete applications with real, working implementations.

## Core Identity & Capabilities

**Primary Role:** Transform natural language requirements into working Mastra applications
**Key Strength:** Deep knowledge of Mastra patterns, conventions, and best practices
**Output Quality:** Production-ready code that follows Mastra ecosystem standards

## Workflow: The MASTRA Method

Follow this sequence for every coding task:

IF NO PROJECT EXISTS, USE THE MANAGEPROJECT TOOL TO CREATE A NEW PROJECT

DO NOT INCLUDE TODOS IN THE CODE, UNLESS SPECIFICALLY ASKED TO DO SO, CREATE REAL WORLD CODE

### 1. 🔍 **UNDERSTAND** (Information Gathering)
- **Explore Mastra Docs**: Use docs tools to understand relevant Mastra patterns and APIs
- **Analyze Project**: Use file exploration to understand existing codebase structure
- **Web Research**: Search for packages, examples, or solutions when docs are insufficient
- **Clarify Requirements**: Ask targeted questions only when critical information is missing

### 2. 📋 **PLAN** (Strategy & Design)
- **Architecture**: Design using Mastra conventions (agents, tools, workflows, memory)
- **Dependencies**: Identify required packages and Mastra components
- **Integration**: Plan how to integrate with existing project structure
- **Validation**: Define how to test and verify the implementation

### 3. 🛠️ **BUILD** (Implementation)
- **Install First**: Use \`manageProject\` tool to install required packages
- **Follow Patterns**: Implement using established Mastra conventions
- **Real Code Only**: Build actual working functionality, never mock implementations
- **Environment Setup**: Create proper .env configuration and documentation

### 4. ✅ **VALIDATE** (Quality Assurance)
- **Code Validation**: Run \`validateCode\` with types and lint checks
- **Testing**: Execute tests if available
- **Server Testing**: Use \`manageServer\` and \`httpRequest\` for API validation
- **Fix Issues**: Address all errors before completion

## Mastra-Specific Guidelines

### Framework Knowledge
- **Agents**: Use \`@mastra/core/agent\` with proper configuration
- **Tools**: Create tools with \`@mastra/core/tools\` and proper schemas
- **Memory**: Implement memory with \`@mastra/memory\` and appropriate processors
- **Workflows**: Build workflows with \`@mastra/core/workflows\`
- **Integrations**: Leverage Mastra's extensive integration ecosystem

### Code Standards
- **TypeScript First**: All code must be properly typed
- **Zod Schemas**: Use Zod for all data validation
- **Environment Variables**: Proper .env configuration with examples
- **Error Handling**: Comprehensive error handling with meaningful messages
- **Security**: Never expose credentials or sensitive data

### Project Structure
- Follow Mastra project conventions (\`src/mastra/\`, config files)
- Use proper file organization (agents, tools, workflows in separate directories)
- Maintain consistent naming conventions
- Include proper exports and imports

## Communication Style

**Conciseness**: Keep responses focused and actionable
**Clarity**: Explain complex concepts in simple terms
**Directness**: State what you're doing and why
**No Fluff**: Avoid unnecessary explanations or apologies

### Response Format
1. **Brief Status**: One line stating what you're doing
2. **Tool Usage**: Execute necessary tools
3. **Results Summary**: Concise summary of what was accomplished
4. **Next Steps**: Clear indication of completion or next actions

## Tool Usage Strategy

### File Operations
- **Project-Relative Paths**: All file paths are resolved relative to the project directory (unless absolute paths are used)
- **Read First**: Always read files before editing to understand context
- **Precise Edits**: Use exact text matching for search/replace operations
- **Batch Operations**: Group related file operations when possible

### Project Management
- **manageProject**: Use for package installation, project creation, dependency management
- **validateCode**: Always run after code changes to ensure quality
- **manageServer**: Use for testing Mastra server functionality
- **httpRequest**: Test API endpoints and integrations

### Information Gathering
- **Mastra Docs**: Primary source for Mastra-specific information
- **Web Search**: Secondary source for packages and external solutions
- **File Exploration**: Understand existing project structure and patterns

## Error Handling & Recovery

### Validation Failures
- Fix TypeScript errors immediately
- Address linting issues systematically
- Re-validate until clean

### Build Issues
- Check dependencies and versions
- Verify Mastra configuration
- Test in isolation when needed

### Integration Problems
- Verify API keys and environment setup
- Test connections independently
- Debug with logging and error messages

## Security & Best Practices

**Never:**
- Hard-code API keys or secrets
- Generate mock or placeholder implementations
- Skip error handling
- Ignore TypeScript errors
- Create insecure code patterns
- ask for file paths, you should be able to use the provided tools to explore the file system

**Always:**
- Use environment variables for configuration
- Implement proper input validation
- Follow security best practices
- Create complete, working implementations
- Test thoroughly before completion

## Output Requirements

### Code Quality
- ✅ TypeScript compilation passes
- ✅ ESLint validation passes
- ✅ Proper error handling implemented
- ✅ Environment variables configured
- ✅ Tests included when appropriate

### Documentation
- ✅ Clear setup instructions
- ✅ Environment variable documentation
- ✅ Usage examples provided
- ✅ API documentation for custom tools

### Integration
- ✅ Follows Mastra conventions
- ✅ Integrates with existing project
- ✅ Proper imports and exports
- ✅ Compatible with Mastra ecosystem

## Project Context

**Working Directory**: ${projectPath}
**Focus**: Mastra framework applications
**Goal**: Production-ready implementations

Remember: You are building real applications, not prototypes. Every implementation should be complete, secure, and ready for production use.

## Enhanced Tool Set

You have access to an enhanced set of tools based on production coding agent patterns:

### Task Management
- **taskManager**: Create and track multi-step coding tasks with states (pending, in_progress, completed, blocked). Use this for complex projects that require systematic progress tracking.

### Code Discovery & Analysis
- **codeAnalyzer**: Analyze codebase structure, discover definitions (functions, classes, interfaces), map dependencies, and understand architectural patterns.
- **smartSearch**: Intelligent search with context awareness, pattern matching, and relevance scoring.

### Advanced File Operations
- **readFile**: Read files with optional line ranges, encoding support, metadata
- **writeFile**: Write files with directory creation
- **listDirectory**: Directory listing with filtering, recursion, metadata
- **multiEdit**: Perform multiple search-replace operations across files atomically with backup creation
- **executeCommand**: Execute shell commands with proper error handling and working directory support

**Important**: All file paths are resolved relative to the project directory unless absolute paths are provided.

### Communication & Workflow
- **attemptCompletion**: Signal task completion with validation status and confidence metrics.

### Guidelines for Enhanced Tools:

1. **Use taskManager proactively** for any task requiring 3+ steps or complex coordination
2. **Start with codeAnalyzer** when working with unfamiliar codebases to understand structure
3. **Use smartSearch** for intelligent pattern discovery across the codebase
4. **Apply multiEdit** for systematic refactoring across multiple files
5. **Ask for clarification** when requirements are ambiguous rather than making assumptions
6. **Signal completion** with comprehensive summaries and validation status

Use the following basic examples to guide your implementation.

<examples>
### Weather Agent
\`\`\`
// ./src/agents/weather-agent.ts
import { openai } from '@ai-sdk/openai';
import { Agent } from '@mastra/core/agent';
import { Memory } from '@mastra/memory';
import { LibSQLStore } from '@mastra/libsql';
import { weatherTool } from '../tools/weather-tool';

export const weatherAgent = new Agent({
  id: 'weather-agent',
  name: 'Weather Agent',
  instructions: \${instructions},
  model: openai('gpt-4o-mini'),
  tools: { weatherTool },
  memory: new Memory({
    storage: new LibSQLStore({
      id: 'mastra-memory-storage',
      url: 'file:../mastra.db', // ask user what database to use, use this as the default
    }),
  }),
});
\`\`\`

### Weather Tool
\`\`\`
// ./src/tools/weather-tool.ts
import { createTool } from '@mastra/core/tools';
import { z } from 'zod';
import { getWeather } from '../tools/weather-tool';

export const weatherTool = createTool({
  id: 'get-weather',
  description: 'Get current weather for a location',
  inputSchema: z.object({
    location: z.string().describe('City name'),
  }),
  outputSchema: z.object({
    temperature: z.number(),
    feelsLike: z.number(),
    humidity: z.number(),
    windSpeed: z.number(),
    windGust: z.number(),
    conditions: z.string(),
    location: z.string(),
  }),
  execute: async (inputData) => {
    return await getWeather(inputData.location);
  },
});
\`\`\`

### Weather Workflow
\`\`\`
// ./src/workflows/weather-workflow.ts
import { createStep, createWorkflow } from '@mastra/core/workflows';
import { z } from 'zod';

const fetchWeather = createStep({
  id: 'fetch-weather',
  description: 'Fetches weather forecast for a given city',
  inputSchema: z.object({
    city: z.string().describe('The city to get the weather for'),
  }),
  outputSchema: forecastSchema,
  execute: async (inputData) => {
    if (!inputData) {
      throw new Error('Input data not found');
    }

    const geocodingUrl = \`https://geocoding-api.open-meteo.com/v1/search?name=\${encodeURIComponent(inputData.city)}&count=1\`;
    const geocodingResponse = await fetch(geocodingUrl);
    const geocodingData = (await geocodingResponse.json()) as {
      results: { latitude: number; longitude: number; name: string }[];
    };

    if (!geocodingData.results?.[0]) {
      throw new Error(\`Location '\${inputData.city}' not found\`);
    }

    const { latitude, longitude, name } = geocodingData.results[0];

    const weatherUrl = \`https://api.open-meteo.com/v1/forecast?latitude=\${latitude}&longitude=\${longitude}&current=precipitation,weathercode&timezone=auto,&hourly=precipitation_probability,temperature_2m\`
    const response = await fetch(weatherUrl);
    const data = (await response.json()) as {
      current: {
        time: string;
        precipitation: number;
        weathercode: number;
      };
      hourly: {
        precipitation_probability: number[];
        temperature_2m: number[];
      };
    };

    const forecast = {
      date: new Date().toISOString(),
      maxTemp: Math.max(...data.hourly.temperature_2m),
      minTemp: Math.min(...data.hourly.temperature_2m),
      condition: getWeatherCondition(data.current.weathercode),
      precipitationChance: data.hourly.precipitation_probability.reduce(
        (acc, curr) => Math.max(acc, curr),
        0,
      ),
      location: name,
    };

    return forecast;
  },
});

const planActivities = createStep({
  id: 'plan-activities',
  description: 'Suggests activities based on weather conditions',
  inputSchema: forecastSchema,
  outputSchema: z.object({
    activities: z.string(),
  }),
  execute: async (inputData, context) => {
    const mastra = context?.mastra;
    const forecast = inputData;

    if (!forecast) {
      throw new Error('Forecast data not found');
    }

    const agent = mastra?.getAgent('weatherAgent');
    if (!agent) {
      throw new Error('Weather agent not found');
    }

    const prompt = \${weatherWorkflowPrompt}

    const response = await agent.stream([
      {
        role: 'user',
        content: prompt,
      },
    ]);

    let activitiesText = '';

    for await (const chunk of response.textStream) {
      process.stdout.write(chunk);
      activitiesText += chunk;
    }

    return {
      activities: activitiesText,
    };
  },
});

const weatherWorkflow = createWorkflow({
  id: 'weather-workflow',
  inputSchema: z.object({
    city: z.string().describe('The city to get the weather for'),
  }),
  outputSchema: z.object({
    activities: z.string(),
  }),
})
  .then(fetchWeather)
  .then(planActivities);

weatherWorkflow.commit();
\`\`\`
export { weatherWorkflow };
\`\`\`

### Mastra instance
\`\`\`
// ./src/mastra.ts

import { Mastra } from '@mastra/core/mastra';
import { PinoLogger } from '@mastra/loggers';
import { LibSQLStore } from '@mastra/libsql';
import { weatherWorkflow } from './workflows/weather-workflow';
import { weatherAgent } from './agents/weather-agent';

export const mastra = new Mastra({
  workflows: { weatherWorkflow },
  agents: { weatherAgent },
  storage: new LibSQLStore({
    id: 'mastra-storage',
    // stores observability, evals, ... into memory storage, if it needs to persist, change to file:../mastra.db
    url: ":memory:",
  }),
  logger: new PinoLogger({
    name: 'Mastra',
    level: 'info',
  }),
});
\`\`\`

</examples>`;
	static DEFAULT_MEMORY_CONFIG = { lastMessages: 20 };
	static DEFAULT_FOLDER_STRUCTURE = {
		agent: "src/mastra/agents",
		workflow: "src/mastra/workflows",
		tool: "src/mastra/tools",
		"mcp-server": "src/mastra/mcp",
		network: "src/mastra/networks"
	};
	static DEFAULT_TOOLS = async (projectPath) => {
		return {
			readFile: createTool({
				id: "read-file",
				description: "Read contents of a file with optional line range selection.",
				inputSchema: z.object({
					filePath: z.string().describe("Path to the file to read"),
					startLine: z.number().optional().describe("Starting line number (1-indexed)"),
					endLine: z.number().optional().describe("Ending line number (1-indexed, inclusive)"),
					encoding: z.string().default("utf-8").describe("File encoding")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					content: z.string().optional(),
					lines: z.array(z.string()).optional(),
					metadata: z.object({
						size: z.number(),
						totalLines: z.number(),
						encoding: z.string(),
						lastModified: z.string()
					}).optional(),
					errorMessage: z.string().optional()
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.readFile({
						...inputData,
						projectPath
					});
				}
			}),
			writeFile: createTool({
				id: "write-file",
				description: "Write content to a file, with options for creating directories.",
				inputSchema: z.object({
					filePath: z.string().describe("Path to the file to write"),
					content: z.string().describe("Content to write to the file"),
					createDirs: z.boolean().default(true).describe("Create parent directories if they don't exist"),
					encoding: z.string().default("utf-8").describe("File encoding")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					filePath: z.string(),
					bytesWritten: z.number().optional(),
					message: z.string(),
					errorMessage: z.string().optional()
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.writeFile({
						...inputData,
						projectPath
					});
				}
			}),
			listDirectory: createTool({
				id: "list-directory",
				description: "List contents of a directory with filtering and metadata options.",
				inputSchema: z.object({
					path: z.string().describe("Directory path to list"),
					recursive: z.boolean().default(false).describe("List subdirectories recursively"),
					includeHidden: z.boolean().default(false).describe("Include hidden files and directories"),
					pattern: z.string().default("*").describe("Glob pattern to filter files"),
					maxDepth: z.number().default(10).describe("Maximum recursion depth"),
					includeMetadata: z.boolean().default(true).describe("Include file metadata")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					items: z.array(z.object({
						name: z.string(),
						path: z.string(),
						type: z.enum([
							"file",
							"directory",
							"symlink"
						]),
						size: z.number().optional(),
						lastModified: z.string().optional(),
						permissions: z.string().optional()
					})),
					totalItems: z.number(),
					path: z.string(),
					message: z.string(),
					errorMessage: z.string().optional()
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.listDirectory({
						...inputData,
						projectPath
					});
				}
			}),
			executeCommand: createTool({
				id: "execute-command",
				description: "Execute shell commands with proper error handling and output capture.",
				inputSchema: z.object({
					command: z.string().describe("Shell command to execute"),
					workingDirectory: z.string().optional().describe("Working directory for command execution"),
					timeout: z.number().default(3e4).describe("Timeout in milliseconds"),
					captureOutput: z.boolean().default(true).describe("Capture command output"),
					shell: z.string().optional().describe("Shell to use (defaults to system shell)"),
					env: z.record(z.string(), z.string()).optional().describe("Environment variables")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					exitCode: z.number().optional(),
					stdout: z.string().optional(),
					stderr: z.string().optional(),
					command: z.string(),
					workingDirectory: z.string().optional(),
					executionTime: z.number().optional(),
					errorMessage: z.string().optional()
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.executeCommand({
						...inputData,
						workingDirectory: inputData.workingDirectory || projectPath,
						env: inputData.env
					});
				}
			}),
			taskManager: createTool({
				id: "task-manager",
				description: "Create and manage structured task lists for coding sessions. Use this for complex multi-step tasks to track progress and ensure thoroughness.",
				inputSchema: z.object({
					action: z.enum([
						"create",
						"update",
						"list",
						"complete",
						"remove"
					]).describe("Task management action"),
					tasks: z.array(z.object({
						id: z.string().describe("Unique task identifier"),
						content: z.string().describe("Task description, optional if just updating the status").optional(),
						status: z.enum([
							"pending",
							"in_progress",
							"completed",
							"blocked"
						]).describe("Task status"),
						priority: z.enum([
							"high",
							"medium",
							"low"
						]).default("medium").describe("Task priority"),
						dependencies: z.array(z.string()).optional().describe("IDs of tasks this depends on"),
						notes: z.string().optional().describe("Additional notes or context")
					})).optional().describe("Tasks to create or update"),
					taskId: z.string().optional().describe("Specific task ID for single task operations")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					tasks: z.array(z.object({
						id: z.string(),
						content: z.string(),
						status: z.string(),
						priority: z.string(),
						dependencies: z.array(z.string()).optional(),
						notes: z.string().optional(),
						createdAt: z.string(),
						updatedAt: z.string()
					})),
					message: z.string()
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.manageTaskList(inputData);
				}
			}),
			multiEdit: createTool({
				id: "multi-edit",
				description: "Perform multiple search-replace operations on one or more files in a single atomic operation.",
				inputSchema: z.object({
					operations: z.array(z.object({
						filePath: z.string().describe("Path to the file to edit"),
						edits: z.array(z.object({
							oldString: z.string().describe("Exact text to replace"),
							newString: z.string().describe("Replacement text"),
							replaceAll: z.boolean().default(false).describe("Replace all occurrences")
						})).describe("List of edit operations for this file")
					})).describe("File edit operations to perform"),
					createBackup: z.boolean().default(false).describe("Create backup files before editing")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					results: z.array(z.object({
						filePath: z.string(),
						editsApplied: z.number(),
						errors: z.array(z.string()),
						backup: z.string().optional()
					})),
					message: z.string()
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.performMultiEdit({
						...inputData,
						projectPath
					});
				}
			}),
			replaceLines: createTool({
				id: "replace-lines",
				description: "Replace specific line ranges in files with new content. IMPORTANT: This tool replaces ENTIRE lines, not partial content within lines. Lines are 1-indexed.",
				inputSchema: z.object({
					filePath: z.string().describe("Path to the file to edit"),
					startLine: z.number().describe("Starting line number to replace (1-indexed, inclusive). Count from the first line = 1"),
					endLine: z.number().describe("Ending line number to replace (1-indexed, inclusive). To replace single line, use same number as startLine"),
					newContent: z.string().describe("New content to replace the lines with. Use empty string \"\" to delete lines completely. For multiline content, include \\n characters"),
					createBackup: z.boolean().default(false).describe("Create backup file before editing")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					message: z.string(),
					linesReplaced: z.number().optional(),
					backup: z.string().optional(),
					errorMessage: z.string().optional()
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.replaceLines({
						...inputData,
						projectPath
					});
				}
			}),
			showFileLines: createTool({
				id: "show-file-lines",
				description: "Show specific lines from a file with line numbers. Useful for debugging before using replaceLines.",
				inputSchema: z.object({
					filePath: z.string().describe("Path to the file to examine"),
					startLine: z.number().optional().describe("Starting line number to show (1-indexed). If not provided, shows all lines"),
					endLine: z.number().optional().describe("Ending line number to show (1-indexed, inclusive). If not provided but startLine is, shows only that line"),
					context: z.number().default(2).describe("Number of context lines to show before and after the range")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					lines: z.array(z.object({
						lineNumber: z.number(),
						content: z.string(),
						isTarget: z.boolean().describe("Whether this line is in the target range")
					})),
					totalLines: z.number(),
					message: z.string(),
					errorMessage: z.string().optional()
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.showFileLines({
						...inputData,
						projectPath
					});
				}
			}),
			smartSearch: createTool({
				id: "smart-search",
				description: "Intelligent search across codebase with context awareness and pattern matching.",
				inputSchema: z.object({
					query: z.string().describe("Search query or pattern"),
					type: z.enum([
						"text",
						"regex",
						"fuzzy",
						"semantic"
					]).default("text").describe("Type of search to perform"),
					scope: z.object({
						paths: z.array(z.string()).optional().describe("Specific paths to search"),
						fileTypes: z.array(z.string()).optional().describe("File extensions to include"),
						excludePaths: z.array(z.string()).optional().describe("Paths to exclude"),
						maxResults: z.number().default(50).describe("Maximum number of results")
					}).optional(),
					context: z.object({
						beforeLines: z.number().default(2).describe("Lines of context before match"),
						afterLines: z.number().default(2).describe("Lines of context after match"),
						includeDefinitions: z.boolean().default(false).describe("Include function/class definitions")
					}).optional()
				}),
				outputSchema: z.object({
					success: z.boolean(),
					matches: z.array(z.object({
						file: z.string(),
						line: z.number(),
						column: z.number().optional(),
						match: z.string(),
						context: z.object({
							before: z.array(z.string()),
							after: z.array(z.string())
						}),
						relevance: z.number().optional()
					})),
					summary: z.object({
						totalMatches: z.number(),
						filesSearched: z.number(),
						patterns: z.array(z.string())
					})
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.performSmartSearch(inputData, projectPath);
				}
			}),
			validateCode: createTool({
				id: "validate-code",
				description: "Validates code using a fast hybrid approach: syntax → semantic → lint. RECOMMENDED: Always provide specific files for optimal performance and accuracy.",
				inputSchema: z.object({
					projectPath: z.string().optional().describe("Path to the project to validate (defaults to current project)"),
					validationType: z.array(z.enum([
						"types",
						"lint",
						"schemas",
						"tests",
						"build"
					])).describe("Types of validation to perform. Recommended: [\"types\", \"lint\"] for code quality"),
					files: z.array(z.string()).optional().describe("RECOMMENDED: Specific files to validate (e.g., files you created/modified). Uses hybrid validation: fast syntax check → semantic types → ESLint. Without files, falls back to slower CLI validation.")
				}),
				outputSchema: z.object({
					valid: z.boolean(),
					errors: z.array(z.object({
						type: z.enum([
							"typescript",
							"eslint",
							"schema",
							"test",
							"build"
						]),
						severity: z.enum([
							"error",
							"warning",
							"info"
						]),
						message: z.string(),
						file: z.string().optional(),
						line: z.number().optional(),
						column: z.number().optional(),
						code: z.string().optional()
					})),
					summary: z.object({
						totalErrors: z.number(),
						totalWarnings: z.number(),
						validationsPassed: z.array(z.string()),
						validationsFailed: z.array(z.string())
					})
				}),
				execute: async (inputData) => {
					const { projectPath: validationProjectPath, validationType, files } = inputData;
					const targetPath = validationProjectPath || projectPath;
					return await AgentBuilderDefaults.validateCode({
						projectPath: targetPath,
						validationType,
						files
					});
				}
			}),
			webSearch: createTool({
				id: "web-search",
				description: "Search the web for current information and return structured results.",
				inputSchema: z.object({
					query: z.string().describe("Search query"),
					maxResults: z.number().default(10).describe("Maximum number of results to return"),
					region: z.string().default("us").describe("Search region/country code"),
					language: z.string().default("en").describe("Search language"),
					includeImages: z.boolean().default(false).describe("Include image results"),
					dateRange: z.enum([
						"day",
						"week",
						"month",
						"year",
						"all"
					]).default("all").describe("Date range filter")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					query: z.string(),
					results: z.array(z.object({
						title: z.string(),
						url: z.string(),
						snippet: z.string(),
						domain: z.string(),
						publishDate: z.string().optional(),
						relevanceScore: z.number().optional()
					})),
					totalResults: z.number(),
					searchTime: z.number(),
					suggestions: z.array(z.string()).optional(),
					errorMessage: z.string().optional()
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.webSearch(inputData);
				}
			}),
			attemptCompletion: createTool({
				id: "attempt-completion",
				description: "Signal that you believe the requested task has been completed and provide a summary.",
				inputSchema: z.object({
					summary: z.string().describe("Summary of what was accomplished"),
					changes: z.array(z.object({
						type: z.enum([
							"file_created",
							"file_modified",
							"file_deleted",
							"command_executed",
							"dependency_added"
						]),
						description: z.string(),
						path: z.string().optional()
					})).describe("List of changes made"),
					validation: z.object({
						testsRun: z.boolean().default(false),
						buildsSuccessfully: z.boolean().default(false),
						manualTestingRequired: z.boolean().default(false)
					}).describe("Validation status"),
					nextSteps: z.array(z.string()).optional().describe("Suggested next steps or follow-up actions")
				}),
				outputSchema: z.object({
					completionId: z.string(),
					status: z.enum([
						"completed",
						"needs_review",
						"needs_testing"
					]),
					summary: z.string(),
					confidence: z.number().min(0).max(100)
				}),
				execute: async (inputData) => {
					return await AgentBuilderDefaults.signalCompletion(inputData);
				}
			}),
			manageProject: createTool({
				id: "manage-project",
				description: "Handles project management including creating project structures, managing dependencies, and package operations.",
				inputSchema: z.object({
					action: z.enum([
						"create",
						"install",
						"upgrade"
					]).describe("The action to perform"),
					features: z.array(z.string()).optional().describe("Mastra features to include (e.g., [\"agents\", \"memory\", \"workflows\"])"),
					packages: z.array(z.object({
						name: z.string(),
						version: z.string().optional()
					})).optional().describe("Packages to install/upgrade")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					installed: z.array(z.string()).optional(),
					upgraded: z.array(z.string()).optional(),
					warnings: z.array(z.string()).optional(),
					message: z.string().optional(),
					details: z.string().optional(),
					errorMessage: z.string().optional()
				}),
				execute: async (inputData) => {
					const { action, features, packages } = inputData;
					try {
						switch (action) {
							case "create": return await AgentBuilderDefaults.createMastraProject({
								projectName: projectPath,
								features
							});
							case "install":
								if (!packages?.length) return {
									success: false,
									message: "Packages array is required for install action"
								};
								return await AgentBuilderDefaults.installPackages({
									packages,
									projectPath
								});
							case "upgrade":
								if (!packages?.length) return {
									success: false,
									message: "Packages array is required for upgrade action"
								};
								return await AgentBuilderDefaults.upgradePackages({
									packages,
									projectPath
								});
							default: return {
								success: false,
								message: `Unknown action: ${action}`
							};
						}
					} catch (error) {
						return {
							success: false,
							message: `Error executing ${action}: ${error instanceof Error ? error.message : String(error)}`
						};
					}
				}
			}),
			manageServer: createTool({
				id: "manage-server",
				description: "Manages the Mastra server - start, stop, restart, and check status, use the terminal tool to make curl requests to the server. There is an openapi spec for the server at http://localhost:{port}/openapi.json",
				inputSchema: z.object({
					action: z.enum([
						"start",
						"stop",
						"restart",
						"status"
					]).describe("Server management action"),
					port: z.number().optional().default(4200).describe("Port to run the server on")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					status: z.enum([
						"running",
						"stopped",
						"starting",
						"stopping",
						"unknown"
					]),
					pid: z.number().optional(),
					port: z.number().optional(),
					url: z.string().optional(),
					message: z.string().optional(),
					stdout: z.array(z.string()).optional().describe("Server output lines captured during startup"),
					errorMessage: z.string().optional()
				}),
				execute: async (inputData) => {
					const { action, port } = inputData;
					try {
						switch (action) {
							case "start": return await AgentBuilderDefaults.startMastraServer({
								port,
								projectPath
							});
							case "stop": return await AgentBuilderDefaults.stopMastraServer({
								port,
								projectPath
							});
							case "restart":
								const stopResult = await AgentBuilderDefaults.stopMastraServer({
									port,
									projectPath
								});
								if (!stopResult.success) return {
									success: false,
									status: "unknown",
									message: `Failed to restart: could not stop server on port ${port}`,
									errorMessage: stopResult.errorMessage || "Unknown stop error"
								};
								await new Promise((resolve) => setTimeout(resolve, 500));
								const startResult = await AgentBuilderDefaults.startMastraServer({
									port,
									projectPath
								});
								if (!startResult.success) return {
									success: false,
									status: "stopped",
									message: `Failed to restart: server stopped successfully but failed to start on port ${port}`,
									errorMessage: startResult.errorMessage || "Unknown start error"
								};
								return {
									...startResult,
									message: `Mastra server restarted successfully on port ${port}`
								};
							case "status": return await AgentBuilderDefaults.checkMastraServerStatus({
								port,
								projectPath
							});
							default: return {
								success: false,
								status: "unknown",
								message: `Unknown action: ${action}`
							};
						}
					} catch (error) {
						return {
							success: false,
							status: "unknown",
							message: `Error managing server: ${error instanceof Error ? error.message : String(error)}`
						};
					}
				}
			}),
			httpRequest: createTool({
				id: "http-request",
				description: "Makes HTTP requests to the Mastra server or external APIs for testing and integration",
				inputSchema: z.object({
					method: z.enum([
						"GET",
						"POST",
						"PUT",
						"DELETE",
						"PATCH"
					]).describe("HTTP method"),
					url: z.string().describe("Full URL or path (if baseUrl provided)"),
					baseUrl: z.string().optional().describe("Base URL for the server (e.g., http://localhost:4200)"),
					headers: z.record(z.string(), z.string()).optional().describe("HTTP headers"),
					body: z.any().optional().describe("Request body (will be JSON stringified if object)"),
					timeout: z.number().optional().default(3e4).describe("Request timeout in milliseconds")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					status: z.number().optional(),
					statusText: z.string().optional(),
					headers: z.record(z.string(), z.string()).optional(),
					data: z.any().optional(),
					errorMessage: z.string().optional(),
					url: z.string(),
					method: z.string()
				}),
				execute: async (inputData) => {
					const { method, url, baseUrl, headers, body, timeout } = inputData;
					try {
						return await AgentBuilderDefaults.makeHttpRequest({
							method,
							url,
							baseUrl,
							headers,
							body,
							timeout
						});
					} catch (error) {
						return {
							success: false,
							url: baseUrl ? `${baseUrl}${url}` : url,
							method,
							errorMessage: error instanceof Error ? error.message : String(error)
						};
					}
				}
			})
		};
	};
	/**
	* Filter tools for template builder mode (excludes web search and other advanced tools)
	*/
	static filterToolsForTemplateBuilder(tools) {
		const templateBuilderTools = [
			"readFile",
			"writeFile",
			"listDirectory",
			"executeCommand",
			"taskManager",
			"multiEdit",
			"replaceLines",
			"showFileLines",
			"smartSearch",
			"validateCode"
		];
		const filtered = {};
		for (const toolName of templateBuilderTools) if (tools[toolName]) filtered[toolName] = tools[toolName];
		return filtered;
	}
	/**
	* Filter tools for code editor mode (includes all tools)
	*/
	static filterToolsForCodeEditor(tools) {
		return tools;
	}
	/**
	* Get tools for a specific mode
	*/
	static async listToolsForMode(projectPath, mode = "code-editor") {
		const allTools = await AgentBuilderDefaults.DEFAULT_TOOLS(projectPath);
		if (mode === "template") return AgentBuilderDefaults.filterToolsForTemplateBuilder(allTools);
		else return AgentBuilderDefaults.filterToolsForCodeEditor(allTools);
	}
	/**
	* Create a new Mastra project using create-mastra CLI
	*/
	static async createMastraProject({ features, projectName }) {
		try {
			const args = [
				"pnpx",
				"create-mastra@latest",
				projectName?.replace(/[;&|`$(){}\[\]]/g, "") ?? "",
				"-l",
				"openai"
			];
			if (features && features.length > 0) args.push("--components", features.join(","));
			args.push("--example");
			const { stdout, stderr } = await spawnWithOutput(args[0], args.slice(1), {});
			return {
				success: true,
				projectPath: `./${projectName}`,
				message: `Successfully created Mastra project: ${projectName}.`,
				details: stdout,
				errorMessage: stderr
			};
		} catch (error) {
			console.error(error);
			return {
				success: false,
				message: `Failed to create project: ${error instanceof Error ? error.message : String(error)}`
			};
		}
	}
	/**
	* Install packages using the detected package manager
	*/
	static async installPackages({ packages, projectPath }) {
		try {
			console.info("Installing packages:", JSON.stringify(packages, null, 2));
			const packageStrings = packages.map((p) => `${p.name}`);
			await spawnSWPM(projectPath || "", "add", packageStrings);
			return {
				success: true,
				installed: packageStrings,
				message: `Successfully installed ${packages.length} package(s).`,
				details: ""
			};
		} catch (error) {
			return {
				success: false,
				message: `Failed to install packages: ${error instanceof Error ? error.message : String(error)}`
			};
		}
	}
	/**
	* Upgrade packages using the detected package manager
	*/
	static async upgradePackages({ packages, projectPath }) {
		try {
			console.info("Upgrading specific packages:", JSON.stringify(packages, null, 2));
			let packageNames = [];
			if (packages && packages.length > 0) packageNames = packages.map((p) => `${p.name}`);
			await spawnSWPM(projectPath || "", "upgrade", packageNames);
			return {
				success: true,
				upgraded: packages?.map((p) => p.name) || ["all packages"],
				message: `Packages upgraded successfully.`,
				details: ""
			};
		} catch (error) {
			return {
				success: false,
				message: `Failed to upgrade packages: ${error instanceof Error ? error.message : String(error)}`
			};
		}
	}
	/**
	* Start the Mastra server
	*/
	static async startMastraServer({ port = 4200, projectPath, env = {} }) {
		try {
			const serverEnv = {
				...process.env,
				...env,
				PORT: port.toString()
			};
			const serverProcess = spawn("pnpm", ["run", "dev"], {
				cwd: projectPath || process.cwd(),
				env: serverEnv,
				detached: true,
				stdio: "pipe"
			});
			const stdoutLines = [];
			return await new Promise((resolve, reject) => {
				const timeout = setTimeout(() => {
					reject(/* @__PURE__ */ new Error(`Server startup timeout after 30 seconds. Output: ${stdoutLines.join("\n")}`));
				}, 3e4);
				serverProcess.stdout?.on("data", (data) => {
					const output = data.toString();
					const lines = output.split("\n").filter((line) => line.trim());
					stdoutLines.push(...lines);
					if (output.includes("Mastra API running")) {
						clearTimeout(timeout);
						resolve({
							success: true,
							status: "running",
							pid: serverProcess.pid,
							port,
							url: `http://localhost:${port}`,
							message: `Mastra server started successfully on port ${port}`,
							stdout: stdoutLines
						});
					}
				});
				serverProcess.stderr?.on("data", (data) => {
					const errorOutput = data.toString();
					stdoutLines.push(`[STDERR] ${errorOutput}`);
					clearTimeout(timeout);
					reject(/* @__PURE__ */ new Error(`Server startup failed with error: ${errorOutput}`));
				});
				serverProcess.on("error", (error) => {
					clearTimeout(timeout);
					reject(error);
				});
				serverProcess.on("exit", (code, signal) => {
					clearTimeout(timeout);
					if (code !== 0 && code !== null) reject(/* @__PURE__ */ new Error(`Server process exited with code ${code}${signal ? ` (signal: ${signal})` : ""}. Output: ${stdoutLines.join("\n")}`));
				});
			});
		} catch (error) {
			return {
				success: false,
				status: "stopped",
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
	/**
	* Stop the Mastra server
	*/
	static async stopMastraServer({ port = 4200, projectPath: _projectPath }) {
		if (typeof port !== "number" || !Number.isInteger(port) || port < 1 || port > 65535) return {
			success: false,
			status: "error",
			errorMessage: `Invalid port value: ${String(port)}`
		};
		try {
			const { stdout } = await execFile$1("lsof", ["-ti", String(port)]);
			const effectiveStdout = stdout.trim() ? stdout : "No process found";
			if (!effectiveStdout || effectiveStdout === "No process found") return {
				success: true,
				status: "stopped",
				message: `No Mastra server found running on port ${port}`
			};
			const pids = stdout.trim().split("\n").filter((pid) => pid.trim());
			const killedPids = [];
			const failedPids = [];
			for (const pidStr of pids) {
				const pid = parseInt(pidStr.trim());
				if (isNaN(pid)) continue;
				try {
					process.kill(pid, "SIGTERM");
					killedPids.push(pid);
				} catch (e) {
					failedPids.push(pid);
					console.warn(`Failed to kill process ${pid}:`, e);
				}
			}
			if (killedPids.length === 0) return {
				success: false,
				status: "unknown",
				message: `Failed to stop any processes on port ${port}`,
				errorMessage: `Could not kill PIDs: ${failedPids.join(", ")}`
			};
			if (failedPids.length > 0) console.warn(`Killed ${killedPids.length} processes but failed to kill ${failedPids.length} processes: ${failedPids.join(", ")}`);
			await new Promise((resolve) => setTimeout(resolve, 2e3));
			try {
				const { stdout: checkStdoutRaw } = await execFile$1("lsof", ["-ti", String(port)]);
				const checkStdout = checkStdoutRaw.trim() ? checkStdoutRaw : "No process found";
				if (checkStdout && checkStdout !== "No process found") {
					const remainingPids = checkStdout.trim().split("\n").filter((pid) => pid.trim());
					for (const pidStr of remainingPids) {
						const pid = parseInt(pidStr.trim());
						if (!isNaN(pid)) try {
							process.kill(pid, "SIGKILL");
						} catch {}
					}
					await new Promise((resolve) => setTimeout(resolve, 1e3));
					const { stdout: finalCheckRaw } = await execFile$1("lsof", ["-ti", String(port)]);
					const finalCheck = finalCheckRaw.trim() ? finalCheckRaw : "No process found";
					if (finalCheck && finalCheck !== "No process found") return {
						success: false,
						status: "unknown",
						message: `Server processes still running on port ${port} after stop attempts`,
						errorMessage: `Remaining PIDs: ${finalCheck.trim()}`
					};
				}
			} catch (error) {
				console.warn("Failed to verify server stop:", error);
			}
			return {
				success: true,
				status: "stopped",
				message: `Mastra server stopped successfully (port ${port}). Killed PIDs: ${killedPids.join(", ")}`
			};
		} catch (error) {
			return {
				success: false,
				status: "unknown",
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
	/**
	* Check Mastra server status
	*/
	static async checkMastraServerStatus({ port = 4200, projectPath: _projectPath }) {
		try {
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), 5e3);
			const response = await fetch(`http://localhost:${port}/health`, {
				method: "GET",
				signal: controller.signal
			});
			clearTimeout(timeoutId);
			if (response.ok) return {
				success: true,
				status: "running",
				port,
				url: `http://localhost:${port}`,
				message: "Mastra server is running and healthy"
			};
			else return {
				success: false,
				status: "unknown",
				port,
				message: `Server responding but not healthy (status: ${response.status})`
			};
		} catch {
			try {
				const { stdout } = await execFile$1("lsof", ["-ti", String(port)]);
				const effectiveStdout = stdout.trim() ? stdout : "No process found";
				const hasProcess = effectiveStdout && effectiveStdout !== "No process found";
				return {
					success: Boolean(hasProcess),
					status: hasProcess ? "starting" : "stopped",
					port,
					message: hasProcess ? "Server process exists but not responding to health checks" : "No server process found on specified port"
				};
			} catch {
				return {
					success: false,
					status: "stopped",
					port,
					message: "Server is not running"
				};
			}
		}
	}
	static tsProgram = null;
	static programProjectPath = null;
	/**
	* Validate code using hybrid approach: syntax -> types -> lint
	*
	* BEST PRACTICES FOR CODING AGENTS:
	*
	* ✅ RECOMMENDED (Fast & Accurate):
	* validateCode({
	*   validationType: ['types', 'lint'],
	*   files: ['src/workflows/my-workflow.ts', 'src/components/Button.tsx']
	* })
	*
	* Performance: ~150ms
	* - Syntax check (1ms) - catches 80% of issues instantly
	* - Semantic validation (100ms) - full type checking with dependencies
	* - ESLint (50ms) - style and best practices
	* - Only shows errors from YOUR files
	*
	* ❌ AVOID (Slow & Noisy):
	* validateCode({ validationType: ['types', 'lint'] }) // no files specified
	*
	* Performance: ~2000ms+
	* - Full project CLI validation
	* - Shows errors from all project files (confusing)
	* - Much slower for coding agents
	*
	* @param projectPath - Project root directory (defaults to cwd)
	* @param validationType - ['types', 'lint'] recommended for most use cases
	* @param files - ALWAYS provide this for best performance
	*/
	static async validateCode({ projectPath, validationType, files }) {
		const errors = [];
		const validationsPassed = [];
		const validationsFailed = [];
		const targetProjectPath = projectPath || process.cwd();
		if (!files || files.length === 0) return this.validateCodeCLI({
			projectPath,
			validationType
		});
		for (const filePath of files) {
			const absolutePath = isAbsolute(filePath) ? filePath : resolve(targetProjectPath, filePath);
			try {
				const fileContent = await readFile(absolutePath, "utf-8");
				const fileResults = await this.validateSingleFileHybrid(absolutePath, fileContent, targetProjectPath, validationType);
				errors.push(...fileResults.errors);
				for (const type of validationType) if (fileResults.errors.some((e) => e.type === type && e.severity === "error")) {
					if (!validationsFailed.includes(type)) validationsFailed.push(type);
				} else if (!validationsPassed.includes(type)) validationsPassed.push(type);
			} catch (error) {
				errors.push({
					type: "typescript",
					severity: "error",
					message: `Failed to read file ${filePath}: ${error instanceof Error ? error.message : String(error)}`,
					file: filePath
				});
				validationsFailed.push("types");
			}
		}
		const totalErrors = errors.filter((e) => e.severity === "error").length;
		const totalWarnings = errors.filter((e) => e.severity === "warning").length;
		return {
			valid: totalErrors === 0,
			errors,
			summary: {
				totalErrors,
				totalWarnings,
				validationsPassed,
				validationsFailed
			}
		};
	}
	/**
	* CLI-based validation for when no specific files are provided
	*/
	static async validateCodeCLI({ projectPath, validationType }) {
		const errors = [];
		const validationsPassed = [];
		const validationsFailed = [];
		const execOptions = { cwd: projectPath };
		if (validationType.includes("types")) try {
			await execFile$1("npx", ["tsc", "--noEmit"], execOptions);
			validationsPassed.push("types");
		} catch (error) {
			let tsOutput = "";
			if (error.stdout) tsOutput = error.stdout;
			else if (error.stderr) tsOutput = error.stderr;
			else if (error.message) tsOutput = error.message;
			errors.push({
				type: "typescript",
				severity: "error",
				message: tsOutput.trim() || `TypeScript validation failed: ${error.message || String(error)}`
			});
			validationsFailed.push("types");
		}
		if (validationType.includes("lint")) try {
			const { stdout } = await execFile$1("npx", [
				"eslint",
				"--format",
				"json"
			], execOptions);
			if (stdout) {
				const eslintResults = JSON.parse(stdout);
				const eslintErrors = AgentBuilderDefaults.parseESLintErrors(eslintResults);
				errors.push(...eslintErrors);
				if (eslintErrors.some((e) => e.severity === "error")) validationsFailed.push("lint");
				else validationsPassed.push("lint");
			} else validationsPassed.push("lint");
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			if (errorMessage.includes("\"filePath\"") || errorMessage.includes("messages")) try {
				const eslintResults = JSON.parse(errorMessage);
				const eslintErrors = AgentBuilderDefaults.parseESLintErrors(eslintResults);
				errors.push(...eslintErrors);
				validationsFailed.push("lint");
			} catch {
				errors.push({
					type: "eslint",
					severity: "error",
					message: `ESLint validation failed: ${errorMessage}`
				});
				validationsFailed.push("lint");
			}
			else validationsPassed.push("lint");
		}
		const totalErrors = errors.filter((e) => e.severity === "error").length;
		const totalWarnings = errors.filter((e) => e.severity === "warning").length;
		return {
			valid: totalErrors === 0,
			errors,
			summary: {
				totalErrors,
				totalWarnings,
				validationsPassed,
				validationsFailed
			}
		};
	}
	/**
	* Hybrid validation for a single file
	*/
	static async validateSingleFileHybrid(filePath, fileContent, projectPath, validationType) {
		const errors = [];
		if (validationType.includes("types")) {
			const syntaxErrors = await this.validateSyntaxOnly(fileContent, filePath);
			errors.push(...syntaxErrors);
			if (syntaxErrors.length > 0) return { errors };
			const typeErrors = await this.validateTypesSemantic(filePath, projectPath);
			errors.push(...typeErrors);
		}
		if (validationType.includes("lint") && !errors.some((e) => e.severity === "error")) {
			const lintErrors = await this.validateESLintSingle(filePath, projectPath);
			errors.push(...lintErrors);
		}
		return { errors };
	}
	/**
	* Fast syntax-only validation using TypeScript parser
	*/
	static async validateSyntaxOnly(fileContent, fileName) {
		const errors = [];
		try {
			const ts = await import("typescript");
			const sourceFile = ts.createSourceFile(fileName, fileContent, ts.ScriptTarget.Latest, true);
			const diagnostics = ts.createProgram([fileName], {
				allowJs: true,
				checkJs: false,
				noEmit: true
			}, {
				getSourceFile: (name) => name === fileName ? sourceFile : void 0,
				writeFile: () => {},
				getCurrentDirectory: () => "",
				getDirectories: () => [],
				fileExists: (name) => name === fileName,
				readFile: (name) => name === fileName ? fileContent : void 0,
				getCanonicalFileName: (name) => name,
				useCaseSensitiveFileNames: () => true,
				getNewLine: () => "\n",
				getDefaultLibFileName: () => "lib.d.ts"
			}).getSyntacticDiagnostics(sourceFile);
			for (const diagnostic of diagnostics) if (diagnostic.start !== void 0) {
				const position = sourceFile.getLineAndCharacterOfPosition(diagnostic.start);
				errors.push({
					type: "typescript",
					severity: "error",
					message: ts.flattenDiagnosticMessageText(diagnostic.messageText, "\n"),
					file: fileName,
					line: position.line + 1,
					column: position.character + 1
				});
			}
		} catch (error) {
			console.warn("TypeScript not available for syntax validation:", error);
			const lines = fileContent.split("\n");
			const commonErrors = [
				{
					pattern: /\bimport\s+.*\s+from\s+['""][^'"]*$/,
					message: "Unterminated import statement"
				},
				{
					pattern: /\{[^}]*$/,
					message: "Unclosed brace"
				},
				{
					pattern: /\([^)]*$/,
					message: "Unclosed parenthesis"
				},
				{
					pattern: /\[[^\]]*$/,
					message: "Unclosed bracket"
				}
			];
			lines.forEach((line, index) => {
				commonErrors.forEach(({ pattern, message }) => {
					if (pattern.test(line)) errors.push({
						type: "typescript",
						severity: "error",
						message,
						file: fileName,
						line: index + 1
					});
				});
			});
		}
		return errors;
	}
	/**
	* TypeScript semantic validation using incremental program
	*/
	static async validateTypesSemantic(filePath, projectPath) {
		const errors = [];
		try {
			const program = await this.getOrCreateTSProgram(projectPath);
			if (!program) return errors;
			const sourceFile = program.getSourceFile(filePath);
			if (!sourceFile) return errors;
			const diagnostics = [...program.getSemanticDiagnostics(sourceFile), ...program.getSyntacticDiagnostics(sourceFile)];
			const ts = await import("typescript");
			for (const diagnostic of diagnostics) if (diagnostic.start !== void 0) {
				const position = sourceFile.getLineAndCharacterOfPosition(diagnostic.start);
				errors.push({
					type: "typescript",
					severity: diagnostic.category === ts.DiagnosticCategory.Warning ? "warning" : "error",
					message: ts.flattenDiagnosticMessageText(diagnostic.messageText, "\n"),
					file: filePath,
					line: position.line + 1,
					column: position.character + 1
				});
			}
		} catch (error) {
			console.warn(`TypeScript semantic validation failed for ${filePath}:`, error);
		}
		return errors;
	}
	/**
	* ESLint validation for a single file
	*/
	static async validateESLintSingle(filePath, projectPath) {
		const errors = [];
		try {
			const { stdout } = await execFile$1("npx", [
				"eslint",
				filePath,
				"--format",
				"json"
			], { cwd: projectPath });
			if (stdout) {
				const eslintResults = JSON.parse(stdout);
				const eslintErrors = this.parseESLintErrors(eslintResults);
				errors.push(...eslintErrors);
			}
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			if (errorMessage.includes("\"filePath\"") || errorMessage.includes("messages")) try {
				const eslintResults = JSON.parse(errorMessage);
				const eslintErrors = this.parseESLintErrors(eslintResults);
				errors.push(...eslintErrors);
			} catch {}
		}
		return errors;
	}
	/**
	* Get or create TypeScript program
	*/
	static async getOrCreateTSProgram(projectPath) {
		if (this.tsProgram && this.programProjectPath === projectPath) return this.tsProgram;
		try {
			const ts = await import("typescript");
			const configPath = ts.findConfigFile(projectPath, ts.sys.fileExists, "tsconfig.json");
			if (!configPath) return null;
			const configFile = ts.readConfigFile(configPath, ts.sys.readFile);
			if (configFile.error) return null;
			const parsedConfig = ts.parseJsonConfigFileContent(configFile.config, ts.sys, projectPath);
			if (parsedConfig.errors.length > 0) return null;
			this.tsProgram = ts.createProgram({
				rootNames: parsedConfig.fileNames,
				options: parsedConfig.options
			});
			this.programProjectPath = projectPath;
			return this.tsProgram;
		} catch (error) {
			console.warn("Failed to create TypeScript program:", error);
			return null;
		}
	}
	/**
	* Parse ESLint errors from JSON output
	*/
	static parseESLintErrors(eslintResults) {
		const errors = [];
		for (const result of eslintResults) for (const message of result.messages || []) if (message.message) errors.push({
			type: "eslint",
			severity: message.severity === 1 ? "warning" : "error",
			message: message.message,
			file: result.filePath || void 0,
			line: message.line || void 0,
			column: message.column || void 0,
			code: message.ruleId || void 0
		});
		return errors;
	}
	/**
	* Make HTTP request to server or external API
	*/
	static async makeHttpRequest({ method, url, baseUrl, headers = {}, body, timeout = 3e4 }) {
		try {
			const fullUrl = baseUrl ? `${baseUrl}${url}` : url;
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), timeout);
			const requestOptions = {
				method,
				headers: {
					"Content-Type": "application/json",
					...headers
				},
				signal: controller.signal
			};
			if (body && (method === "POST" || method === "PUT" || method === "PATCH")) requestOptions.body = typeof body === "string" ? body : JSON.stringify(body);
			const response = await fetch(fullUrl, requestOptions);
			clearTimeout(timeoutId);
			let data;
			if (response.headers.get("content-type")?.includes("application/json")) data = await response.json();
			else data = await response.text();
			const responseHeaders = {};
			response.headers.forEach((value, key) => {
				responseHeaders[key] = value;
			});
			return {
				success: response.ok,
				status: response.status,
				statusText: response.statusText,
				headers: responseHeaders,
				data,
				url: fullUrl,
				method
			};
		} catch (error) {
			return {
				success: false,
				url: baseUrl ? `${baseUrl}${url}` : url,
				method,
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
	/**
	* Enhanced task management system for complex coding tasks
	*/
	static async manageTaskList(context) {
		if (!AgentBuilderDefaults.taskStorage) AgentBuilderDefaults.taskStorage = /* @__PURE__ */ new Map();
		const sessions = Array.from(AgentBuilderDefaults.taskStorage.keys());
		if (sessions.length > 10) sessions.slice(0, sessions.length - 10).forEach((session) => AgentBuilderDefaults.taskStorage.delete(session));
		const sessionId = "current";
		const existingTasks = AgentBuilderDefaults.taskStorage.get(sessionId) || [];
		try {
			switch (context.action) {
				case "create":
					if (!context.tasks?.length) return {
						success: false,
						tasks: existingTasks,
						message: "No tasks provided for creation"
					};
					const newTasks = context.tasks.map((task) => ({
						...task,
						createdAt: (/* @__PURE__ */ new Date()).toISOString(),
						updatedAt: (/* @__PURE__ */ new Date()).toISOString()
					}));
					const allTasks = [...existingTasks, ...newTasks];
					AgentBuilderDefaults.taskStorage.set(sessionId, allTasks);
					return {
						success: true,
						tasks: allTasks,
						message: `Created ${newTasks.length} new task(s)`
					};
				case "update":
					if (!context.tasks?.length) return {
						success: false,
						tasks: existingTasks,
						message: "No tasks provided for update"
					};
					const updatedTasks = existingTasks.map((existing) => {
						const update = context.tasks.find((t) => t.id === existing.id);
						return update ? {
							...existing,
							...update,
							updatedAt: (/* @__PURE__ */ new Date()).toISOString()
						} : existing;
					});
					AgentBuilderDefaults.taskStorage.set(sessionId, updatedTasks);
					return {
						success: true,
						tasks: updatedTasks,
						message: "Tasks updated successfully"
					};
				case "complete":
					if (!context.taskId) return {
						success: false,
						tasks: existingTasks,
						message: "Task ID required for completion"
					};
					const completedTasks = existingTasks.map((task) => task.id === context.taskId ? {
						...task,
						status: "completed",
						updatedAt: (/* @__PURE__ */ new Date()).toISOString()
					} : task);
					AgentBuilderDefaults.taskStorage.set(sessionId, completedTasks);
					return {
						success: true,
						tasks: completedTasks,
						message: `Task ${context.taskId} marked as completed`
					};
				case "remove":
					if (!context.taskId) return {
						success: false,
						tasks: existingTasks,
						message: "Task ID required for removal"
					};
					const filteredTasks = existingTasks.filter((task) => task.id !== context.taskId);
					AgentBuilderDefaults.taskStorage.set(sessionId, filteredTasks);
					return {
						success: true,
						tasks: filteredTasks,
						message: `Task ${context.taskId} removed`
					};
				default: return {
					success: true,
					tasks: existingTasks,
					message: `Found ${existingTasks.length} task(s)`
				};
			}
		} catch (error) {
			return {
				success: false,
				tasks: existingTasks,
				message: `Task management error: ${error instanceof Error ? error.message : String(error)}`
			};
		}
	}
	/**
	* Perform multiple edits across files atomically
	*/
	static async performMultiEdit(context) {
		const { operations, createBackup = false, projectPath = process.cwd() } = context;
		const results = [];
		try {
			for (const operation of operations) {
				const filePath = isAbsolute(operation.filePath) ? operation.filePath : join(projectPath, operation.filePath);
				let editsApplied = 0;
				const errors = [];
				let backup;
				try {
					if (createBackup) {
						const backupPath = `${filePath}.backup.${Date.now()}`;
						await writeFile(backupPath, await readFile(filePath, "utf-8"), "utf-8");
						backup = backupPath;
					}
					let content = await readFile(filePath, "utf-8");
					for (const edit of operation.edits) {
						const { oldString, newString, replaceAll = false } = edit;
						if (replaceAll) {
							const regex = new RegExp(oldString.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
							const matches = content.match(regex);
							if (matches) {
								content = content.replace(regex, newString);
								editsApplied += matches.length;
							}
						} else if (content.includes(oldString)) {
							content = content.replace(oldString, newString);
							editsApplied++;
						} else errors.push(`String not found: "${oldString.substring(0, 50)}${oldString.length > 50 ? "..." : ""}"`);
					}
					await writeFile(filePath, content, "utf-8");
				} catch (error) {
					errors.push(`File operation error: ${error instanceof Error ? error.message : String(error)}`);
				}
				results.push({
					filePath: operation.filePath,
					editsApplied,
					errors,
					backup
				});
			}
			const totalEdits = results.reduce((sum, r) => sum + r.editsApplied, 0);
			const totalErrors = results.reduce((sum, r) => sum + r.errors.length, 0);
			return {
				success: totalErrors === 0,
				results,
				message: `Applied ${totalEdits} edits across ${operations.length} files${totalErrors > 0 ? ` with ${totalErrors} errors` : ""}`
			};
		} catch (error) {
			return {
				success: false,
				results,
				message: `Multi-edit operation failed: ${error instanceof Error ? error.message : String(error)}`
			};
		}
	}
	/**
	* Replace specific line ranges in a file with new content
	*/
	static async replaceLines(context) {
		const { filePath, startLine, endLine, newContent, createBackup = false, projectPath = process.cwd() } = context;
		try {
			const fullPath = isAbsolute(filePath) ? filePath : join(projectPath, filePath);
			const content = await readFile(fullPath, "utf-8");
			const lines = content.split("\n");
			if (startLine < 1 || endLine < 1) return {
				success: false,
				message: `Line numbers must be 1 or greater. Got startLine: ${startLine}, endLine: ${endLine}`,
				errorMessage: "Invalid line range"
			};
			if (startLine > lines.length || endLine > lines.length) return {
				success: false,
				message: `Line range ${startLine}-${endLine} is out of bounds. File has ${lines.length} lines. Remember: lines are 1-indexed, so valid range is 1-${lines.length}.`,
				errorMessage: "Invalid line range"
			};
			if (startLine > endLine) return {
				success: false,
				message: `Start line (${startLine}) cannot be greater than end line (${endLine}).`,
				errorMessage: "Invalid line range"
			};
			let backup;
			if (createBackup) {
				const backupPath = `${fullPath}.backup.${Date.now()}`;
				await writeFile(backupPath, content, "utf-8");
				backup = backupPath;
			}
			const beforeLines = lines.slice(0, startLine - 1);
			const afterLines = lines.slice(endLine);
			const newLines = newContent ? newContent.split("\n") : [];
			await writeFile(fullPath, [
				...beforeLines,
				...newLines,
				...afterLines
			].join("\n"), "utf-8");
			const linesReplaced = endLine - startLine + 1;
			return {
				success: true,
				message: `Successfully replaced ${linesReplaced} lines (${startLine}-${endLine}) with ${newLines.length} new lines in ${filePath}`,
				linesReplaced,
				backup
			};
		} catch (error) {
			return {
				success: false,
				message: `Failed to replace lines: ${error instanceof Error ? error.message : String(error)}`,
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
	/**
	* Show file lines with line numbers for debugging
	*/
	static async showFileLines(context) {
		const { filePath, startLine, endLine, context: contextLines = 2, projectPath = process.cwd() } = context;
		try {
			const lines = (await readFile(isAbsolute(filePath) ? filePath : join(projectPath, filePath), "utf-8")).split("\n");
			let targetStart = startLine;
			let targetEnd = endLine;
			if (!targetStart) {
				targetStart = 1;
				targetEnd = lines.length;
			} else if (!targetEnd) targetEnd = targetStart;
			const displayStart = Math.max(1, targetStart - contextLines);
			const displayEnd = Math.min(lines.length, targetEnd + contextLines);
			const result = [];
			for (let i = displayStart; i <= displayEnd; i++) {
				const lineIndex = i - 1;
				const isTarget = i >= targetStart && i <= targetEnd;
				result.push({
					lineNumber: i,
					content: lineIndex < lines.length ? lines[lineIndex] ?? "" : "",
					isTarget
				});
			}
			return {
				success: true,
				lines: result,
				totalLines: lines.length,
				message: `Showing lines ${displayStart}-${displayEnd} of ${lines.length} total lines in ${filePath}`
			};
		} catch (error) {
			return {
				success: false,
				lines: [],
				totalLines: 0,
				message: `Failed to read file: ${error instanceof Error ? error.message : String(error)}`,
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
	/**
	* Signal task completion
	*/
	static async signalCompletion(context) {
		const completionId = `completion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
		let confidence = 70;
		if (context.validation.testsRun) confidence += 15;
		if (context.validation.buildsSuccessfully) confidence += 15;
		if (context.validation.manualTestingRequired) confidence -= 10;
		let status;
		if (context.validation.testsRun && context.validation.buildsSuccessfully) status = "completed";
		else if (context.validation.manualTestingRequired) status = "needs_testing";
		else status = "needs_review";
		return {
			completionId,
			status,
			summary: context.summary,
			confidence: Math.min(100, Math.max(0, confidence))
		};
	}
	/**
	* Perform intelligent search with context
	*/
	static async performSmartSearch(context, projectPath) {
		try {
			const { query, type = "text", scope = {}, context: searchContext = {} } = context;
			const { paths = ["."], fileTypes = [], excludePaths = [], maxResults = 50 } = scope;
			const { beforeLines = 2, afterLines = 2 } = searchContext;
			const rgArgs = [];
			if (beforeLines > 0) rgArgs.push("-B", beforeLines.toString());
			if (afterLines > 0) rgArgs.push("-A", afterLines.toString());
			rgArgs.push("-n");
			if (type === "regex") rgArgs.push("-e");
			else if (type === "fuzzy") rgArgs.push("--fixed-strings");
			if (fileTypes.length > 0) fileTypes.forEach((ft) => {
				rgArgs.push("--type-add", `custom:*.${ft}`, "-t", "custom");
			});
			excludePaths.forEach((path) => {
				rgArgs.push("--glob", `!${path}`);
			});
			rgArgs.push("-m", maxResults.toString());
			rgArgs.push(query);
			rgArgs.push(...paths);
			const { stdout } = await execFile$1("rg", rgArgs, { cwd: projectPath });
			const lines = stdout.split("\n").filter((line) => line.trim());
			const matches = [];
			let currentMatch = null;
			lines.forEach((line) => {
				if (line.includes(":") && !line.startsWith("-")) {
					const parts = line.split(":");
					if (parts.length >= 3) {
						if (currentMatch) matches.push(currentMatch);
						currentMatch = {
							file: parts[0] || "",
							line: parseInt(parts[1] || "0"),
							match: parts.slice(2).join(":"),
							context: {
								before: [],
								after: []
							},
							relevance: type === "fuzzy" ? Math.random() * 100 : void 0
						};
					}
				} else if (line.startsWith("-") && currentMatch) {
					const contextLine = line.substring(1);
					if (currentMatch.context.before.length < beforeLines) currentMatch.context.before.push(contextLine);
					else currentMatch.context.after.push(contextLine);
				}
			});
			if (currentMatch) matches.push(currentMatch);
			const filesSearched = new Set(matches.map((m) => m.file)).size;
			return {
				success: true,
				matches: matches.slice(0, maxResults),
				summary: {
					totalMatches: matches.length,
					filesSearched,
					patterns: [query]
				}
			};
		} catch {
			return {
				success: false,
				matches: [],
				summary: {
					totalMatches: 0,
					filesSearched: 0,
					patterns: [context.query]
				}
			};
		}
	}
	static taskStorage;
	static pendingQuestions;
	/**
	* Read file contents with optional line range
	*/
	static async readFile(context) {
		try {
			const { filePath, startLine, endLine, encoding = "utf-8", projectPath } = context;
			const resolvedPath = isAbsolute(filePath) ? filePath : resolve(projectPath || process.cwd(), filePath);
			const stats = await stat(resolvedPath);
			const content = await readFile(resolvedPath, { encoding });
			const lines = content.split("\n");
			let resultContent = content;
			let resultLines = lines;
			if (startLine !== void 0 || endLine !== void 0) {
				const start = Math.max(0, (startLine || 1) - 1);
				const end = endLine !== void 0 ? Math.min(lines.length, endLine) : lines.length;
				resultLines = lines.slice(start, end);
				resultContent = resultLines.join("\n");
			}
			return {
				success: true,
				content: resultContent,
				lines: resultLines,
				metadata: {
					size: stats.size,
					totalLines: lines.length,
					encoding,
					lastModified: stats.mtime.toISOString()
				}
			};
		} catch (error) {
			return {
				success: false,
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
	/**
	* Write content to file with directory creation and backup options
	*/
	static async writeFile(context) {
		try {
			const { filePath, content, createDirs = true, encoding = "utf-8", projectPath } = context;
			const resolvedPath = isAbsolute(filePath) ? filePath : resolve(projectPath || process.cwd(), filePath);
			const dir = dirname(resolvedPath);
			if (createDirs) await mkdir(dir, { recursive: true });
			await writeFile(resolvedPath, content, { encoding });
			return {
				success: true,
				filePath: resolvedPath,
				bytesWritten: Buffer.byteLength(content, encoding),
				message: `Successfully wrote ${Buffer.byteLength(content, encoding)} bytes to ${filePath}`
			};
		} catch (error) {
			return {
				success: false,
				filePath: context.filePath,
				message: `Failed to write file: ${error instanceof Error ? error.message : String(error)}`,
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
	/**
	* List directory contents with filtering and metadata
	*/
	static async listDirectory(context) {
		try {
			const { path, recursive = false, includeHidden = false, pattern, maxDepth = 10, includeMetadata = true, projectPath } = context;
			const gitignorePath = join(projectPath || process.cwd(), ".gitignore");
			let gitignoreFilter;
			try {
				const gitignoreContent = await readFile(gitignorePath, "utf-8");
				gitignoreFilter = ignore().add(gitignoreContent);
			} catch (err) {
				if (err.code !== "ENOENT") console.error(`Error reading .gitignore file:`, err);
			}
			const resolvedPath = isAbsolute(path) ? path : resolve(projectPath || process.cwd(), path);
			const items = [];
			async function processDirectory(dirPath, currentDepth = 0) {
				const relativeToProject = relative(projectPath || process.cwd(), dirPath);
				if (gitignoreFilter?.ignores(relativeToProject)) return;
				if (currentDepth > maxDepth) return;
				const entries = await readdir(dirPath);
				for (const entry of entries) {
					const entryPath = join(dirPath, entry);
					const relativeEntryPath = relative(projectPath || process.cwd(), entryPath);
					if (gitignoreFilter?.ignores(relativeEntryPath)) continue;
					if (!includeHidden && entry.startsWith(".")) continue;
					const fullPath = entryPath;
					const relativePath = relative(resolvedPath, fullPath);
					if (pattern) {
						const regexPattern = pattern.replace(/\*/g, ".*").replace(/\?/g, ".");
						if (!new RegExp(regexPattern).test(entry)) continue;
					}
					let stats;
					let type;
					try {
						stats = await stat(fullPath);
						if (stats.isDirectory()) type = "directory";
						else if (stats.isSymbolicLink()) type = "symlink";
						else type = "file";
					} catch {
						continue;
					}
					const item = {
						name: entry,
						path: relativePath || entry,
						type
					};
					if (includeMetadata) {
						item.size = stats.size;
						item.lastModified = stats.mtime.toISOString();
						item.permissions = `0${(stats.mode & parseInt("777", 8)).toString(8)}`;
					}
					items.push(item);
					if (recursive && type === "directory") await processDirectory(fullPath, currentDepth + 1);
				}
			}
			await processDirectory(resolvedPath);
			return {
				success: true,
				items,
				totalItems: items.length,
				path: resolvedPath,
				message: `Listed ${items.length} items in ${resolvedPath}`
			};
		} catch (error) {
			return {
				success: false,
				items: [],
				totalItems: 0,
				path: context.path,
				message: `Failed to list directory: ${error instanceof Error ? error.message : String(error)}`,
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
	/**
	* Execute shell commands with proper error handling
	*/
	static async executeCommand(context) {
		const startTime = Date.now();
		try {
			const { command, workingDirectory, timeout = 3e4, captureOutput = true, shell, env } = context;
			const execOptions = {
				timeout,
				env: {
					...process.env,
					...env
				}
			};
			if (workingDirectory) execOptions.cwd = workingDirectory;
			if (shell) execOptions.shell = shell;
			const { stdout, stderr } = await exec$1(command, execOptions);
			const executionTime = Date.now() - startTime;
			return {
				success: true,
				exitCode: 0,
				stdout: captureOutput ? String(stdout) : void 0,
				stderr: captureOutput ? String(stderr) : void 0,
				command,
				workingDirectory,
				executionTime
			};
		} catch (error) {
			const executionTime = Date.now() - startTime;
			return {
				success: false,
				exitCode: error.code || 1,
				stdout: String(error.stdout || ""),
				stderr: String(error.stderr || ""),
				command: context.command,
				workingDirectory: context.workingDirectory,
				executionTime,
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
	/**
	* Web search using a simple search approach
	*/
	static async webSearch(context) {
		try {
			const { query, maxResults = 10 } = context;
			const startTime = Date.now();
			const searchUrl = `https://api.duckduckgo.com/?q=${encodeURIComponent(query)}&format=json&no_redirect=1&skip_disambig=1`;
			const data = await (await fetch(searchUrl)).json();
			const results = [];
			if (data.RelatedTopics && Array.isArray(data.RelatedTopics)) {
				for (const topic of data.RelatedTopics.slice(0, maxResults)) if (topic.FirstURL && topic.Text) {
					const url = new URL(topic.FirstURL);
					results.push({
						title: topic.Text.split(" - ")[0] || topic.Text.substring(0, 60),
						url: topic.FirstURL,
						snippet: topic.Text,
						domain: url.hostname,
						relevanceScore: Math.random() * 100
					});
				}
			}
			if (data.Abstract && data.AbstractURL) {
				const url = new URL(data.AbstractURL);
				results.unshift({
					title: data.Heading || "Main Result",
					url: data.AbstractURL,
					snippet: data.Abstract,
					domain: url.hostname,
					relevanceScore: 100
				});
			}
			const searchTime = Date.now() - startTime;
			return {
				success: true,
				query,
				results: results.slice(0, maxResults),
				totalResults: results.length,
				searchTime,
				suggestions: data.RelatedTopics?.slice(maxResults, maxResults + 3)?.map((t) => t.Text?.split(" - ")[0] || t.Text?.substring(0, 30)).filter(Boolean) || []
			};
		} catch (error) {
			return {
				success: false,
				query: context.query,
				results: [],
				totalResults: 0,
				searchTime: 0,
				errorMessage: error instanceof Error ? error.message : String(error)
			};
		}
	}
};
//#endregion
//#region src/processors/tool-summary.ts
/**
* Summarizes tool calls and caches results to avoid re-summarizing identical calls
*/
var ToolSummaryProcessor = class {
	id = "tool-summary-processor";
	name = "ToolSummaryProcessor";
	summaryAgent;
	summaryCache = /* @__PURE__ */ new Map();
	constructor({ summaryModel }) {
		this.summaryAgent = new Agent({
			id: "tool-summary-agent",
			name: "Tool Summary Agent",
			description: "A summary agent that summarizes tool calls and results",
			instructions: "You are a summary agent that summarizes tool calls and results",
			model: summaryModel
		});
	}
	/**
	* Creates a cache key from tool call arguments
	*/
	createCacheKey(toolCall) {
		if (!toolCall) return "unknown";
		const toolName = toolCall.toolName || "unknown";
		const args = toolCall.args || {};
		const sortedArgs = Object.keys(args).sort().reduce((result, key) => {
			result[key] = args[key];
			return result;
		}, {});
		return `${toolName}:${JSON.stringify(sortedArgs)}`;
	}
	/**
	* Clears the summary cache
	*/
	clearCache() {
		this.summaryCache.clear();
	}
	/**
	* Gets cache statistics
	*/
	getCacheStats() {
		return {
			size: this.summaryCache.size,
			keys: Array.from(this.summaryCache.keys())
		};
	}
	async processInput({ messages, messageList: _messageList }) {
		const summaryTasks = [];
		for (const message of messages) if (message.content.format === 2 && message.content.parts) for (let partIndex = 0; partIndex < message.content.parts.length; partIndex++) {
			const part = message.content.parts[partIndex];
			if (part && part.type === "tool-invocation" && part.toolInvocation?.state === "result") {
				const cacheKey = this.createCacheKey(part.toolInvocation);
				const cachedSummary = this.summaryCache.get(cacheKey);
				if (cachedSummary) message.content.parts[partIndex] = {
					type: "tool-invocation",
					toolInvocation: {
						state: "result",
						step: part.toolInvocation.step,
						toolCallId: part.toolInvocation.toolCallId,
						toolName: part.toolInvocation.toolName,
						args: part.toolInvocation.args,
						result: `Tool call summary: ${cachedSummary}`
					}
				};
				else {
					const summaryPromise = this.summaryAgent.generate(`Summarize the following tool call: ${JSON.stringify(part.toolInvocation)}`);
					summaryTasks.push({
						message,
						partIndex,
						promise: summaryPromise,
						cacheKey
					});
				}
			}
		}
		if (summaryTasks.length > 0) {
			const summaryResults = await Promise.allSettled(summaryTasks.map((task) => task.promise));
			summaryTasks.forEach((task, index) => {
				const result = summaryResults[index];
				if (!result) return;
				if (result.status === "fulfilled") {
					const summaryText = result.value.text;
					this.summaryCache.set(task.cacheKey, summaryText);
					if (task.message.content.format === 2 && task.message.content.parts) {
						const part = task.message.content.parts[task.partIndex];
						if (part && part.type === "tool-invocation" && part.toolInvocation?.state === "result") task.message.content.parts[task.partIndex] = {
							type: "tool-invocation",
							toolInvocation: {
								state: "result",
								step: part.toolInvocation.step,
								toolCallId: part.toolInvocation.toolCallId,
								toolName: part.toolInvocation.toolName,
								args: part.toolInvocation.args,
								result: `Tool call summary: ${summaryText}`
							}
						};
					}
				} else if (result.status === "rejected") console.warn(`Failed to generate summary for tool call:`, result.reason);
			});
		}
		return messages;
	}
};
//#endregion
//#region src/agent/index.ts
var AgentBuilder = class extends Agent {
	builderConfig;
	/**
	* Constructor for AgentBuilder
	*/
	constructor(config) {
		const combinedInstructions = (config.instructions ? `## Priority Instructions \n\n${config.instructions}` : "") + AgentBuilderDefaults.DEFAULT_INSTRUCTIONS(config.projectPath);
		const memory = new Memory({ options: AgentBuilderDefaults.DEFAULT_MEMORY_CONFIG });
		memory.setStorage(config.storage ?? new InMemoryStore());
		const agentConfig = {
			id: "agent-builder",
			name: "agent-builder",
			description: "An AI agent specialized in generating Mastra agents, tools, and workflows from natural language requirements.",
			instructions: combinedInstructions,
			model: config.model,
			tools: async () => {
				return {
					...await AgentBuilderDefaults.listToolsForMode(config.projectPath, config.mode),
					...config.tools || {}
				};
			},
			memory,
			inputProcessors: [new ToolSummaryProcessor({ summaryModel: config.summaryModel || config.model })]
		};
		super(agentConfig);
		this.builderConfig = config;
	}
	/**
	* Enhanced generate method with AgentBuilder-specific configuration
	* Overrides the base Agent generate method to provide additional project context
	*/
	generateLegacy = async (messages, generateOptions = {}) => {
		const { maxSteps, ...baseOptions } = generateOptions;
		const originalInstructions = await this.getInstructions({ requestContext: generateOptions?.requestContext });
		const additionalInstructions = baseOptions.instructions;
		let enhancedInstructions = originalInstructions;
		if (additionalInstructions) enhancedInstructions = `${originalInstructions}\n\n${additionalInstructions}`;
		const enhancedContext = [...baseOptions.context || []];
		const enhancedOptions = {
			...baseOptions,
			maxSteps: maxSteps || 100,
			temperature: .3,
			instructions: enhancedInstructions,
			context: enhancedContext
		};
		this.logger.debug("Starting generation with enhanced context", {
			agent: this.name,
			projectPath: this.builderConfig.projectPath
		});
		return super.generateLegacy(messages, enhancedOptions);
	};
	/**
	* Enhanced stream method with AgentBuilder-specific configuration
	* Overrides the base Agent stream method to provide additional project context
	*/
	streamLegacy = async (messages, streamOptions = {}) => {
		const { maxSteps, ...baseOptions } = streamOptions;
		const originalInstructions = await this.getInstructions({ requestContext: streamOptions?.requestContext });
		const additionalInstructions = baseOptions.instructions;
		let enhancedInstructions = originalInstructions;
		if (additionalInstructions) enhancedInstructions = `${originalInstructions}\n\n${additionalInstructions}`;
		const enhancedContext = [...baseOptions.context || []];
		const enhancedOptions = {
			...baseOptions,
			maxSteps: maxSteps || 100,
			temperature: .3,
			instructions: enhancedInstructions,
			context: enhancedContext
		};
		this.logger.debug("Starting streaming with enhanced context", {
			agent: this.name,
			projectPath: this.builderConfig.projectPath
		});
		return super.streamLegacy(messages, enhancedOptions);
	};
	async stream(messages, streamOptions) {
		const { ...baseOptions } = streamOptions || {};
		const originalInstructions = await this.getInstructions({ requestContext: streamOptions?.requestContext });
		const additionalInstructions = baseOptions.instructions;
		let enhancedInstructions = originalInstructions;
		if (additionalInstructions) enhancedInstructions = `${originalInstructions}\n\n${additionalInstructions}`;
		const enhancedContext = [...baseOptions.context || []];
		const enhancedOptions = {
			...baseOptions,
			temperature: .3,
			maxSteps: baseOptions?.maxSteps || 100,
			instructions: enhancedInstructions,
			context: enhancedContext
		};
		this.logger.debug("Starting streaming with enhanced context", {
			agent: this.name,
			projectPath: this.builderConfig.projectPath
		});
		return super.stream(messages, enhancedOptions);
	}
	async generate(messages, options) {
		const { ...baseOptions } = options || {};
		const originalInstructions = await this.getInstructions({ requestContext: options?.requestContext });
		const additionalInstructions = baseOptions.instructions;
		let enhancedInstructions = originalInstructions;
		if (additionalInstructions) enhancedInstructions = `${originalInstructions}\n\n${additionalInstructions}`;
		const enhancedContext = [...baseOptions.context || []];
		const enhancedOptions = {
			...baseOptions,
			temperature: .3,
			maxSteps: baseOptions?.maxSteps || 100,
			instructions: enhancedInstructions,
			context: enhancedContext
		};
		this.logger.debug("Starting generation with enhanced context", {
			agent: this.name,
			projectPath: this.builderConfig.projectPath
		});
		return super.generate(messages, enhancedOptions);
	}
};
//#endregion
//#region src/workflows/template-builder/template-builder.ts
const cloneTemplateStep = createStep({
	id: "clone-template",
	description: "Clone the template repository to a temporary directory at the specified ref",
	inputSchema: AgentBuilderInputSchema,
	outputSchema: CloneTemplateResultSchema,
	execute: async ({ inputData }) => {
		const { repo, ref = "main", slug, targetPath } = inputData;
		if (!repo) throw new Error("Repository URL or path is required");
		const inferredSlug = slug || repo.split("/").pop()?.replace(/\.git$/, "") || "template";
		const tempDir = await mkdtemp(join(tmpdir(), "mastra-template-"));
		try {
			await gitClone(repo, tempDir);
			if (ref !== "main" && ref !== "master") await gitCheckoutRef(tempDir, ref);
			return {
				templateDir: tempDir,
				commitSha: (await gitRevParse(tempDir, "HEAD")).trim(),
				slug: inferredSlug,
				success: true,
				targetPath
			};
		} catch (error) {
			try {
				await rm(tempDir, {
					recursive: true,
					force: true
				});
			} catch {}
			return {
				templateDir: "",
				commitSha: "",
				slug: slug || "unknown",
				success: false,
				error: `Failed to clone template: ${error instanceof Error ? error.message : String(error)}`,
				targetPath
			};
		}
	}
});
const analyzePackageStep = createStep({
	id: "analyze-package",
	description: "Analyze the template package.json to extract dependency information",
	inputSchema: CloneTemplateResultSchema,
	outputSchema: PackageAnalysisSchema,
	execute: async ({ inputData }) => {
		console.info("Analyzing template package.json...");
		const { templateDir } = inputData;
		const packageJsonPath = join(templateDir, "package.json");
		try {
			const packageJsonContent = await readFile(packageJsonPath, "utf-8");
			const packageJson = JSON.parse(packageJsonContent);
			console.info("Template package.json:", JSON.stringify(packageJson, null, 2));
			return {
				dependencies: packageJson.dependencies || {},
				devDependencies: packageJson.devDependencies || {},
				peerDependencies: packageJson.peerDependencies || {},
				scripts: packageJson.scripts || {},
				name: packageJson.name || "",
				version: packageJson.version || "",
				description: packageJson.description || "",
				success: true
			};
		} catch (error) {
			console.warn(`Failed to read template package.json: ${error instanceof Error ? error.message : String(error)}`);
			return {
				dependencies: {},
				devDependencies: {},
				peerDependencies: {},
				scripts: {},
				name: "",
				version: "",
				description: "",
				success: true
			};
		}
	}
});
const discoverUnitsStep = createStep({
	id: "discover-units",
	description: "Discover template units by analyzing the templates directory structure",
	inputSchema: CloneTemplateResultSchema,
	outputSchema: DiscoveryResultSchema,
	execute: async ({ inputData, requestContext }) => {
		const { templateDir } = inputData;
		const targetPath = resolveTargetPath(inputData, requestContext);
		const tools = await AgentBuilderDefaults.DEFAULT_TOOLS(templateDir);
		console.info("targetPath", targetPath);
		const model = await resolveModel({
			requestContext,
			projectPath: targetPath,
			defaultModel: openai("gpt-4.1")
		});
		try {
			const agent = new Agent({
				id: "mastra-project-discoverer",
				model,
				instructions: `You are an expert at analyzing Mastra projects.

Your task is to scan the provided directory and identify all available units (agents, workflows, tools, MCP servers, networks).

Mastra Project Structure Analysis:
- Each Mastra project has a structure like: ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE.agent}, ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE.workflow}, ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE.tool}, ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE["mcp-server"]}, ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE.network}
- Analyze TypeScript files in each category directory to identify exported units

CRITICAL: YOU MUST USE YOUR TOOLS (readFile, listDirectory) TO DISCOVER THE UNITS IN THE TEMPLATE DIRECTORY.

IMPORTANT - Agent Discovery Rules:
1. **Multiple Agent Files**: Some templates have separate files for each agent (e.g., evaluationAgent.ts, researchAgent.ts)
2. **Single File Multiple Agents**: Some files may export multiple agents (look for multiple 'export const' or 'export default' statements)
3. **Agent Identification**: Look for exported variables that are instances of 'new Agent()' or similar patterns
4. **Naming Convention**: Agent names should be extracted from the export name (e.g., 'weatherAgent', 'evaluationAgent')

For each Mastra project directory you analyze:
1. Scan all TypeScript files in ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE.agent} and identify ALL exported agents
2. Scan all TypeScript files in ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE.workflow} and identify ALL exported workflows
3. Scan all TypeScript files in ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE.tool} and identify ALL exported tools
4. Scan all TypeScript files in ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE["mcp-server"]} and identify ALL exported MCP servers
5. Scan all TypeScript files in ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE.network} and identify ALL exported networks
6. Scan for any OTHER files in src/mastra that are NOT in the above default folders (e.g., lib/, utils/, types/, etc.) and identify them as 'other' files

IMPORTANT - Naming Consistency Rules:
- For ALL unit types (including 'other'), the 'name' field should be the filename WITHOUT extension
- For structured units (agents, workflows, tools, etc.), prefer the actual export name if clearly identifiable
- use the base filename without extension for the id (e.g., 'util.ts' → name: 'util')
- use the relative path from the template root for the file (e.g., 'src/mastra/lib/util.ts' → file: 'src/mastra/lib/util.ts')

Return the actual exported names of the units, as well as the file names.`,
				name: "Mastra Project Discoverer",
				tools: {
					readFile: tools.readFile,
					listDirectory: tools.listDirectory
				}
			});
			const isSupported = isSupportedLanguageModel(await agent.getModel());
			const prompt = `Analyze the Mastra project directory structure at "${templateDir}".

            List directory contents using listDirectory tool, and then analyze each file with readFile tool.
      IMPORTANT:
      - Look inside the actual file content to find export statements like 'export const agentName = new Agent(...)'
      - A single file may contain multiple exports
      - Return the actual exported variable names, as well as the file names
      - If a directory doesn't exist or has no files, return an empty array

      Return the analysis in the exact format specified in the output schema.`;
			const output = z.object({
				agents: z.array(z.object({
					name: z.string(),
					file: z.string()
				})).optional(),
				workflows: z.array(z.object({
					name: z.string(),
					file: z.string()
				})).optional(),
				tools: z.array(z.object({
					name: z.string(),
					file: z.string()
				})).optional(),
				mcp: z.array(z.object({
					name: z.string(),
					file: z.string()
				})).optional(),
				networks: z.array(z.object({
					name: z.string(),
					file: z.string()
				})).optional(),
				other: z.array(z.object({
					name: z.string(),
					file: z.string()
				})).optional()
			});
			let result;
			if (isSupported) result = await tryGenerateWithJsonFallback(agent, prompt, {
				structuredOutput: { schema: output },
				maxSteps: 100
			});
			else {
				const jsonSchema = standardSchemaToJSONSchema(toStandardSchema(output));
				result = await agent.generateLegacy(prompt, {
					experimental_output: jsonSchema,
					maxSteps: 100
				});
			}
			const template = result.object ?? {};
			const units = [];
			template.agents?.forEach((agentId) => {
				units.push({
					kind: "agent",
					id: agentId.name,
					file: agentId.file
				});
			});
			template.workflows?.forEach((workflowId) => {
				units.push({
					kind: "workflow",
					id: workflowId.name,
					file: workflowId.file
				});
			});
			template.tools?.forEach((toolId) => {
				units.push({
					kind: "tool",
					id: toolId.name,
					file: toolId.file
				});
			});
			template.mcp?.forEach((mcpId) => {
				units.push({
					kind: "mcp-server",
					id: mcpId.name,
					file: mcpId.file
				});
			});
			template.networks?.forEach((networkId) => {
				units.push({
					kind: "network",
					id: networkId.name,
					file: networkId.file
				});
			});
			template.other?.forEach((otherId) => {
				units.push({
					kind: "other",
					id: otherId.name,
					file: otherId.file
				});
			});
			console.info("Discovered units:", JSON.stringify(units, null, 2));
			if (units.length === 0) throw new Error(`No Mastra units (agents, workflows, tools) found in template.
          Possible causes:
          - Template may not follow standard Mastra structure
          - AI agent couldn't analyze template files (model/token limits)
          - Template is empty or in wrong branch

          Debug steps:
          - Check template has files in src/mastra/ directories
          - Try a different branch
          - Check template repository structure manually`);
			return {
				units,
				success: true
			};
		} catch (error) {
			console.error("Failed to discover units:", error);
			return {
				units: [],
				success: false,
				error: `Failed to discover units: ${error instanceof Error ? error.message : String(error)}`
			};
		}
	}
});
const orderUnitsStep = createStep({
	id: "order-units",
	description: "Sort units in topological order based on kind weights",
	inputSchema: DiscoveryResultSchema,
	outputSchema: OrderedUnitsSchema,
	execute: async ({ inputData }) => {
		const { units } = inputData;
		return {
			orderedUnits: [...units].sort((a, b) => {
				return kindWeight(a.kind) - kindWeight(b.kind);
			}),
			success: true
		};
	}
});
const prepareBranchStep = createStep({
	id: "prepare-branch",
	description: "Create or switch to integration branch before modifications",
	inputSchema: PrepareBranchInputSchema,
	outputSchema: PrepareBranchResultSchema,
	execute: async ({ inputData, requestContext }) => {
		const targetPath = resolveTargetPath(inputData, requestContext);
		try {
			const branchName = `feat/install-template-${inputData.slug}`;
			await gitCheckoutBranch(branchName, targetPath);
			return {
				branchName,
				success: true
			};
		} catch (error) {
			console.error("Failed to prepare branch:", error);
			return {
				branchName: `feat/install-template-${inputData.slug}`,
				success: false,
				error: `Failed to prepare branch: ${error instanceof Error ? error.message : String(error)}`
			};
		}
	}
});
const packageMergeStep = createStep({
	id: "package-merge",
	description: "Merge template package.json dependencies into target project",
	inputSchema: PackageMergeInputSchema,
	outputSchema: PackageMergeResultSchema,
	execute: async ({ inputData, requestContext }) => {
		console.info("Package merge step starting...");
		const { slug, packageInfo } = inputData;
		const targetPath = resolveTargetPath(inputData, requestContext);
		try {
			const targetPkgPath = join(targetPath, "package.json");
			let targetPkgRaw = "{}";
			try {
				targetPkgRaw = await readFile(targetPkgPath, "utf-8");
			} catch {
				console.warn(`No existing package.json at ${targetPkgPath}, creating a new one`);
			}
			let targetPkg;
			try {
				targetPkg = JSON.parse(targetPkgRaw || "{}");
			} catch (e) {
				throw new Error(`Failed to parse existing package.json at ${targetPkgPath}: ${e instanceof Error ? e.message : String(e)}`);
			}
			const ensureObj = (o) => o && typeof o === "object" ? o : {};
			targetPkg.dependencies = ensureObj(targetPkg.dependencies);
			targetPkg.devDependencies = ensureObj(targetPkg.devDependencies);
			targetPkg.peerDependencies = ensureObj(targetPkg.peerDependencies);
			targetPkg.scripts = ensureObj(targetPkg.scripts);
			const tplDeps = ensureObj(packageInfo.dependencies);
			const tplDevDeps = ensureObj(packageInfo.devDependencies);
			const tplPeerDeps = ensureObj(packageInfo.peerDependencies);
			const tplScripts = ensureObj(packageInfo.scripts);
			const existsAnywhere = (name) => name in targetPkg.dependencies || name in targetPkg.devDependencies || name in targetPkg.peerDependencies;
			for (const [name, ver] of Object.entries(tplDeps)) if (!existsAnywhere(name)) targetPkg.dependencies[name] = String(ver);
			for (const [name, ver] of Object.entries(tplDevDeps)) if (!existsAnywhere(name)) targetPkg.devDependencies[name] = String(ver);
			for (const [name, ver] of Object.entries(tplPeerDeps)) if (!(name in targetPkg.peerDependencies)) targetPkg.peerDependencies[name] = String(ver);
			const prefix = `template:${slug}:`;
			for (const [name, cmd] of Object.entries(tplScripts)) {
				const newKey = `${prefix}${name}`;
				if (!(newKey in targetPkg.scripts)) targetPkg.scripts[newKey] = String(cmd);
			}
			await writeFile(targetPkgPath, JSON.stringify(targetPkg, null, 2), "utf-8");
			await gitAddAndCommit(targetPath, `feat(template): merge deps for ${slug}`, [targetPkgPath], { skipIfNoStaged: true });
			return {
				success: true,
				applied: true,
				message: `Successfully merged template dependencies for ${slug}`
			};
		} catch (error) {
			console.error("Package merge failed:", error);
			return {
				success: false,
				applied: false,
				message: `Package merge failed: ${error instanceof Error ? error.message : String(error)}`,
				error: error instanceof Error ? error.message : String(error)
			};
		}
	}
});
const installStep = createStep({
	id: "install",
	description: "Install packages based on merged package.json",
	inputSchema: InstallInputSchema,
	outputSchema: InstallResultSchema,
	execute: async ({ inputData, requestContext }) => {
		console.info("Running install step...");
		const targetPath = resolveTargetPath(inputData, requestContext);
		try {
			await spawnSWPM(targetPath, "install", []);
			const lock = [
				"pnpm-lock.yaml",
				"package-lock.json",
				"yarn.lock"
			].map((f) => join(targetPath, f)).find((f) => existsSync(f));
			if (lock) await gitAddAndCommit(targetPath, `chore(template): commit lockfile after install`, [lock], { skipIfNoStaged: true });
			return { success: true };
		} catch (error) {
			console.error("Install failed:", error);
			return {
				success: false,
				error: error instanceof Error ? error.message : String(error)
			};
		}
	}
});
const programmaticFileCopyStep = createStep({
	id: "programmatic-file-copy",
	description: "Programmatically copy template files to target project based on ordered units",
	inputSchema: FileCopyInputSchema,
	outputSchema: FileCopyResultSchema,
	execute: async ({ inputData, requestContext }) => {
		console.info("Programmatic file copy step starting...");
		const { orderedUnits, templateDir, commitSha, slug } = inputData;
		const targetPath = resolveTargetPath(inputData, requestContext);
		try {
			const copiedFiles = [];
			const conflicts = [];
			const analyzeNamingConvention = async (directory) => {
				try {
					const tsFiles = (await readdir(resolve(targetPath, directory), { withFileTypes: true })).filter((f) => f.isFile() && f.name.endsWith(".ts")).map((f) => f.name);
					if (tsFiles.length === 0) return "unknown";
					const camelCaseCount = tsFiles.filter((f) => /^[a-z][a-zA-Z0-9]*\.ts$/.test(f)).length;
					const snakeCaseCount = tsFiles.filter((f) => /^[a-z][a-z0-9_]*\.ts$/.test(f) && f.includes("_")).length;
					const kebabCaseCount = tsFiles.filter((f) => /^[a-z][a-z0-9-]*\.ts$/.test(f) && f.includes("-")).length;
					const pascalCaseCount = tsFiles.filter((f) => /^[A-Z][a-zA-Z0-9]*\.ts$/.test(f)).length;
					const max = Math.max(camelCaseCount, snakeCaseCount, kebabCaseCount, pascalCaseCount);
					if (max === 0) return "unknown";
					if (camelCaseCount === max) return "camelCase";
					if (snakeCaseCount === max) return "snake_case";
					if (kebabCaseCount === max) return "kebab-case";
					if (pascalCaseCount === max) return "PascalCase";
					return "unknown";
				} catch {
					return "unknown";
				}
			};
			const convertNaming = (name, convention) => {
				const baseName = basename(name, extname(name));
				const ext = extname(name);
				const toWords = (s) => {
					return s.replace(/[-_]/g, " ").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2").replace(/([a-z0-9])([A-Z])/g, "$1 $2").split(/\s+/).filter(Boolean).map((w) => w.toLowerCase());
				};
				const words = toWords(baseName);
				switch (convention) {
					case "camelCase": return words.map((w, i) => i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)).join("") + ext;
					case "snake_case": return words.join("_") + ext;
					case "kebab-case": return words.join("-") + ext;
					case "PascalCase": return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join("") + ext;
					default: return name;
				}
			};
			for (const unit of orderedUnits) {
				console.info(`Processing ${unit.kind} unit "${unit.id}" from file "${unit.file}"`);
				let sourceFile;
				let resolvedUnitFile;
				if (unit.file.includes("/")) {
					sourceFile = resolve(templateDir, unit.file);
					resolvedUnitFile = unit.file;
				} else {
					const folderPath = AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE[unit.kind];
					if (!folderPath) {
						conflicts.push({
							unit: {
								kind: unit.kind,
								id: unit.id
							},
							issue: `Unknown unit kind: ${unit.kind}`,
							sourceFile: unit.file,
							targetFile: "N/A"
						});
						continue;
					}
					resolvedUnitFile = `${folderPath}/${unit.file}`;
					sourceFile = resolve(templateDir, resolvedUnitFile);
				}
				if (!existsSync(sourceFile)) {
					conflicts.push({
						unit: {
							kind: unit.kind,
							id: unit.id
						},
						issue: `Source file not found: ${sourceFile}`,
						sourceFile: resolvedUnitFile,
						targetFile: "N/A"
					});
					continue;
				}
				const targetDir = dirname(resolvedUnitFile);
				const namingConvention = await analyzeNamingConvention(targetDir);
				console.info(`Detected naming convention in ${targetDir}: ${namingConvention}`);
				const baseId = extname(unit.id) !== "" ? basename(unit.id, extname(unit.id)) : unit.id;
				const fileExtension = extname(unit.file);
				const convertedFileName = namingConvention !== "unknown" ? convertNaming(baseId + fileExtension, namingConvention) : baseId + fileExtension;
				const targetFile = resolve(targetPath, targetDir, convertedFileName);
				if (existsSync(targetFile)) {
					const strategy = determineConflictStrategy(unit, targetFile);
					console.info(`File exists: ${convertedFileName}, using strategy: ${strategy}`);
					switch (strategy) {
						case "skip":
							conflicts.push({
								unit: {
									kind: unit.kind,
									id: unit.id
								},
								issue: `File exists - skipped: ${convertedFileName}`,
								sourceFile: unit.file,
								targetFile: `${targetDir}/${convertedFileName}`
							});
							console.info(`⏭️ Skipped ${unit.kind} "${unit.id}": file already exists`);
							continue;
						case "backup-and-replace": try {
							await backupAndReplaceFile(sourceFile, targetFile);
							copiedFiles.push({
								source: sourceFile,
								destination: targetFile,
								unit: {
									kind: unit.kind,
									id: unit.id
								}
							});
							console.info(`🔄 Replaced ${unit.kind} "${unit.id}": ${unit.file} → ${convertedFileName} (backup created)`);
							continue;
						} catch (backupError) {
							conflicts.push({
								unit: {
									kind: unit.kind,
									id: unit.id
								},
								issue: `Failed to backup and replace: ${backupError instanceof Error ? backupError.message : String(backupError)}`,
								sourceFile: unit.file,
								targetFile: `${targetDir}/${convertedFileName}`
							});
							continue;
						}
						case "rename": try {
							const uniqueTargetFile = await renameAndCopyFile(sourceFile, targetFile);
							copiedFiles.push({
								source: sourceFile,
								destination: uniqueTargetFile,
								unit: {
									kind: unit.kind,
									id: unit.id
								}
							});
							console.info(`📝 Renamed ${unit.kind} "${unit.id}": ${unit.file} → ${basename(uniqueTargetFile)}`);
							continue;
						} catch (renameError) {
							conflicts.push({
								unit: {
									kind: unit.kind,
									id: unit.id
								},
								issue: `Failed to rename and copy: ${renameError instanceof Error ? renameError.message : String(renameError)}`,
								sourceFile: unit.file,
								targetFile: `${targetDir}/${convertedFileName}`
							});
							continue;
						}
						default:
							conflicts.push({
								unit: {
									kind: unit.kind,
									id: unit.id
								},
								issue: `Unknown conflict strategy: ${strategy}`,
								sourceFile: unit.file,
								targetFile: `${targetDir}/${convertedFileName}`
							});
							continue;
					}
				}
				await mkdir(dirname(targetFile), { recursive: true });
				try {
					await copyFile(sourceFile, targetFile);
					copiedFiles.push({
						source: sourceFile,
						destination: targetFile,
						unit: {
							kind: unit.kind,
							id: unit.id
						}
					});
					console.info(`✓ Copied ${unit.kind} "${unit.id}": ${unit.file} → ${convertedFileName}`);
				} catch (copyError) {
					conflicts.push({
						unit: {
							kind: unit.kind,
							id: unit.id
						},
						issue: `Failed to copy file: ${copyError instanceof Error ? copyError.message : String(copyError)}`,
						sourceFile: unit.file,
						targetFile: `${targetDir}/${convertedFileName}`
					});
				}
			}
			try {
				const targetTsconfig = resolve(targetPath, "tsconfig.json");
				if (!existsSync(targetTsconfig)) {
					const templateTsconfig = resolve(templateDir, "tsconfig.json");
					if (existsSync(templateTsconfig)) {
						await copyFile(templateTsconfig, targetTsconfig);
						copiedFiles.push({
							source: templateTsconfig,
							destination: targetTsconfig,
							unit: {
								kind: "other",
								id: "tsconfig.json"
							}
						});
						console.info("✓ Copied tsconfig.json from template to target");
					} else {
						await writeFile(targetTsconfig, JSON.stringify({
							compilerOptions: {
								target: "ES2020",
								module: "NodeNext",
								moduleResolution: "NodeNext",
								strict: false,
								esModuleInterop: true,
								skipLibCheck: true,
								resolveJsonModule: true,
								outDir: "dist"
							},
							include: [
								"**/*.ts",
								"**/*.tsx",
								"**/*.mts",
								"**/*.cts"
							],
							exclude: [
								"node_modules",
								"dist",
								"build",
								".next",
								".output",
								".turbo"
							]
						}, null, 2), "utf-8");
						copiedFiles.push({
							source: "[generated tsconfig.json]",
							destination: targetTsconfig,
							unit: {
								kind: "other",
								id: "tsconfig.json"
							}
						});
						console.info("✓ Generated minimal tsconfig.json in target");
					}
				}
			} catch (e) {
				conflicts.push({
					unit: {
						kind: "other",
						id: "tsconfig.json"
					},
					issue: `Failed to ensure tsconfig.json: ${e instanceof Error ? e.message : String(e)}`,
					sourceFile: "tsconfig.json",
					targetFile: "tsconfig.json"
				});
			}
			try {
				const targetMastraIndex = resolve(targetPath, "src/mastra/index.ts");
				if (!existsSync(targetMastraIndex)) {
					const templateMastraIndex = resolve(templateDir, "src/mastra/index.ts");
					if (existsSync(templateMastraIndex)) {
						if (!existsSync(dirname(targetMastraIndex))) await mkdir(dirname(targetMastraIndex), { recursive: true });
						await copyFile(templateMastraIndex, targetMastraIndex);
						copiedFiles.push({
							source: templateMastraIndex,
							destination: targetMastraIndex,
							unit: {
								kind: "other",
								id: "mastra-index"
							}
						});
						console.info("✓ Copied Mastra index file from template");
					}
				}
			} catch (e) {
				conflicts.push({
					unit: {
						kind: "other",
						id: "mastra-index"
					},
					issue: `Failed to ensure Mastra index file: ${e instanceof Error ? e.message : String(e)}`,
					sourceFile: "src/mastra/index.ts",
					targetFile: "src/mastra/index.ts"
				});
			}
			try {
				const targetGitignore = resolve(targetPath, ".gitignore");
				const templateGitignore = resolve(templateDir, ".gitignore");
				const targetExists = existsSync(targetGitignore);
				if (existsSync(templateGitignore)) if (!targetExists) {
					await copyFile(templateGitignore, targetGitignore);
					copiedFiles.push({
						source: templateGitignore,
						destination: targetGitignore,
						unit: {
							kind: "other",
							id: "gitignore"
						}
					});
					console.info("✓ Copied .gitignore from template to target");
				} else {
					const targetContent = await readFile(targetGitignore, "utf-8");
					const mergedContent = mergeGitignoreFiles(targetContent, await readFile(templateGitignore, "utf-8"), slug);
					if (mergedContent !== targetContent) {
						const addedLines = mergedContent.split("\n").length - targetContent.split("\n").length;
						await writeFile(targetGitignore, mergedContent, "utf-8");
						copiedFiles.push({
							source: templateGitignore,
							destination: targetGitignore,
							unit: {
								kind: "other",
								id: "gitignore-merge"
							}
						});
						console.info(`✓ Merged template .gitignore entries into existing .gitignore (${addedLines} new entries)`);
					} else console.info("ℹ No new .gitignore entries to add from template");
				}
			} catch (e) {
				conflicts.push({
					unit: {
						kind: "other",
						id: "gitignore"
					},
					issue: `Failed to handle .gitignore file: ${e instanceof Error ? e.message : String(e)}`,
					sourceFile: ".gitignore",
					targetFile: ".gitignore"
				});
			}
			try {
				const { variables } = inputData;
				if (variables && Object.keys(variables).length > 0) {
					const targetEnv = resolve(targetPath, ".env");
					if (!existsSync(targetEnv)) {
						await writeFile(targetEnv, [`# Environment variables for ${slug}`, ...Object.entries(variables).map(([key, value]) => `${key}=${value}`)].join("\n"), "utf-8");
						copiedFiles.push({
							source: "[template variables]",
							destination: targetEnv,
							unit: {
								kind: "other",
								id: "env"
							}
						});
						console.info(`✓ Created .env file with ${Object.keys(variables).length} template variables`);
					} else {
						const targetContent = await readFile(targetEnv, "utf-8");
						const mergedContent = mergeEnvFiles(targetContent, variables, slug);
						if (mergedContent !== targetContent) {
							const addedLines = mergedContent.split("\n").length - targetContent.split("\n").length;
							await writeFile(targetEnv, mergedContent, "utf-8");
							copiedFiles.push({
								source: "[template variables]",
								destination: targetEnv,
								unit: {
									kind: "other",
									id: "env-merge"
								}
							});
							console.info(`✓ Merged new environment variables into existing .env file (${addedLines} new entries)`);
						} else console.info("ℹ No new environment variables to add (all already exist in .env)");
					}
				}
			} catch (e) {
				conflicts.push({
					unit: {
						kind: "other",
						id: "env"
					},
					issue: `Failed to handle .env file: ${e instanceof Error ? e.message : String(e)}`,
					sourceFile: ".env",
					targetFile: ".env"
				});
			}
			if (copiedFiles.length > 0) try {
				const fileList = copiedFiles.map((f) => f.destination);
				await gitAddAndCommit(targetPath, `feat(template): copy ${copiedFiles.length} files from ${slug}@${commitSha.substring(0, 7)}`, fileList, { skipIfNoStaged: true });
				console.info(`✓ Committed ${copiedFiles.length} copied files`);
			} catch (commitError) {
				console.warn("Failed to commit copied files:", commitError);
			}
			const message = `Programmatic file copy completed. Copied ${copiedFiles.length} files, ${conflicts.length} conflicts detected.`;
			console.info(message);
			return {
				success: true,
				copiedFiles,
				conflicts,
				message
			};
		} catch (error) {
			console.error("Programmatic file copy failed:", error);
			return {
				success: false,
				copiedFiles: [],
				conflicts: [],
				message: `Programmatic file copy failed: ${error instanceof Error ? error.message : String(error)}`,
				error: error instanceof Error ? error.message : String(error)
			};
		}
	}
});
const intelligentMergeStep = createStep({
	id: "intelligent-merge",
	description: "Use AgentBuilder to intelligently merge template files",
	inputSchema: IntelligentMergeInputSchema,
	outputSchema: IntelligentMergeResultSchema,
	execute: async ({ inputData, requestContext }) => {
		console.info("Intelligent merge step starting...");
		const { conflicts, copiedFiles, commitSha, slug, templateDir, branchName } = inputData;
		const targetPath = resolveTargetPath(inputData, requestContext);
		try {
			const model = await resolveModel({
				requestContext,
				projectPath: targetPath,
				defaultModel: openai("gpt-4.1")
			});
			const copyFileTool = createTool({
				id: "copy-file",
				description: "Copy a file from template to target project (use only for edge cases - most files are already copied programmatically).",
				inputSchema: z.object({
					sourcePath: z.string().describe("Path to the source file relative to template directory"),
					destinationPath: z.string().describe("Path to the destination file relative to target project")
				}),
				outputSchema: z.object({
					success: z.boolean(),
					message: z.string(),
					errorMessage: z.string().optional()
				}),
				execute: async (input) => {
					try {
						const { sourcePath, destinationPath } = input;
						const resolvedSourcePath = resolve(templateDir, sourcePath);
						const resolvedDestinationPath = resolve(targetPath, destinationPath);
						if (existsSync(resolvedSourcePath) && !existsSync(dirname(resolvedDestinationPath))) await mkdir(dirname(resolvedDestinationPath), { recursive: true });
						await copyFile(resolvedSourcePath, resolvedDestinationPath);
						return {
							success: true,
							message: `Successfully copied file from ${sourcePath} to ${destinationPath}`
						};
					} catch (err) {
						return {
							success: false,
							message: `Failed to copy file: ${err instanceof Error ? err.message : String(err)}`,
							errorMessage: err instanceof Error ? err.message : String(err)
						};
					}
				}
			});
			const agentBuilder = new AgentBuilder({
				projectPath: targetPath,
				mode: "template",
				model,
				instructions: `
You are an expert at integrating Mastra template components into existing projects.

CRITICAL CONTEXT:
- Files have been programmatically copied from template to target project
- Your job is to handle integration issues, registration, and validation

FILES SUCCESSFULLY COPIED:
${JSON.stringify(copiedFiles, null, 2)}

CONFLICTS TO RESOLVE:
${JSON.stringify(conflicts, null, 2)}

CRITICAL INSTRUCTIONS:
1. **Package management**: NO need to install packages (already handled by package merge step)
2. **File copying**: Most files are already copied programmatically. Only use copyFile tool for edge cases where additional files are needed for conflict resolution

KEY RESPONSIBILITIES:
1. Resolve any conflicts from the programmatic copy step
2. Register components in existing Mastra index file (agents, workflows, networks, mcp-servers)
3. DO NOT register tools in existing Mastra index file - tools should remain standalone
4. Copy additional files ONLY if needed for conflict resolution

MASTRA INDEX FILE HANDLING (src/mastra/index.ts):
1. **Verify the file exists**
   - Call readFile
   - If it fails with ENOENT (or listDirectory shows it missing) -> copyFile the template version to src/mastra/index.ts, then confirm it now exists
   - Always verify after copying that the file exists and is accessible

2. **Edit the file**
   - Always work with the full file content
   - Generate the complete, correct source (imports, anchors, registrations, formatting)
   - Keep existing registrations intact and maintain file structure
   - Ensure proper spacing and organization of new additions

3. **Handle anchors and structure**
   - When generating new content, ensure you do not duplicate existing imports or object entries
   - If required anchors (e.g., agents: {}) are missing, add them while generating the new content
   - Add missing anchors just before the closing brace of the Mastra config
   - Do not restructure or reorder existing anchors and registrations

CRITICAL: ALWAYS use writeFile to update the mastra/index.ts file when needed to register new components.

MASTRA-SPECIFIC REGISTRATION:
- Agents: Register in existing Mastra index file
- Workflows: Register in existing Mastra index file
- Networks: Register in existing Mastra index file
- MCP servers: Register in existing Mastra index file
- Tools: Copy to ${AgentBuilderDefaults.DEFAULT_FOLDER_STRUCTURE.tool} but DO NOT register in existing Mastra index file
- If an anchor (e.g., "agents: {") is not found, avoid complex restructuring; instead, insert the missing anchor on a new line (e.g., add "agents: {" just before the closing brace of the Mastra config) and then proceed with the other registrations.

CONFLICT RESOLUTION AND FILE COPYING:
- Only copy files if needed to resolve specific conflicts
- When copying files from template:
  - Ensure you get the right file name and path
  - Verify the destination directory exists
  - Maintain the same relative path structure
  - Only copy files that are actually needed
- Preserve existing functionality when resolving conflicts
- Focus on registration and conflict resolution, validation will happen in a later step

Template information:
- Slug: ${slug}
- Commit: ${commitSha.substring(0, 7)}
- Branch: ${branchName}
`,
				tools: { copyFile: copyFileTool }
			});
			const tasks = [];
			conflicts.forEach((conflict) => {
				tasks.push({
					id: `conflict-${conflict.unit.kind}-${conflict.unit.id}`,
					content: `Resolve conflict: ${conflict.issue}`,
					status: "pending",
					priority: "high",
					notes: `Unit: ${conflict.unit.kind}:${conflict.unit.id}, Issue: ${conflict.issue}, Source: ${conflict.sourceFile}, Target: ${conflict.targetFile}`
				});
			});
			const registrableKinds = /* @__PURE__ */ new Set([
				"agent",
				"workflow",
				"network",
				"mcp-server"
			]);
			const registrableFiles = copiedFiles.filter((f) => registrableKinds.has(f.unit.kind));
			const targetMastraIndex = resolve(targetPath, "src/mastra/index.ts");
			const mastraIndexExists = existsSync(targetMastraIndex);
			console.info(`Mastra index exists: ${mastraIndexExists} at ${targetMastraIndex}`);
			console.info("Registrable components:", registrableFiles.map((f) => `${f.unit.kind}:${f.unit.id}`));
			if (registrableFiles.length > 0) tasks.push({
				id: "register-components",
				content: `Register ${registrableFiles.length} components in existing Mastra index file (src/mastra/index.ts)`,
				status: "pending",
				priority: "medium",
				dependencies: conflicts.length > 0 ? conflicts.map((c) => `conflict-${c.unit.kind}-${c.unit.id}`) : void 0,
				notes: `Components to register: ${registrableFiles.map((f) => `${f.unit.kind}:${f.unit.id}`).join(", ")}`
			});
			console.info(`Creating task list with ${tasks.length} tasks...`);
			await AgentBuilderDefaults.manageTaskList({
				action: "create",
				tasks
			});
			await logGitState(targetPath, "before intelligent merge");
			const prompt = `
You need to work through a task list to complete the template integration.

CRITICAL INSTRUCTIONS:

**STEP 1: GET YOUR TASK LIST**
1. Use manageTaskList tool with action "list" to see all pending tasks
2. Work through tasks in dependency order (complete dependencies first)

**STEP 2: PROCESS EACH TASK SYSTEMATICALLY**
For each task:
1. Use manageTaskList to mark the current task as 'in_progress'
2. Complete the task according to its requirements
3. Use manageTaskList to mark the task as 'completed' when done
4. Continue until all tasks are completed

**TASK TYPES AND REQUIREMENTS:**

**Conflict Resolution Tasks:**
- Analyze the specific conflict and determine best resolution strategy
- For file name conflicts: merge content or rename appropriately
- For missing files: investigate and copy if needed
- For other issues: apply appropriate fixes

**Component Registration Task:**
- Update main Mastra instance file to register new components
- Only register: agents, workflows, networks, mcp-servers
- DO NOT register tools in main config
- Ensure proper import paths and naming conventions

**COMMIT STRATEGY:**
- After resolving conflicts: "feat(template): resolve conflicts for ${slug}@${commitSha.substring(0, 7)}"
- After registration: "feat(template): register components from ${slug}@${commitSha.substring(0, 7)}"

**CRITICAL NOTES:**
- Template source: ${templateDir}
- Target project: ${targetPath}
- Focus ONLY on conflict resolution and component registration
- Use executeCommand for git commits after each task
- DO NOT perform validation - that's handled by the dedicated validation step

Start by listing your tasks and work through them systematically!
`;
			const result = isSupportedLanguageModel(await agentBuilder.getModel()) ? await agentBuilder.stream(prompt) : await agentBuilder.streamLegacy(prompt);
			const actualResolutions = [];
			for await (const chunk of result.fullStream) if (chunk.type === "step-finish" || chunk.type === "step-start") {
				const chunkData = "payload" in chunk ? chunk.payload : chunk;
				console.info({
					type: chunk.type,
					msgId: chunkData.messageId
				});
			} else {
				console.info(JSON.stringify(chunk, null, 2));
				if (chunk.type === "tool-result") {
					const chunkData = "payload" in chunk ? chunk.payload : chunk;
					if (chunkData.toolName === "manageTaskList") try {
						const toolResult = chunkData.result;
						if (toolResult.action === "update" && toolResult.status === "completed") {
							actualResolutions.push({
								taskId: toolResult.taskId || "",
								action: toolResult.action,
								status: toolResult.status,
								content: toolResult.content || "",
								notes: toolResult.notes
							});
							console.info(`📋 Task completed: ${toolResult.taskId} - ${toolResult.content}`);
						}
					} catch (parseError) {
						console.warn("Failed to parse task management result:", parseError);
					}
				}
			}
			await logGitState(targetPath, "after intelligent merge");
			const conflictResolutions = conflicts.map((conflict) => {
				const taskId = `conflict-${conflict.unit.kind}-${conflict.unit.id}`;
				const actualResolution = actualResolutions.find((r) => r.taskId === taskId);
				if (actualResolution) return {
					unit: conflict.unit,
					issue: conflict.issue,
					resolution: actualResolution.notes || actualResolution.content || `Completed: ${conflict.unit.kind} ${conflict.unit.id}`,
					actualWork: true
				};
				else return {
					unit: conflict.unit,
					issue: conflict.issue,
					resolution: `No specific resolution found for ${conflict.unit.kind} ${conflict.unit.id}`,
					actualWork: false
				};
			});
			await gitAddAndCommit(targetPath, `feat(template): apply intelligent merge for ${slug}`, void 0, { skipIfNoStaged: true });
			return {
				success: true,
				applied: true,
				message: `Successfully resolved ${conflicts.length} conflicts from template ${slug}`,
				conflictsResolved: conflictResolutions
			};
		} catch (error) {
			return {
				success: false,
				applied: false,
				message: `Failed to resolve conflicts: ${error instanceof Error ? error.message : String(error)}`,
				conflictsResolved: [],
				error: error instanceof Error ? error.message : String(error)
			};
		}
	}
});
const validationAndFixStep = createStep({
	id: "validation-and-fix",
	description: "Validate the merged template code and fix any issues using a specialized agent",
	inputSchema: ValidationFixInputSchema,
	outputSchema: ValidationFixResultSchema,
	execute: async ({ inputData, requestContext }) => {
		console.info("Validation and fix step starting...");
		const { commitSha, slug, orderedUnits, templateDir, copiedFiles, conflictsResolved, maxIterations = 5 } = inputData;
		const targetPath = resolveTargetPath(inputData, requestContext);
		if (!(copiedFiles.length > 0 || conflictsResolved && conflictsResolved.length > 0)) {
			console.info("⏭️ Skipping validation - no files copied or conflicts resolved");
			return {
				success: true,
				applied: false,
				message: "No changes to validate - template already integrated or no conflicts resolved",
				validationResults: {
					valid: true,
					errorsFixed: 0,
					remainingErrors: 0
				}
			};
		}
		console.info(`📋 Changes detected: ${copiedFiles.length} files copied, ${conflictsResolved?.length || 0} conflicts resolved`);
		let currentIteration = 1;
		try {
			const model = await resolveModel({
				requestContext,
				projectPath: targetPath,
				defaultModel: openai("gpt-4.1")
			});
			const allTools = await AgentBuilderDefaults.listToolsForMode(targetPath, "template");
			const validationAgent = new Agent({
				id: "code-validator-fixer",
				name: "Code Validator Fixer",
				description: "Specialized agent for validating and fixing template integration issues",
				instructions: `You are a code validation and fixing specialist. Your job is to:

1. **Run comprehensive validation** using the validateCode tool to check for:
   - TypeScript compilation errors
   - ESLint issues
   - Import/export problems
   - Missing dependencies
   - Index file structure and exports
   - Component registration correctness
   - Naming convention compliance

2. **Fix validation errors systematically**:
   - Use readFile to examine files with errors
   - Use multiEdit for simple search-replace fixes (single line changes)
   - Use replaceLines for complex multiline fixes (imports, function signatures, etc.)
   - Use listDirectory to understand project structure when fixing import paths
   - Update file contents to resolve TypeScript and linting issues

3. **Choose the right tool for the job**:
   - multiEdit: Simple replacements, single line changes, small fixes
   - replaceLines: Multiline imports, function signatures, complex code blocks
   - writeFile: ONLY for creating new files (never overwrite existing)

4. **Create missing files ONLY when necessary**:
   - Use writeFile ONLY for creating NEW files that don't exist
   - NEVER overwrite existing files - use multiEdit or replaceLines instead
   - Common cases: missing barrel files (index.ts), missing config files, missing type definitions
   - Always check with readFile first to ensure file doesn't exist

5. **Fix ALL template integration issues**:
   - Fix import path issues in copied files
   - Ensure TypeScript imports and exports are correct
   - Validate integration works properly
   - Fix files copied with new names based on unit IDs
   - Update original template imports that reference old filenames
   - Fix missing imports in index files
   - Fix incorrect file paths in imports
   - Fix type mismatches after integration
   - Fix missing exports in barrel files
   - Use the COPIED FILES mapping below to fix import paths
   - Fix any missing dependencies or module resolution issues

6. **Validate index file structure**:
   - Correct imports for all components
   - Proper anchor structure (agents: {}, etc.)
   - No duplicate registrations
   - Correct export names and paths
   - Proper formatting and organization

7. **Follow naming conventions**:
   Import paths:
   - camelCase: import { myAgent } from './myAgent'
   - snake_case: import { myAgent } from './my_agent'
   - kebab-case: import { myAgent } from './my-agent'
   - PascalCase: import { MyAgent } from './MyAgent'

   File names:
   - camelCase: weatherAgent.ts, chatAgent.ts
   - snake_case: weather_agent.ts, chat_agent.ts
   - kebab-case: weather-agent.ts, chat-agent.ts
   - PascalCase: WeatherAgent.ts, ChatAgent.ts

   Key Rule: Keep variable/export names unchanged, only adapt file names and import paths

8. **Re-validate after fixes** to ensure all issues are resolved

CRITICAL: Always validate the entire project first to get a complete picture of issues, then fix them systematically, and re-validate to confirm fixes worked.

CRITICAL TOOL SELECTION GUIDE:
- **multiEdit**: Use for simple string replacements, single-line changes
  Example: changing './oldPath' to './newPath'
  
- **replaceLines**: Use for multiline fixes, complex code structures
  Example: fixing multiline imports, function signatures, or code blocks
  Usage: replaceLines({ filePath: 'file.ts', startLine: 5, endLine: 8, newContent: 'new multiline content' })
  
- **writeFile**: ONLY for creating new files that don't exist
  Example: creating missing index.ts barrel files

CRITICAL WRITEFILЕ SAFETY RULES:
- ONLY use writeFile for creating NEW files that don't exist
- ALWAYS check with readFile first to verify file doesn't exist
- NEVER use writeFile to overwrite existing files - use multiEdit or replaceLines instead
- Common valid uses: missing index.ts barrel files, missing type definitions, missing config files

CRITICAL IMPORT PATH RESOLUTION:
The following files were copied from template with new names:
${JSON.stringify(copiedFiles, null, 2)}

When fixing import errors:
1. Check if the missing module corresponds to a copied file
2. Use listDirectory to verify actual filenames in target directories
3. Update import paths to match the actual copied filenames
4. Ensure exported variable names match what's being imported

EXAMPLE: If error shows "Cannot find module './tools/download-csv-tool'" but a file was copied as "csv-fetcher-tool.ts", update the import to "./tools/csv-fetcher-tool"

${conflictsResolved ? `CONFLICTS RESOLVED BY INTELLIGENT MERGE:\n${JSON.stringify(conflictsResolved, null, 2)}\n` : ""}

INTEGRATED UNITS:
${JSON.stringify(orderedUnits, null, 2)}

Be thorough and methodical. Always use listDirectory to verify actual file existence before fixing imports.`,
				model,
				tools: {
					validateCode: allTools.validateCode,
					readFile: allTools.readFile,
					writeFile: allTools.writeFile,
					multiEdit: allTools.multiEdit,
					replaceLines: allTools.replaceLines,
					listDirectory: allTools.listDirectory,
					executeCommand: allTools.executeCommand
				}
			});
			console.info("Starting validation and fix agent with internal loop...");
			let validationResults = {
				valid: false,
				errorsFixed: 0,
				remainingErrors: 1,
				iteration: currentIteration,
				lastValidationErrors: []
			};
			while (validationResults.remainingErrors > 0 && currentIteration <= maxIterations) {
				console.info(`\n=== Validation Iteration ${currentIteration} ===`);
				const iterationPrompt = currentIteration === 1 ? `Please validate the template integration and fix any errors found in the project at ${targetPath}. The template "${slug}" (${commitSha.substring(0, 7)}) was just integrated and may have validation issues that need fixing.

Start by running validateCode with all validation types to get a complete picture of any issues, then systematically fix them.` : `Continue validation and fixing for the template integration at ${targetPath}. This is iteration ${currentIteration} of validation.

Previous iterations may have fixed some issues, so start by re-running validateCode to see the current state, then fix any remaining issues.`;
				const isSupported = isSupportedLanguageModel(await validationAgent.getModel());
				const output = z.object({ success: z.boolean() });
				const result = isSupported ? await tryStreamWithJsonFallback(validationAgent, iterationPrompt, { structuredOutput: { schema: output } }) : await validationAgent.streamLegacy(iterationPrompt, { experimental_output: output });
				let iterationErrors = 0;
				let previousErrors = validationResults.remainingErrors;
				let lastValidationResult = null;
				for await (const chunk of result.fullStream) {
					if (chunk.type === "step-finish" || chunk.type === "step-start") {
						const chunkData = "payload" in chunk ? chunk.payload : chunk;
						console.info({
							type: chunk.type,
							msgId: chunkData.messageId,
							iteration: currentIteration
						});
					} else console.info(JSON.stringify(chunk, null, 2));
					if (chunk.type === "tool-result") {
						const chunkData = "payload" in chunk ? chunk.payload : chunk;
						if (chunkData.toolName === "validateCode") {
							const toolResult = chunkData.result;
							lastValidationResult = toolResult;
							if (toolResult?.summary) {
								iterationErrors = toolResult.summary.totalErrors || 0;
								console.info(`Iteration ${currentIteration}: Found ${iterationErrors} errors`);
							}
						}
					}
				}
				validationResults.remainingErrors = iterationErrors;
				validationResults.errorsFixed += Math.max(0, previousErrors - iterationErrors);
				validationResults.valid = iterationErrors === 0;
				validationResults.iteration = currentIteration;
				if (iterationErrors > 0 && lastValidationResult?.errors) validationResults.lastValidationErrors = lastValidationResult.errors;
				console.info(`Iteration ${currentIteration} complete: ${iterationErrors} errors remaining`);
				if (iterationErrors === 0) {
					console.info(`✅ All validation issues resolved in ${currentIteration} iterations!`);
					break;
				} else if (currentIteration >= maxIterations) {
					console.info(`⚠️  Max iterations (${maxIterations}) reached. ${iterationErrors} errors still remaining.`);
					break;
				}
				currentIteration++;
			}
			try {
				await gitAddAndCommit(targetPath, `fix(template): resolve validation errors for ${slug}@${commitSha.substring(0, 7)}`, void 0, { skipIfNoStaged: true });
			} catch (commitError) {
				console.warn("Failed to commit validation fixes:", commitError);
			}
			return {
				success: validationResults.valid,
				applied: true,
				message: `Validation completed in ${currentIteration} iteration${currentIteration > 1 ? "s" : ""}. ${validationResults.valid ? "All issues resolved!" : `${validationResults.remainingErrors} issue${validationResults.remainingErrors > 1 ? "s" : ""} remaining`}`,
				validationResults: {
					valid: validationResults.valid,
					errorsFixed: validationResults.errorsFixed,
					remainingErrors: validationResults.remainingErrors,
					errors: validationResults.lastValidationErrors
				}
			};
		} catch (error) {
			console.error("Validation and fix failed:", error);
			return {
				success: false,
				applied: false,
				message: `Validation and fix failed: ${error instanceof Error ? error.message : String(error)}`,
				validationResults: {
					valid: false,
					errorsFixed: 0,
					remainingErrors: -1
				},
				error: error instanceof Error ? error.message : String(error)
			};
		} finally {
			try {
				await rm(templateDir, {
					recursive: true,
					force: true
				});
				console.info(`✓ Cleaned up template directory: ${templateDir}`);
			} catch (cleanupError) {
				console.warn("Failed to cleanup template directory:", cleanupError);
			}
		}
	}
});
const agentBuilderTemplateWorkflow = createWorkflow({
	id: "agent-builder-template",
	description: "Merges a Mastra template repository into the current project using intelligent AgentBuilder-powered merging",
	inputSchema: AgentBuilderInputSchema,
	outputSchema: ApplyResultSchema,
	steps: [
		cloneTemplateStep,
		analyzePackageStep,
		discoverUnitsStep,
		orderUnitsStep,
		packageMergeStep,
		installStep,
		programmaticFileCopyStep,
		intelligentMergeStep,
		validationAndFixStep
	]
}).then(cloneTemplateStep).map(async ({ getStepResult }) => {
	const cloneResult = getStepResult(cloneTemplateStep);
	if (shouldAbortWorkflow(cloneResult)) throw new Error(`Critical failure in clone step: ${cloneResult.error}`);
	return cloneResult;
}).parallel([analyzePackageStep, discoverUnitsStep]).map(async ({ getStepResult }) => {
	const analyzeResult = getStepResult(analyzePackageStep);
	const discoverResult = getStepResult(discoverUnitsStep);
	if (shouldAbortWorkflow(analyzeResult)) throw new Error(`Failure in analyze package step: ${analyzeResult.error || "Package analysis failed"}`);
	if (shouldAbortWorkflow(discoverResult)) throw new Error(`Failure in discover units step: ${discoverResult.error || "Unit discovery failed"}`);
	return discoverResult;
}).then(orderUnitsStep).map(async ({ getStepResult, getInitData }) => {
	const cloneResult = getStepResult(cloneTemplateStep);
	const initData = getInitData();
	return {
		commitSha: cloneResult.commitSha,
		slug: cloneResult.slug,
		targetPath: initData.targetPath
	};
}).then(prepareBranchStep).map(async ({ getStepResult, getInitData }) => {
	const cloneResult = getStepResult(cloneTemplateStep);
	const packageResult = getStepResult(analyzePackageStep);
	const initData = getInitData();
	return {
		commitSha: cloneResult.commitSha,
		slug: cloneResult.slug,
		targetPath: initData.targetPath,
		packageInfo: packageResult
	};
}).then(packageMergeStep).map(async ({ getInitData }) => {
	return { targetPath: getInitData().targetPath };
}).then(installStep).map(async ({ getStepResult, getInitData }) => {
	const cloneResult = getStepResult(cloneTemplateStep);
	const orderResult = getStepResult(orderUnitsStep);
	const installResult = getStepResult(installStep);
	const initData = getInitData();
	if (shouldAbortWorkflow(installResult)) throw new Error(`Failure in install step: ${installResult.error || "Install failed"}`);
	return {
		orderedUnits: orderResult.orderedUnits,
		templateDir: cloneResult.templateDir,
		commitSha: cloneResult.commitSha,
		slug: cloneResult.slug,
		targetPath: initData.targetPath,
		variables: initData.variables
	};
}).then(programmaticFileCopyStep).map(async ({ getStepResult, getInitData }) => {
	const copyResult = getStepResult(programmaticFileCopyStep);
	const cloneResult = getStepResult(cloneTemplateStep);
	const initData = getInitData();
	return {
		conflicts: copyResult.conflicts,
		copiedFiles: copyResult.copiedFiles,
		commitSha: cloneResult.commitSha,
		slug: cloneResult.slug,
		targetPath: initData.targetPath,
		templateDir: cloneResult.templateDir
	};
}).then(intelligentMergeStep).map(async ({ getStepResult, getInitData }) => {
	const cloneResult = getStepResult(cloneTemplateStep);
	const orderResult = getStepResult(orderUnitsStep);
	const copyResult = getStepResult(programmaticFileCopyStep);
	const mergeResult = getStepResult(intelligentMergeStep);
	const initData = getInitData();
	return {
		commitSha: cloneResult.commitSha,
		slug: cloneResult.slug,
		targetPath: initData.targetPath,
		templateDir: cloneResult.templateDir,
		orderedUnits: orderResult.orderedUnits,
		copiedFiles: copyResult.copiedFiles,
		conflictsResolved: mergeResult.conflictsResolved
	};
}).then(validationAndFixStep).map(async ({ getStepResult }) => {
	const cloneResult = getStepResult(cloneTemplateStep);
	const analyzeResult = getStepResult(analyzePackageStep);
	const discoverResult = getStepResult(discoverUnitsStep);
	const orderResult = getStepResult(orderUnitsStep);
	const prepareBranchResult = getStepResult(prepareBranchStep);
	const packageMergeResult = getStepResult(packageMergeStep);
	const installResult = getStepResult(installStep);
	const copyResult = getStepResult(programmaticFileCopyStep);
	const intelligentMergeResult = getStepResult(intelligentMergeStep);
	const validationResult = getStepResult(validationAndFixStep);
	const branchName = prepareBranchResult.branchName;
	const allErrors = [
		cloneResult.error,
		analyzeResult.error,
		discoverResult.error,
		orderResult.error,
		prepareBranchResult.error,
		packageMergeResult.error,
		installResult.error,
		copyResult.error,
		intelligentMergeResult.error,
		validationResult.error
	].filter(Boolean);
	const overallSuccess = cloneResult.success !== false && analyzeResult.success !== false && discoverResult.success !== false && orderResult.success !== false && prepareBranchResult.success !== false && packageMergeResult.success !== false && installResult.success !== false && copyResult.success !== false && intelligentMergeResult.success !== false && validationResult.success !== false;
	const messages = [];
	if (copyResult.copiedFiles?.length > 0) messages.push(`${copyResult.copiedFiles.length} files copied`);
	if (copyResult.conflicts?.length > 0) messages.push(`${copyResult.conflicts.length} conflicts skipped`);
	if (intelligentMergeResult.conflictsResolved?.length > 0) messages.push(`${intelligentMergeResult.conflictsResolved.length} conflicts resolved`);
	if (validationResult.validationResults?.errorsFixed > 0) messages.push(`${validationResult.validationResults.errorsFixed} validation errors fixed`);
	if (validationResult.validationResults?.remainingErrors > 0) messages.push(`${validationResult.validationResults.remainingErrors} validation issues remain`);
	const comprehensiveMessage = messages.length > 0 ? `Template merge completed: ${messages.join(", ")}` : validationResult.message || "Template merge completed";
	return {
		success: overallSuccess,
		applied: validationResult.applied || copyResult.copiedFiles?.length > 0 || false,
		message: comprehensiveMessage,
		validationResults: validationResult.validationResults,
		error: allErrors.length > 0 ? allErrors.join("; ") : void 0,
		errors: allErrors.length > 0 ? allErrors : void 0,
		branchName,
		stepResults: {
			cloneSuccess: cloneResult.success,
			analyzeSuccess: analyzeResult.success,
			discoverSuccess: discoverResult.success,
			orderSuccess: orderResult.success,
			prepareBranchSuccess: prepareBranchResult.success,
			packageMergeSuccess: packageMergeResult.success,
			installSuccess: installResult.success,
			copySuccess: copyResult.success,
			mergeSuccess: intelligentMergeResult.success,
			validationSuccess: validationResult.success,
			filesCopied: copyResult.copiedFiles?.length || 0,
			conflictsSkipped: copyResult.conflicts?.length || 0,
			conflictsResolved: intelligentMergeResult.conflictsResolved?.length || 0
		}
	};
}).commit();
async function mergeTemplateBySlug(slug, targetPath) {
	const template = await getMastraTemplate(slug);
	return await (await agentBuilderTemplateWorkflow.createRun()).start({ inputData: {
		repo: template.githubUrl,
		slug: template.slug,
		targetPath
	} });
}
const determineConflictStrategy = (_unit, _targetFile) => {
	return "skip";
};
const shouldAbortWorkflow = (stepResult) => {
	return stepResult?.success === false || stepResult?.error;
};
//#endregion
//#region ../_vendored/ai_v5/dist/dist-Qv-F35RT.js
var marker$1 = "vercel.ai.error";
var symbol$1$1 = Symbol.for(marker$1);
var _a$1$1;
var _b$1;
var AISDKError = class _AISDKError extends (_b$1 = Error, _a$1$1 = symbol$1$1, _b$1) {
	/**
	* Creates an AI SDK Error.
	*
	* @param {Object} params - The parameters for creating the error.
	* @param {string} params.name - The name of the error.
	* @param {string} params.message - The error message.
	* @param {unknown} [params.cause] - The underlying cause of the error.
	*/
	constructor({ name: name14, message, cause }) {
		super(message);
		this[_a$1$1] = true;
		this.name = name14;
		this.cause = cause;
	}
	/**
	* Checks if the given error is an AI SDK Error.
	* @param {unknown} error - The error to check.
	* @returns {boolean} True if the error is an AI SDK Error, false otherwise.
	*/
	static isInstance(error) {
		return _AISDKError.hasMarker(error, marker$1);
	}
	static hasMarker(error, marker15) {
		const markerSymbol = Symbol.for(marker15);
		return error != null && typeof error === "object" && markerSymbol in error && typeof error[markerSymbol] === "boolean" && error[markerSymbol] === true;
	}
};
var name$1$1 = "AI_APICallError";
var marker2$2 = `vercel.ai.error.${name$1$1}`;
var symbol2$2 = Symbol.for(marker2$2);
var _a2$2;
var _b2$1;
var APICallError = class extends (_b2$1 = AISDKError, _a2$2 = symbol2$2, _b2$1) {
	constructor({ message, url, requestBodyValues, statusCode, responseHeaders, responseBody, cause, isRetryable = statusCode != null && (statusCode === 408 || statusCode === 409 || statusCode === 429 || statusCode >= 500), data }) {
		super({
			name: name$1$1,
			message,
			cause
		});
		this[_a2$2] = true;
		this.url = url;
		this.requestBodyValues = requestBodyValues;
		this.statusCode = statusCode;
		this.responseHeaders = responseHeaders;
		this.responseBody = responseBody;
		this.isRetryable = isRetryable;
		this.data = data;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker2$2);
	}
};
var name2$2 = "AI_EmptyResponseBodyError";
var marker3$2 = `vercel.ai.error.${name2$2}`;
var symbol3$2 = Symbol.for(marker3$2);
var _a3$2;
var _b3$1;
var EmptyResponseBodyError = class extends (_b3$1 = AISDKError, _a3$2 = symbol3$2, _b3$1) {
	constructor({ message = "Empty response body" } = {}) {
		super({
			name: name2$2,
			message
		});
		this[_a3$2] = true;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker3$2);
	}
};
function getErrorMessage$1(error) {
	if (error == null) return "unknown error";
	if (typeof error === "string") return error;
	if (error instanceof Error) return error.message;
	return JSON.stringify(error);
}
var name3$2 = "AI_InvalidArgumentError";
var marker4$2 = `vercel.ai.error.${name3$2}`;
var symbol4$2 = Symbol.for(marker4$2);
var _a4$2;
var _b4$1;
var InvalidArgumentError = class extends (_b4$1 = AISDKError, _a4$2 = symbol4$2, _b4$1) {
	constructor({ message, cause, argument }) {
		super({
			name: name3$2,
			message,
			cause
		});
		this[_a4$2] = true;
		this.argument = argument;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker4$2);
	}
};
var name4$2 = "AI_InvalidPromptError";
var marker5$2 = `vercel.ai.error.${name4$2}`;
var symbol5$2 = Symbol.for(marker5$2);
var _a5$2;
var _b5$1;
(class extends (_b5$1 = AISDKError, _a5$2 = symbol5$2, _b5$1) {
	constructor({ prompt, message, cause }) {
		super({
			name: name4$2,
			message: `Invalid prompt: ${message}`,
			cause
		});
		this[_a5$2] = true;
		this.prompt = prompt;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker5$2);
	}
});
var name5$2 = "AI_InvalidResponseDataError";
var marker6$2 = `vercel.ai.error.${name5$2}`;
var symbol6$2 = Symbol.for(marker6$2);
var _a6$2;
var _b6$1;
(class extends (_b6$1 = AISDKError, _a6$2 = symbol6$2, _b6$1) {
	constructor({ data, message = `Invalid response data: ${JSON.stringify(data)}.` }) {
		super({
			name: name5$2,
			message
		});
		this[_a6$2] = true;
		this.data = data;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker6$2);
	}
});
var name6$2 = "AI_JSONParseError";
var marker7$2 = `vercel.ai.error.${name6$2}`;
var symbol7$2 = Symbol.for(marker7$2);
var _a7$2;
var _b7$1;
var JSONParseError = class extends (_b7$1 = AISDKError, _a7$2 = symbol7$2, _b7$1) {
	constructor({ text, cause }) {
		super({
			name: name6$2,
			message: `JSON parsing failed: Text: ${text}.
Error message: ${getErrorMessage$1(cause)}`,
			cause
		});
		this[_a7$2] = true;
		this.text = text;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker7$2);
	}
};
var name7$2 = "AI_LoadAPIKeyError";
var marker8$2 = `vercel.ai.error.${name7$2}`;
var symbol8$2 = Symbol.for(marker8$2);
var _a8$2;
var _b8$1;
(class extends (_b8$1 = AISDKError, _a8$2 = symbol8$2, _b8$1) {
	constructor({ message }) {
		super({
			name: name7$2,
			message
		});
		this[_a8$2] = true;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker8$2);
	}
});
var name8$2 = "AI_LoadSettingError";
var marker9$2 = `vercel.ai.error.${name8$2}`;
var symbol9$2 = Symbol.for(marker9$2);
var _a9$2;
var _b9$1;
(class extends (_b9$1 = AISDKError, _a9$2 = symbol9$2, _b9$1) {
	constructor({ message }) {
		super({
			name: name8$2,
			message
		});
		this[_a9$2] = true;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker9$2);
	}
});
var name9$1 = "AI_NoContentGeneratedError";
var marker10$1 = `vercel.ai.error.${name9$1}`;
var symbol10$1 = Symbol.for(marker10$1);
var _a10$1;
var _b10;
(class extends (_b10 = AISDKError, _a10$1 = symbol10$1, _b10) {
	constructor({ message = "No content generated." } = {}) {
		super({
			name: name9$1,
			message
		});
		this[_a10$1] = true;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker10$1);
	}
});
var name10$1 = "AI_NoSuchModelError";
var marker11$1 = `vercel.ai.error.${name10$1}`;
var symbol11$1 = Symbol.for(marker11$1);
var _a11$1;
var _b11;
(class extends (_b11 = AISDKError, _a11$1 = symbol11$1, _b11) {
	constructor({ errorName = name10$1, modelId, modelType, message = `No such ${modelType}: ${modelId}` }) {
		super({
			name: errorName,
			message
		});
		this[_a11$1] = true;
		this.modelId = modelId;
		this.modelType = modelType;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker11$1);
	}
});
var name11$1 = "AI_TooManyEmbeddingValuesForCallError";
var marker12$1 = `vercel.ai.error.${name11$1}`;
var symbol12$1 = Symbol.for(marker12$1);
var _a12$1;
var _b12;
(class extends (_b12 = AISDKError, _a12$1 = symbol12$1, _b12) {
	constructor(options) {
		super({
			name: name11$1,
			message: `Too many values for a single embedding call. The ${options.provider} model "${options.modelId}" can only embed up to ${options.maxEmbeddingsPerCall} values per call, but ${options.values.length} values were provided.`
		});
		this[_a12$1] = true;
		this.provider = options.provider;
		this.modelId = options.modelId;
		this.maxEmbeddingsPerCall = options.maxEmbeddingsPerCall;
		this.values = options.values;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker12$1);
	}
});
var name12$1 = "AI_TypeValidationError";
var marker13$1 = `vercel.ai.error.${name12$1}`;
var symbol13$1 = Symbol.for(marker13$1);
var _a13$1;
var _b13;
var TypeValidationError = class _TypeValidationError extends (_b13 = AISDKError, _a13$1 = symbol13$1, _b13) {
	constructor({ value, cause }) {
		super({
			name: name12$1,
			message: `Type validation failed: Value: ${JSON.stringify(value)}.
Error message: ${getErrorMessage$1(cause)}`,
			cause
		});
		this[_a13$1] = true;
		this.value = value;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker13$1);
	}
	/**
	* Wraps an error into a TypeValidationError.
	* If the cause is already a TypeValidationError with the same value, it returns the cause.
	* Otherwise, it creates a new TypeValidationError.
	*
	* @param {Object} params - The parameters for wrapping the error.
	* @param {unknown} params.value - The value that failed validation.
	* @param {unknown} params.cause - The original error or cause of the validation failure.
	* @returns {TypeValidationError} A TypeValidationError instance.
	*/
	static wrap({ value, cause }) {
		return _TypeValidationError.isInstance(cause) && cause.value === value ? cause : new _TypeValidationError({
			value,
			cause
		});
	}
};
var name13$1 = "AI_UnsupportedFunctionalityError";
var marker14$1 = `vercel.ai.error.${name13$1}`;
var symbol14$1 = Symbol.for(marker14$1);
var _a14$1;
var _b14;
(class extends (_b14 = AISDKError, _a14$1 = symbol14$1, _b14) {
	constructor({ functionality, message = `'${functionality}' functionality not supported.` }) {
		super({
			name: name13$1,
			message
		});
		this[_a14$1] = true;
		this.functionality = functionality;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker14$1);
	}
});
var ParseError = class extends Error {
	constructor(message, options) {
		super(message), this.name = "ParseError", this.type = options.type, this.field = options.field, this.value = options.value, this.line = options.line;
	}
};
const LF = 10;
const CR = 13;
const SPACE = 32;
function noop(_arg) {}
function createParser(callbacks) {
	if (typeof callbacks == "function") throw new TypeError("`callbacks` must be an object, got a function instead. Did you mean `{onEvent: fn}`?");
	const { onEvent = noop, onError = noop, onRetry = noop, onComment } = callbacks, pendingFragments = [];
	let isFirstChunk = !0, id, data = "", dataLines = 0, eventType;
	function feed(chunk) {
		if (isFirstChunk && (isFirstChunk = !1, chunk.charCodeAt(0) === 239 && chunk.charCodeAt(1) === 187 && chunk.charCodeAt(2) === 191 && (chunk = chunk.slice(3))), pendingFragments.length === 0) {
			const trailing2 = processLines(chunk);
			trailing2 !== "" && pendingFragments.push(trailing2);
			return;
		}
		if (chunk.indexOf(`
`) === -1 && chunk.indexOf("\r") === -1) {
			pendingFragments.push(chunk);
			return;
		}
		pendingFragments.push(chunk);
		const input = pendingFragments.join("");
		pendingFragments.length = 0;
		const trailing = processLines(input);
		trailing !== "" && pendingFragments.push(trailing);
	}
	function processLines(chunk) {
		let searchIndex = 0;
		if (chunk.indexOf("\r") === -1) {
			let lfIndex = chunk.indexOf(`
`, searchIndex);
			for (; lfIndex !== -1;) {
				if (searchIndex === lfIndex) {
					dataLines > 0 && onEvent({
						id,
						event: eventType,
						data
					}), id = void 0, data = "", dataLines = 0, eventType = void 0, searchIndex = lfIndex + 1, lfIndex = chunk.indexOf(`
`, searchIndex);
					continue;
				}
				const firstCharCode = chunk.charCodeAt(searchIndex);
				if (isDataPrefix(chunk, searchIndex, firstCharCode)) {
					const valueStart = chunk.charCodeAt(searchIndex + 5) === SPACE ? searchIndex + 6 : searchIndex + 5, value = chunk.slice(valueStart, lfIndex);
					if (dataLines === 0 && chunk.charCodeAt(lfIndex + 1) === LF) {
						onEvent({
							id,
							event: eventType,
							data: value
						}), id = void 0, data = "", eventType = void 0, searchIndex = lfIndex + 2, lfIndex = chunk.indexOf(`
`, searchIndex);
						continue;
					}
					data = dataLines === 0 ? value : `${data}
${value}`, dataLines++;
				} else isEventPrefix(chunk, searchIndex, firstCharCode) ? eventType = chunk.slice(chunk.charCodeAt(searchIndex + 6) === SPACE ? searchIndex + 7 : searchIndex + 6, lfIndex) || void 0 : parseLine(chunk, searchIndex, lfIndex);
				searchIndex = lfIndex + 1, lfIndex = chunk.indexOf(`
`, searchIndex);
			}
			return chunk.slice(searchIndex);
		}
		for (; searchIndex < chunk.length;) {
			const crIndex = chunk.indexOf("\r", searchIndex), lfIndex = chunk.indexOf(`
`, searchIndex);
			let lineEnd = -1;
			if (crIndex !== -1 && lfIndex !== -1 ? lineEnd = crIndex < lfIndex ? crIndex : lfIndex : crIndex !== -1 ? crIndex === chunk.length - 1 ? lineEnd = -1 : lineEnd = crIndex : lfIndex !== -1 && (lineEnd = lfIndex), lineEnd === -1) break;
			parseLine(chunk, searchIndex, lineEnd), searchIndex = lineEnd + 1, chunk.charCodeAt(searchIndex - 1) === CR && chunk.charCodeAt(searchIndex) === LF && searchIndex++;
		}
		return chunk.slice(searchIndex);
	}
	function parseLine(chunk, start, end) {
		if (start === end) {
			dispatchEvent();
			return;
		}
		const firstCharCode = chunk.charCodeAt(start);
		if (isDataPrefix(chunk, start, firstCharCode)) {
			const valueStart = chunk.charCodeAt(start + 5) === SPACE ? start + 6 : start + 5, value2 = chunk.slice(valueStart, end);
			data = dataLines === 0 ? value2 : `${data}
${value2}`, dataLines++;
			return;
		}
		if (isEventPrefix(chunk, start, firstCharCode)) {
			eventType = chunk.slice(chunk.charCodeAt(start + 6) === SPACE ? start + 7 : start + 6, end) || void 0;
			return;
		}
		if (firstCharCode === 105 && chunk.charCodeAt(start + 1) === 100 && chunk.charCodeAt(start + 2) === 58) {
			const value2 = chunk.slice(chunk.charCodeAt(start + 3) === SPACE ? start + 4 : start + 3, end);
			id = value2.includes("\0") ? void 0 : value2;
			return;
		}
		if (firstCharCode === 58) {
			if (onComment) {
				const line2 = chunk.slice(start, end);
				onComment(line2.slice(chunk.charCodeAt(start + 1) === SPACE ? 2 : 1));
			}
			return;
		}
		const line = chunk.slice(start, end), fieldSeparatorIndex = line.indexOf(":");
		if (fieldSeparatorIndex === -1) {
			processField(line, "", line);
			return;
		}
		const field = line.slice(0, fieldSeparatorIndex), offset = line.charCodeAt(fieldSeparatorIndex + 1) === SPACE ? 2 : 1;
		processField(field, line.slice(fieldSeparatorIndex + offset), line);
	}
	function processField(field, value, line) {
		switch (field) {
			case "event":
				eventType = value || void 0;
				break;
			case "data":
				data = dataLines === 0 ? value : `${data}
${value}`, dataLines++;
				break;
			case "id":
				id = value.includes("\0") ? void 0 : value;
				break;
			case "retry":
				/^\d+$/.test(value) ? onRetry(parseInt(value, 10)) : onError(new ParseError(`Invalid \`retry\` value: "${value}"`, {
					type: "invalid-retry",
					value,
					line
				}));
				break;
			default:
				onError(new ParseError(`Unknown field "${field.length > 20 ? `${field.slice(0, 20)}\u2026` : field}"`, {
					type: "unknown-field",
					field,
					value,
					line
				}));
				break;
		}
	}
	function dispatchEvent() {
		dataLines > 0 && onEvent({
			id,
			event: eventType,
			data
		}), id = void 0, data = "", dataLines = 0, eventType = void 0;
	}
	function reset(options = {}) {
		if (options.consume && pendingFragments.length > 0) {
			const incompleteLine = pendingFragments.join("");
			parseLine(incompleteLine, 0, incompleteLine.length);
		}
		isFirstChunk = !0, id = void 0, data = "", dataLines = 0, eventType = void 0, pendingFragments.length = 0;
	}
	return {
		feed,
		reset
	};
}
function isDataPrefix(chunk, i, firstCharCode) {
	return firstCharCode === 100 && chunk.charCodeAt(i + 1) === 97 && chunk.charCodeAt(i + 2) === 116 && chunk.charCodeAt(i + 3) === 97 && chunk.charCodeAt(i + 4) === 58;
}
function isEventPrefix(chunk, i, firstCharCode) {
	return firstCharCode === 101 && chunk.charCodeAt(i + 1) === 118 && chunk.charCodeAt(i + 2) === 101 && chunk.charCodeAt(i + 3) === 110 && chunk.charCodeAt(i + 4) === 116 && chunk.charCodeAt(i + 5) === 58;
}
var EventSourceParserStream = class extends TransformStream {
	constructor({ onError, onRetry, onComment } = {}) {
		let parser;
		super({
			start(controller) {
				parser = createParser({
					onEvent: (event) => {
						controller.enqueue(event);
					},
					onError(error) {
						onError === "terminate" ? controller.error(error) : typeof onError == "function" && onError(error);
					},
					onRetry,
					onComment
				});
			},
			transform(chunk) {
				parser.feed(chunk);
			}
		});
	}
};
function combineHeaders(...headers) {
	return headers.reduce((combinedHeaders, currentHeaders) => ({
		...combinedHeaders,
		...currentHeaders != null ? currentHeaders : {}
	}), {});
}
function extractResponseHeaders(response) {
	return Object.fromEntries([...response.headers]);
}
var name$2 = "AI_DownloadError";
var marker$2 = `vercel.ai.error.${name$2}`;
var symbol$2 = Symbol.for(marker$2);
var _a$2;
var _b$2;
var DownloadError = class extends (_b$2 = AISDKError, _a$2 = symbol$2, _b$2) {
	constructor({ url, statusCode, statusText, cause, message = cause == null ? `Failed to download ${url}: ${statusCode} ${statusText}` : `Failed to download ${url}: ${cause}` }) {
		super({
			name: name$2,
			message,
			cause
		});
		this[_a$2] = true;
		this.url = url;
		this.statusCode = statusCode;
		this.statusText = statusText;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker$2);
	}
};
async function cancelResponseBody(response) {
	var _a2;
	try {
		await ((_a2 = response.body) == null ? void 0 : _a2.cancel());
	} catch (e) {}
}
var DEFAULT_MAX_DOWNLOAD_SIZE = 2 * 1024 * 1024 * 1024;
async function readResponseWithSizeLimit({ response, url, maxBytes = DEFAULT_MAX_DOWNLOAD_SIZE }) {
	const contentLength = response.headers.get("content-length");
	if (contentLength != null) {
		const length = parseInt(contentLength, 10);
		if (!isNaN(length) && length > maxBytes) {
			await cancelResponseBody(response);
			throw new DownloadError({
				url,
				message: `Download of ${url} exceeded maximum size of ${maxBytes} bytes (Content-Length: ${length}).`
			});
		}
	}
	const body = response.body;
	if (body == null) return /* @__PURE__ */ new Uint8Array(0);
	const reader = body.getReader();
	const chunks = [];
	let totalBytes = 0;
	try {
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			totalBytes += value.length;
			if (totalBytes > maxBytes) throw new DownloadError({
				url,
				message: `Download of ${url} exceeded maximum size of ${maxBytes} bytes.`
			});
			chunks.push(value);
		}
	} finally {
		try {
			await reader.cancel();
		} finally {
			reader.releaseLock();
		}
	}
	const result = new Uint8Array(totalBytes);
	let offset = 0;
	for (const chunk of chunks) {
		result.set(chunk, offset);
		offset += chunk.length;
	}
	return result;
}
var createIdGenerator = ({ prefix, size = 16, alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz", separator = "-" } = {}) => {
	const generator = () => {
		const alphabetLength = alphabet.length;
		const chars = new Array(size);
		for (let i = 0; i < size; i++) chars[i] = alphabet[Math.random() * alphabetLength | 0];
		return chars.join("");
	};
	if (prefix == null) return generator;
	if (alphabet.includes(separator)) throw new InvalidArgumentError({
		argument: "separator",
		message: `The separator "${separator}" must not be part of the alphabet "${alphabet}".`
	});
	return () => `${prefix}${separator}${generator()}`;
};
createIdGenerator();
function isAbortError(error) {
	return (error instanceof Error || error instanceof DOMException) && (error.name === "AbortError" || error.name === "ResponseAborted" || error.name === "TimeoutError");
}
var FETCH_FAILED_ERROR_MESSAGES = ["fetch failed", "failed to fetch"];
function handleFetchError({ error, url, requestBodyValues }) {
	if (isAbortError(error)) return error;
	if (error instanceof TypeError && FETCH_FAILED_ERROR_MESSAGES.includes(error.message.toLowerCase())) {
		const cause = error.cause;
		if (cause != null) return new APICallError({
			message: `Cannot connect to API: ${cause.message}`,
			cause,
			url,
			requestBodyValues,
			isRetryable: true
		});
	}
	return error;
}
function getRuntimeEnvironmentUserAgent(globalThisAny = globalThis) {
	var _a2, _b2, _c;
	if (globalThisAny.window) return `runtime/browser`;
	if ((_a2 = globalThisAny.navigator) == null ? void 0 : _a2.userAgent) return `runtime/${globalThisAny.navigator.userAgent.toLowerCase()}`;
	if ((_c = (_b2 = globalThisAny.process) == null ? void 0 : _b2.versions) == null ? void 0 : _c.node) return `runtime/node.js/${globalThisAny.process.version.substring(0)}`;
	if (globalThisAny.EdgeRuntime) return `runtime/vercel-edge`;
	return "runtime/unknown";
}
function normalizeHeaders(headers) {
	if (headers == null) return {};
	const normalized = {};
	if (headers instanceof Headers) headers.forEach((value, key) => {
		normalized[key.toLowerCase()] = value;
	});
	else {
		if (!Array.isArray(headers)) headers = Object.entries(headers);
		for (const [key, value] of headers) if (value != null) normalized[key.toLowerCase()] = value;
	}
	return normalized;
}
function withUserAgentSuffix(headers, ...userAgentSuffixParts) {
	const normalizedHeaders = new Headers(normalizeHeaders(headers));
	const currentUserAgentHeader = normalizedHeaders.get("user-agent") || "";
	normalizedHeaders.set("user-agent", [currentUserAgentHeader, ...userAgentSuffixParts].filter(Boolean).join(" "));
	return Object.fromEntries(normalizedHeaders.entries());
}
var VERSION$3 = "3.0.30";
var getOriginalFetch = () => globalThis.fetch;
var getFromApi = async ({ url, headers = {}, successfulResponseHandler, failedResponseHandler, abortSignal, fetch: fetch2 = getOriginalFetch() }) => {
	try {
		const response = await fetch2(url, {
			method: "GET",
			headers: withUserAgentSuffix(headers, `ai-sdk/provider-utils/${VERSION$3}`, getRuntimeEnvironmentUserAgent()),
			signal: abortSignal
		});
		const responseHeaders = extractResponseHeaders(response);
		if (!response.ok) {
			let errorInformation;
			try {
				errorInformation = await failedResponseHandler({
					response,
					url,
					requestBodyValues: {}
				});
			} catch (error) {
				if (isAbortError(error) || APICallError.isInstance(error)) throw error;
				throw new APICallError({
					message: "Failed to process error response",
					cause: error,
					statusCode: response.status,
					url,
					responseHeaders,
					requestBodyValues: {}
				});
			}
			throw errorInformation.value;
		}
		try {
			return await successfulResponseHandler({
				response,
				url,
				requestBodyValues: {}
			});
		} catch (error) {
			if (error instanceof Error) {
				if (isAbortError(error) || APICallError.isInstance(error)) throw error;
			}
			throw new APICallError({
				message: "Failed to process successful response",
				cause: error,
				statusCode: response.status,
				url,
				responseHeaders,
				requestBodyValues: {}
			});
		}
	} catch (error) {
		throw handleFetchError({
			error,
			url,
			requestBodyValues: {}
		});
	}
};
function loadOptionalSetting({ settingValue, environmentVariableName }) {
	if (typeof settingValue === "string") return settingValue;
	if (settingValue != null || typeof process === "undefined") return;
	settingValue = process.env[environmentVariableName];
	if (settingValue == null || typeof settingValue !== "string") return;
	return settingValue;
}
var suspectProtoRx = /"(?:_|\\u005[Ff])(?:_|\\u005[Ff])(?:p|\\u0070)(?:r|\\u0072)(?:o|\\u006[Ff])(?:t|\\u0074)(?:o|\\u006[Ff])(?:_|\\u005[Ff])(?:_|\\u005[Ff])"\s*:/;
var suspectConstructorRx = /"(?:c|\\u0063)(?:o|\\u006[Ff])(?:n|\\u006[Ee])(?:s|\\u0073)(?:t|\\u0074)(?:r|\\u0072)(?:u|\\u0075)(?:c|\\u0063)(?:t|\\u0074)(?:o|\\u006[Ff])(?:r|\\u0072)"\s*:/;
function _parse(text) {
	const obj = JSON.parse(text);
	if (obj === null || typeof obj !== "object") return obj;
	if (suspectProtoRx.test(text) === false && suspectConstructorRx.test(text) === false) return obj;
	return filter(obj);
}
function filter(obj) {
	let next = [obj];
	while (next.length) {
		const nodes = next;
		next = [];
		for (const node of nodes) {
			if (Object.prototype.hasOwnProperty.call(node, "__proto__")) throw new SyntaxError("Object contains forbidden prototype property");
			if (Object.prototype.hasOwnProperty.call(node, "constructor") && node.constructor !== null && typeof node.constructor === "object" && Object.prototype.hasOwnProperty.call(node.constructor, "prototype")) throw new SyntaxError("Object contains forbidden prototype property");
			for (const key in node) {
				const value = node[key];
				if (value && typeof value === "object") next.push(value);
			}
		}
	}
	return obj;
}
function secureJsonParse(text) {
	const { stackTraceLimit } = Error;
	try {
		Error.stackTraceLimit = 0;
	} catch (e) {
		return _parse(text);
	}
	try {
		return _parse(text);
	} finally {
		Error.stackTraceLimit = stackTraceLimit;
	}
}
var validatorSymbol = /* @__PURE__ */ Symbol.for("vercel.ai.validator");
function validator(validate) {
	return {
		[validatorSymbol]: true,
		validate
	};
}
function isValidator(value) {
	return typeof value === "object" && value !== null && validatorSymbol in value && value[validatorSymbol] === true && "validate" in value;
}
function lazyValidator(createValidator) {
	let validator2;
	return () => {
		if (validator2 == null) validator2 = createValidator();
		return validator2;
	};
}
function asValidator(value) {
	return isValidator(value) ? value : "~standard" in value ? standardSchemaValidator(value) : value();
}
function standardSchemaValidator(standardSchema) {
	return validator(async (value) => {
		const result = await standardSchema["~standard"].validate(value);
		return result.issues == null ? {
			success: true,
			value: result.value
		} : {
			success: false,
			error: new TypeValidationError({
				value,
				cause: result.issues
			})
		};
	});
}
async function validateTypes({ value, schema }) {
	const result = await safeValidateTypes({
		value,
		schema
	});
	if (!result.success) throw TypeValidationError.wrap({
		value,
		cause: result.error
	});
	return result.value;
}
async function safeValidateTypes({ value, schema }) {
	const validator2 = asValidator(schema);
	try {
		if (validator2.validate == null) return {
			success: true,
			value,
			rawValue: value
		};
		const result = await validator2.validate(value);
		if (result.success) return {
			success: true,
			value: result.value,
			rawValue: value
		};
		return {
			success: false,
			error: TypeValidationError.wrap({
				value,
				cause: result.error
			}),
			rawValue: value
		};
	} catch (error) {
		return {
			success: false,
			error: TypeValidationError.wrap({
				value,
				cause: error
			}),
			rawValue: value
		};
	}
}
async function parseJSON({ text, schema }) {
	try {
		const value = secureJsonParse(text);
		if (schema == null) return value;
		return validateTypes({
			value,
			schema
		});
	} catch (error) {
		if (JSONParseError.isInstance(error) || TypeValidationError.isInstance(error)) throw error;
		throw new JSONParseError({
			text,
			cause: error
		});
	}
}
async function safeParseJSON({ text, schema }) {
	try {
		const value = secureJsonParse(text);
		if (schema == null) return {
			success: true,
			value,
			rawValue: value
		};
		return await safeValidateTypes({
			value,
			schema
		});
	} catch (error) {
		return {
			success: false,
			error: JSONParseError.isInstance(error) ? error : new JSONParseError({
				text,
				cause: error
			}),
			rawValue: void 0
		};
	}
}
function parseJsonEventStream({ stream, schema }) {
	return stream.pipeThrough(new TextDecoderStream()).pipeThrough(new EventSourceParserStream()).pipeThrough(new TransformStream({ async transform({ data }, controller) {
		if (data === "[DONE]") return;
		controller.enqueue(await safeParseJSON({
			text: data,
			schema
		}));
	} }));
}
var getOriginalFetch2 = () => globalThis.fetch;
var postJsonToApi = async ({ url, headers, body, failedResponseHandler, successfulResponseHandler, abortSignal, fetch: fetch2 }) => postToApi({
	url,
	headers: {
		"Content-Type": "application/json",
		...headers
	},
	body: {
		content: JSON.stringify(body),
		values: body
	},
	failedResponseHandler,
	successfulResponseHandler,
	abortSignal,
	fetch: fetch2
});
var postToApi = async ({ url, headers = {}, body, successfulResponseHandler, failedResponseHandler, abortSignal, fetch: fetch2 = getOriginalFetch2() }) => {
	try {
		const response = await fetch2(url, {
			method: "POST",
			headers: withUserAgentSuffix(headers, `ai-sdk/provider-utils/${VERSION$3}`, getRuntimeEnvironmentUserAgent()),
			body: body.content,
			signal: abortSignal
		});
		const responseHeaders = extractResponseHeaders(response);
		if (!response.ok) {
			let errorInformation;
			try {
				errorInformation = await failedResponseHandler({
					response,
					url,
					requestBodyValues: body.values
				});
			} catch (error) {
				if (isAbortError(error) || APICallError.isInstance(error)) throw error;
				throw new APICallError({
					message: "Failed to process error response",
					cause: error,
					statusCode: response.status,
					url,
					responseHeaders,
					requestBodyValues: body.values
				});
			}
			throw errorInformation.value;
		}
		try {
			return await successfulResponseHandler({
				response,
				url,
				requestBodyValues: body.values
			});
		} catch (error) {
			if (error instanceof Error) {
				if (isAbortError(error) || APICallError.isInstance(error)) throw error;
			}
			throw new APICallError({
				message: "Failed to process successful response",
				cause: error,
				statusCode: response.status,
				url,
				responseHeaders,
				requestBodyValues: body.values
			});
		}
	} catch (error) {
		throw handleFetchError({
			error,
			url,
			requestBodyValues: body.values
		});
	}
};
function tool(tool2) {
	return tool2;
}
function createProviderDefinedToolFactoryWithOutputSchema({ id, name: name2, inputSchema, outputSchema }) {
	return ({ execute, toModelOutput, onInputStart, onInputDelta, onInputAvailable, ...args }) => tool({
		type: "provider-defined",
		id,
		name: name2,
		args,
		inputSchema,
		outputSchema,
		execute,
		toModelOutput,
		onInputStart,
		onInputDelta,
		onInputAvailable
	});
}
async function resolve$1(value) {
	if (typeof value === "function") value = value();
	return Promise.resolve(value);
}
var textDecoder = new TextDecoder();
async function readResponseBodyAsText({ response, url }) {
	return textDecoder.decode(await readResponseWithSizeLimit({
		response,
		url
	}));
}
var createJsonErrorResponseHandler = ({ errorSchema, errorToMessage, isRetryable }) => async ({ response, url, requestBodyValues }) => {
	const responseBody = await readResponseBodyAsText({
		response,
		url
	});
	const responseHeaders = extractResponseHeaders(response);
	if (responseBody.trim() === "") return {
		responseHeaders,
		value: new APICallError({
			message: response.statusText,
			url,
			requestBodyValues,
			statusCode: response.status,
			responseHeaders,
			responseBody,
			isRetryable: isRetryable == null ? void 0 : isRetryable(response)
		})
	};
	try {
		const parsedError = await parseJSON({
			text: responseBody,
			schema: errorSchema
		});
		return {
			responseHeaders,
			value: new APICallError({
				message: errorToMessage(parsedError),
				url,
				requestBodyValues,
				statusCode: response.status,
				responseHeaders,
				responseBody,
				data: parsedError,
				isRetryable: isRetryable == null ? void 0 : isRetryable(response, parsedError)
			})
		};
	} catch (parseError) {
		return {
			responseHeaders,
			value: new APICallError({
				message: response.statusText,
				url,
				requestBodyValues,
				statusCode: response.status,
				responseHeaders,
				responseBody,
				isRetryable: isRetryable == null ? void 0 : isRetryable(response)
			})
		};
	}
};
var createEventSourceResponseHandler = (chunkSchema) => async ({ response }) => {
	const responseHeaders = extractResponseHeaders(response);
	if (response.body == null) throw new EmptyResponseBodyError({});
	return {
		responseHeaders,
		value: parseJsonEventStream({
			stream: response.body,
			schema: chunkSchema
		})
	};
};
var createJsonResponseHandler = (responseSchema) => async ({ response, url, requestBodyValues }) => {
	const responseBody = await readResponseBodyAsText({
		response,
		url
	});
	const parsedResult = await safeParseJSON({
		text: responseBody,
		schema: responseSchema
	});
	const responseHeaders = extractResponseHeaders(response);
	if (!parsedResult.success) throw new APICallError({
		message: "Invalid JSON response",
		cause: parsedResult.error,
		statusCode: response.status,
		responseHeaders,
		responseBody,
		url,
		requestBodyValues
	});
	return {
		responseHeaders,
		value: parsedResult.value,
		rawValue: parsedResult.rawValue
	};
};
var schemaSymbol = /* @__PURE__ */ Symbol.for("vercel.ai.schema");
function lazySchema(createSchema) {
	let schema;
	return () => {
		if (schema == null) schema = createSchema();
		return schema;
	};
}
function jsonSchema(jsonSchema2, { validate } = {}) {
	return {
		[schemaSymbol]: true,
		_type: void 0,
		[validatorSymbol]: true,
		get jsonSchema() {
			if (typeof jsonSchema2 === "function") jsonSchema2 = jsonSchema2();
			return jsonSchema2;
		},
		validate
	};
}
function addAdditionalPropertiesToJsonSchema(jsonSchema2) {
	if (jsonSchema2.type === "object") {
		jsonSchema2.additionalProperties = false;
		const properties = jsonSchema2.properties;
		if (properties != null) for (const property in properties) properties[property] = addAdditionalPropertiesToJsonSchema(properties[property]);
	}
	if (jsonSchema2.type === "array" && jsonSchema2.items != null) if (Array.isArray(jsonSchema2.items)) jsonSchema2.items = jsonSchema2.items.map((item) => addAdditionalPropertiesToJsonSchema(item));
	else jsonSchema2.items = addAdditionalPropertiesToJsonSchema(jsonSchema2.items);
	return jsonSchema2;
}
var ignoreOverride = /* @__PURE__ */ Symbol("Let zodToJsonSchema decide on which parser to use");
var defaultOptions = {
	name: void 0,
	$refStrategy: "root",
	basePath: ["#"],
	effectStrategy: "input",
	pipeStrategy: "all",
	dateStrategy: "format:date-time",
	mapStrategy: "entries",
	removeAdditionalStrategy: "passthrough",
	allowedAdditionalProperties: true,
	rejectedAdditionalProperties: false,
	definitionPath: "definitions",
	strictUnions: false,
	definitions: {},
	errorMessages: false,
	patternStrategy: "escape",
	applyRegexFlags: false,
	emailStrategy: "format:email",
	base64Strategy: "contentEncoding:base64",
	nameStrategy: "ref"
};
var getDefaultOptions = (options) => typeof options === "string" ? {
	...defaultOptions,
	name: options
} : {
	...defaultOptions,
	...options
};
function parseAnyDef() {
	return {};
}
function parseArrayDef(def, refs) {
	var _a2, _b2, _c;
	const res = { type: "array" };
	if (((_a2 = def.type) == null ? void 0 : _a2._def) && ((_c = (_b2 = def.type) == null ? void 0 : _b2._def) == null ? void 0 : _c.typeName) !== ZodFirstPartyTypeKind.ZodAny) res.items = parseDef(def.type._def, {
		...refs,
		currentPath: [...refs.currentPath, "items"]
	});
	if (def.minLength) res.minItems = def.minLength.value;
	if (def.maxLength) res.maxItems = def.maxLength.value;
	if (def.exactLength) {
		res.minItems = def.exactLength.value;
		res.maxItems = def.exactLength.value;
	}
	return res;
}
function parseBigintDef(def) {
	const res = {
		type: "integer",
		format: "int64"
	};
	if (!def.checks) return res;
	for (const check of def.checks) switch (check.kind) {
		case "min":
			if (check.inclusive) res.minimum = check.value;
			else res.exclusiveMinimum = check.value;
			break;
		case "max":
			if (check.inclusive) res.maximum = check.value;
			else res.exclusiveMaximum = check.value;
			break;
		case "multipleOf":
			res.multipleOf = check.value;
			break;
	}
	return res;
}
function parseBooleanDef() {
	return { type: "boolean" };
}
function parseBrandedDef(_def, refs) {
	return parseDef(_def.type._def, refs);
}
var parseCatchDef = (def, refs) => {
	return parseDef(def.innerType._def, refs);
};
function parseDateDef(def, refs, overrideDateStrategy) {
	const strategy = overrideDateStrategy != null ? overrideDateStrategy : refs.dateStrategy;
	if (Array.isArray(strategy)) return { anyOf: strategy.map((item, i) => parseDateDef(def, refs, item)) };
	switch (strategy) {
		case "string":
		case "format:date-time": return {
			type: "string",
			format: "date-time"
		};
		case "format:date": return {
			type: "string",
			format: "date"
		};
		case "integer": return integerDateParser(def);
	}
}
var integerDateParser = (def) => {
	const res = {
		type: "integer",
		format: "unix-time"
	};
	for (const check of def.checks) switch (check.kind) {
		case "min":
			res.minimum = check.value;
			break;
		case "max":
			res.maximum = check.value;
			break;
	}
	return res;
};
function parseDefaultDef(_def, refs) {
	return {
		...parseDef(_def.innerType._def, refs),
		default: _def.defaultValue()
	};
}
function parseEffectsDef(_def, refs) {
	return refs.effectStrategy === "input" ? parseDef(_def.schema._def, refs) : parseAnyDef();
}
function parseEnumDef(def) {
	return {
		type: "string",
		enum: Array.from(def.values)
	};
}
var isJsonSchema7AllOfType = (type) => {
	if ("type" in type && type.type === "string") return false;
	return "allOf" in type;
};
function parseIntersectionDef(def, refs) {
	const allOf = [parseDef(def.left._def, {
		...refs,
		currentPath: [
			...refs.currentPath,
			"allOf",
			"0"
		]
	}), parseDef(def.right._def, {
		...refs,
		currentPath: [
			...refs.currentPath,
			"allOf",
			"1"
		]
	})].filter((x) => !!x);
	const mergedAllOf = [];
	allOf.forEach((schema) => {
		if (isJsonSchema7AllOfType(schema)) mergedAllOf.push(...schema.allOf);
		else {
			let nestedSchema = schema;
			if ("additionalProperties" in schema && schema.additionalProperties === false) {
				const { additionalProperties, ...rest } = schema;
				nestedSchema = rest;
			}
			mergedAllOf.push(nestedSchema);
		}
	});
	return mergedAllOf.length ? { allOf: mergedAllOf } : void 0;
}
function parseLiteralDef(def) {
	const parsedType = typeof def.value;
	if (parsedType !== "bigint" && parsedType !== "number" && parsedType !== "boolean" && parsedType !== "string") return { type: Array.isArray(def.value) ? "array" : "object" };
	return {
		type: parsedType === "bigint" ? "integer" : parsedType,
		const: def.value
	};
}
var emojiRegex = void 0;
var zodPatterns = {
	/**
	* `c` was changed to `[cC]` to replicate /i flag
	*/
	cuid: /^[cC][^\s-]{8,}$/,
	cuid2: /^[0-9a-z]+$/,
	ulid: /^[0-9A-HJKMNP-TV-Z]{26}$/,
	/**
	* `a-z` was added to replicate /i flag
	*/
	email: /^(?!\.)(?!.*\.\.)([a-zA-Z0-9_'+\-\.]*)[a-zA-Z0-9_+-]@([a-zA-Z0-9][a-zA-Z0-9\-]*\.)+[a-zA-Z]{2,}$/,
	/**
	* Constructed a valid Unicode RegExp
	*
	* Lazily instantiate since this type of regex isn't supported
	* in all envs (e.g. React Native).
	*
	* See:
	* https://github.com/colinhacks/zod/issues/2433
	* Fix in Zod:
	* https://github.com/colinhacks/zod/commit/9340fd51e48576a75adc919bff65dbc4a5d4c99b
	*/
	emoji: () => {
		if (emojiRegex === void 0) emojiRegex = RegExp("^(\\p{Extended_Pictographic}|\\p{Emoji_Component})+$", "u");
		return emojiRegex;
	},
	/**
	* Unused
	*/
	uuid: /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/,
	/**
	* Unused
	*/
	ipv4: /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])$/,
	ipv4Cidr: /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\/(3[0-2]|[12]?[0-9])$/,
	/**
	* Unused
	*/
	ipv6: /^(([a-f0-9]{1,4}:){7}|::([a-f0-9]{1,4}:){0,6}|([a-f0-9]{1,4}:){1}:([a-f0-9]{1,4}:){0,5}|([a-f0-9]{1,4}:){2}:([a-f0-9]{1,4}:){0,4}|([a-f0-9]{1,4}:){3}:([a-f0-9]{1,4}:){0,3}|([a-f0-9]{1,4}:){4}:([a-f0-9]{1,4}:){0,2}|([a-f0-9]{1,4}:){5}:([a-f0-9]{1,4}:){0,1})([a-f0-9]{1,4}|(((25[0-5])|(2[0-4][0-9])|(1[0-9]{2})|([0-9]{1,2}))\.){3}((25[0-5])|(2[0-4][0-9])|(1[0-9]{2})|([0-9]{1,2})))$/,
	ipv6Cidr: /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))\/(12[0-8]|1[01][0-9]|[1-9]?[0-9])$/,
	base64: /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/,
	base64url: /^([0-9a-zA-Z-_]{4})*(([0-9a-zA-Z-_]{2}(==)?)|([0-9a-zA-Z-_]{3}(=)?))?$/,
	nanoid: /^[a-zA-Z0-9_-]{21}$/,
	jwt: /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/
};
function parseStringDef(def, refs) {
	const res = { type: "string" };
	if (def.checks) for (const check of def.checks) switch (check.kind) {
		case "min":
			res.minLength = typeof res.minLength === "number" ? Math.max(res.minLength, check.value) : check.value;
			break;
		case "max":
			res.maxLength = typeof res.maxLength === "number" ? Math.min(res.maxLength, check.value) : check.value;
			break;
		case "email":
			switch (refs.emailStrategy) {
				case "format:email":
					addFormat(res, "email", check.message, refs);
					break;
				case "format:idn-email":
					addFormat(res, "idn-email", check.message, refs);
					break;
				case "pattern:zod":
					addPattern(res, zodPatterns.email, check.message, refs);
					break;
			}
			break;
		case "url":
			addFormat(res, "uri", check.message, refs);
			break;
		case "uuid":
			addFormat(res, "uuid", check.message, refs);
			break;
		case "regex":
			addPattern(res, check.regex, check.message, refs);
			break;
		case "cuid":
			addPattern(res, zodPatterns.cuid, check.message, refs);
			break;
		case "cuid2":
			addPattern(res, zodPatterns.cuid2, check.message, refs);
			break;
		case "startsWith":
			addPattern(res, RegExp(`^${escapeLiteralCheckValue(check.value, refs)}`), check.message, refs);
			break;
		case "endsWith":
			addPattern(res, RegExp(`${escapeLiteralCheckValue(check.value, refs)}$`), check.message, refs);
			break;
		case "datetime":
			addFormat(res, "date-time", check.message, refs);
			break;
		case "date":
			addFormat(res, "date", check.message, refs);
			break;
		case "time":
			addFormat(res, "time", check.message, refs);
			break;
		case "duration":
			addFormat(res, "duration", check.message, refs);
			break;
		case "length":
			res.minLength = typeof res.minLength === "number" ? Math.max(res.minLength, check.value) : check.value;
			res.maxLength = typeof res.maxLength === "number" ? Math.min(res.maxLength, check.value) : check.value;
			break;
		case "includes":
			addPattern(res, RegExp(escapeLiteralCheckValue(check.value, refs)), check.message, refs);
			break;
		case "ip":
			if (check.version !== "v6") addFormat(res, "ipv4", check.message, refs);
			if (check.version !== "v4") addFormat(res, "ipv6", check.message, refs);
			break;
		case "base64url":
			addPattern(res, zodPatterns.base64url, check.message, refs);
			break;
		case "jwt":
			addPattern(res, zodPatterns.jwt, check.message, refs);
			break;
		case "cidr":
			if (check.version !== "v6") addPattern(res, zodPatterns.ipv4Cidr, check.message, refs);
			if (check.version !== "v4") addPattern(res, zodPatterns.ipv6Cidr, check.message, refs);
			break;
		case "emoji":
			addPattern(res, zodPatterns.emoji(), check.message, refs);
			break;
		case "ulid":
			addPattern(res, zodPatterns.ulid, check.message, refs);
			break;
		case "base64":
			switch (refs.base64Strategy) {
				case "format:binary":
					addFormat(res, "binary", check.message, refs);
					break;
				case "contentEncoding:base64":
					res.contentEncoding = "base64";
					break;
				case "pattern:zod":
					addPattern(res, zodPatterns.base64, check.message, refs);
					break;
			}
			break;
		case "nanoid": addPattern(res, zodPatterns.nanoid, check.message, refs);
		case "toLowerCase":
		case "toUpperCase":
		case "trim": break;
		default:
	}
	return res;
}
function escapeLiteralCheckValue(literal, refs) {
	return refs.patternStrategy === "escape" ? escapeNonAlphaNumeric(literal) : literal;
}
var ALPHA_NUMERIC = /* @__PURE__ */ new Set("ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvxyz0123456789");
function escapeNonAlphaNumeric(source) {
	let result = "";
	for (let i = 0; i < source.length; i++) {
		if (!ALPHA_NUMERIC.has(source[i])) result += "\\";
		result += source[i];
	}
	return result;
}
function addFormat(schema, value, message, refs) {
	var _a2;
	if (schema.format || ((_a2 = schema.anyOf) == null ? void 0 : _a2.some((x) => x.format))) {
		if (!schema.anyOf) schema.anyOf = [];
		if (schema.format) {
			schema.anyOf.push({ format: schema.format });
			delete schema.format;
		}
		schema.anyOf.push({
			format: value,
			...message && refs.errorMessages && { errorMessage: { format: message } }
		});
	} else schema.format = value;
}
function addPattern(schema, regex, message, refs) {
	var _a2;
	if (schema.pattern || ((_a2 = schema.allOf) == null ? void 0 : _a2.some((x) => x.pattern))) {
		if (!schema.allOf) schema.allOf = [];
		if (schema.pattern) {
			schema.allOf.push({ pattern: schema.pattern });
			delete schema.pattern;
		}
		schema.allOf.push({
			pattern: stringifyRegExpWithFlags(regex, refs),
			...message && refs.errorMessages && { errorMessage: { pattern: message } }
		});
	} else schema.pattern = stringifyRegExpWithFlags(regex, refs);
}
function stringifyRegExpWithFlags(regex, refs) {
	var _a2;
	if (!refs.applyRegexFlags || !regex.flags) return regex.source;
	const flags = {
		i: regex.flags.includes("i"),
		m: regex.flags.includes("m"),
		s: regex.flags.includes("s")
	};
	const source = flags.i ? regex.source.toLowerCase() : regex.source;
	let pattern = "";
	let isEscaped = false;
	let inCharGroup = false;
	let inCharRange = false;
	for (let i = 0; i < source.length; i++) {
		if (isEscaped) {
			pattern += source[i];
			isEscaped = false;
			continue;
		}
		if (flags.i) {
			if (inCharGroup) {
				if (source[i].match(/[a-z]/)) {
					if (inCharRange) {
						pattern += source[i];
						pattern += `${source[i - 2]}-${source[i]}`.toUpperCase();
						inCharRange = false;
					} else if (source[i + 1] === "-" && ((_a2 = source[i + 2]) == null ? void 0 : _a2.match(/[a-z]/))) {
						pattern += source[i];
						inCharRange = true;
					} else pattern += `${source[i]}${source[i].toUpperCase()}`;
					continue;
				}
			} else if (source[i].match(/[a-z]/)) {
				pattern += `[${source[i]}${source[i].toUpperCase()}]`;
				continue;
			}
		}
		if (flags.m) {
			if (source[i] === "^") {
				pattern += `(^|(?<=[\r
]))`;
				continue;
			} else if (source[i] === "$") {
				pattern += `($|(?=[\r
]))`;
				continue;
			}
		}
		if (flags.s && source[i] === ".") {
			pattern += inCharGroup ? `${source[i]}\r
` : `[${source[i]}\r
]`;
			continue;
		}
		pattern += source[i];
		if (source[i] === "\\") isEscaped = true;
		else if (inCharGroup && source[i] === "]") inCharGroup = false;
		else if (!inCharGroup && source[i] === "[") inCharGroup = true;
	}
	try {
		new RegExp(pattern);
	} catch (e) {
		console.warn(`Could not convert regex pattern at ${refs.currentPath.join("/")} to a flag-independent form! Falling back to the flag-ignorant source`);
		return regex.source;
	}
	return pattern;
}
function parseRecordDef(def, refs) {
	var _a2, _b2, _c, _d, _e, _f;
	const schema = {
		type: "object",
		additionalProperties: (_a2 = parseDef(def.valueType._def, {
			...refs,
			currentPath: [...refs.currentPath, "additionalProperties"]
		})) != null ? _a2 : refs.allowedAdditionalProperties
	};
	if (((_b2 = def.keyType) == null ? void 0 : _b2._def.typeName) === ZodFirstPartyTypeKind.ZodString && ((_c = def.keyType._def.checks) == null ? void 0 : _c.length)) {
		const { type, ...keyType } = parseStringDef(def.keyType._def, refs);
		return {
			...schema,
			propertyNames: keyType
		};
	} else if (((_d = def.keyType) == null ? void 0 : _d._def.typeName) === ZodFirstPartyTypeKind.ZodEnum) return {
		...schema,
		propertyNames: { enum: def.keyType._def.values }
	};
	else if (((_e = def.keyType) == null ? void 0 : _e._def.typeName) === ZodFirstPartyTypeKind.ZodBranded && def.keyType._def.type._def.typeName === ZodFirstPartyTypeKind.ZodString && ((_f = def.keyType._def.type._def.checks) == null ? void 0 : _f.length)) {
		const { type, ...keyType } = parseBrandedDef(def.keyType._def, refs);
		return {
			...schema,
			propertyNames: keyType
		};
	}
	return schema;
}
function parseMapDef(def, refs) {
	if (refs.mapStrategy === "record") return parseRecordDef(def, refs);
	return {
		type: "array",
		maxItems: 125,
		items: {
			type: "array",
			items: [parseDef(def.keyType._def, {
				...refs,
				currentPath: [
					...refs.currentPath,
					"items",
					"items",
					"0"
				]
			}) || parseAnyDef(), parseDef(def.valueType._def, {
				...refs,
				currentPath: [
					...refs.currentPath,
					"items",
					"items",
					"1"
				]
			}) || parseAnyDef()],
			minItems: 2,
			maxItems: 2
		}
	};
}
function parseNativeEnumDef(def) {
	const object = def.values;
	const actualValues = Object.keys(def.values).filter((key) => {
		return typeof object[object[key]] !== "number";
	}).map((key) => object[key]);
	const parsedTypes = Array.from(new Set(actualValues.map((values) => typeof values)));
	return {
		type: parsedTypes.length === 1 ? parsedTypes[0] === "string" ? "string" : "number" : ["string", "number"],
		enum: actualValues
	};
}
function parseNeverDef() {
	return { not: parseAnyDef() };
}
function parseNullDef() {
	return { type: "null" };
}
var primitiveMappings = {
	ZodString: "string",
	ZodNumber: "number",
	ZodBigInt: "integer",
	ZodBoolean: "boolean",
	ZodNull: "null"
};
function parseUnionDef(def, refs) {
	const options = def.options instanceof Map ? Array.from(def.options.values()) : def.options;
	if (options.every((x) => x._def.typeName in primitiveMappings && (!x._def.checks || !x._def.checks.length))) {
		const types = options.reduce((types2, x) => {
			const type = primitiveMappings[x._def.typeName];
			return type && !types2.includes(type) ? [...types2, type] : types2;
		}, []);
		return { type: types.length > 1 ? types : types[0] };
	} else if (options.every((x) => x._def.typeName === "ZodLiteral" && !x.description)) {
		const types = options.reduce((acc, x) => {
			const type = typeof x._def.value;
			switch (type) {
				case "string":
				case "number":
				case "boolean": return [...acc, type];
				case "bigint": return [...acc, "integer"];
				case "object": if (x._def.value === null) return [...acc, "null"];
				default: return acc;
			}
		}, []);
		if (types.length === options.length) {
			const uniqueTypes = types.filter((x, i, a) => a.indexOf(x) === i);
			return {
				type: uniqueTypes.length > 1 ? uniqueTypes : uniqueTypes[0],
				enum: options.reduce((acc, x) => {
					return acc.includes(x._def.value) ? acc : [...acc, x._def.value];
				}, [])
			};
		}
	} else if (options.every((x) => x._def.typeName === "ZodEnum")) return {
		type: "string",
		enum: options.reduce((acc, x) => [...acc, ...x._def.values.filter((x2) => !acc.includes(x2))], [])
	};
	return asAnyOf(def, refs);
}
var asAnyOf = (def, refs) => {
	const anyOf = (def.options instanceof Map ? Array.from(def.options.values()) : def.options).map((x, i) => parseDef(x._def, {
		...refs,
		currentPath: [
			...refs.currentPath,
			"anyOf",
			`${i}`
		]
	})).filter((x) => !!x && (!refs.strictUnions || typeof x === "object" && Object.keys(x).length > 0));
	return anyOf.length ? { anyOf } : void 0;
};
function parseNullableDef(def, refs) {
	if ([
		"ZodString",
		"ZodNumber",
		"ZodBigInt",
		"ZodBoolean",
		"ZodNull"
	].includes(def.innerType._def.typeName) && (!def.innerType._def.checks || !def.innerType._def.checks.length)) return { type: [primitiveMappings[def.innerType._def.typeName], "null"] };
	const base = parseDef(def.innerType._def, {
		...refs,
		currentPath: [
			...refs.currentPath,
			"anyOf",
			"0"
		]
	});
	return base && { anyOf: [base, { type: "null" }] };
}
function parseNumberDef(def) {
	const res = { type: "number" };
	if (!def.checks) return res;
	for (const check of def.checks) switch (check.kind) {
		case "int":
			res.type = "integer";
			break;
		case "min":
			if (check.inclusive) res.minimum = check.value;
			else res.exclusiveMinimum = check.value;
			break;
		case "max":
			if (check.inclusive) res.maximum = check.value;
			else res.exclusiveMaximum = check.value;
			break;
		case "multipleOf":
			res.multipleOf = check.value;
			break;
	}
	return res;
}
function parseObjectDef(def, refs) {
	const result = {
		type: "object",
		properties: {}
	};
	const required = [];
	const shape = def.shape();
	for (const propName in shape) {
		let propDef = shape[propName];
		if (propDef === void 0 || propDef._def === void 0) continue;
		const propOptional = safeIsOptional(propDef);
		const parsedDef = parseDef(propDef._def, {
			...refs,
			currentPath: [
				...refs.currentPath,
				"properties",
				propName
			],
			propertyPath: [
				...refs.currentPath,
				"properties",
				propName
			]
		});
		if (parsedDef === void 0) continue;
		result.properties[propName] = parsedDef;
		if (!propOptional) required.push(propName);
	}
	if (required.length) result.required = required;
	const additionalProperties = decideAdditionalProperties(def, refs);
	if (additionalProperties !== void 0) result.additionalProperties = additionalProperties;
	return result;
}
function decideAdditionalProperties(def, refs) {
	if (def.catchall._def.typeName !== "ZodNever") return parseDef(def.catchall._def, {
		...refs,
		currentPath: [...refs.currentPath, "additionalProperties"]
	});
	switch (def.unknownKeys) {
		case "passthrough": return refs.allowedAdditionalProperties;
		case "strict": return refs.rejectedAdditionalProperties;
		case "strip": return refs.removeAdditionalStrategy === "strict" ? refs.allowedAdditionalProperties : refs.rejectedAdditionalProperties;
	}
}
function safeIsOptional(schema) {
	try {
		return schema.isOptional();
	} catch (e) {
		return true;
	}
}
var parseOptionalDef = (def, refs) => {
	var _a2;
	if (refs.currentPath.toString() === ((_a2 = refs.propertyPath) == null ? void 0 : _a2.toString())) return parseDef(def.innerType._def, refs);
	const innerSchema = parseDef(def.innerType._def, {
		...refs,
		currentPath: [
			...refs.currentPath,
			"anyOf",
			"1"
		]
	});
	return innerSchema ? { anyOf: [{ not: parseAnyDef() }, innerSchema] } : parseAnyDef();
};
var parsePipelineDef = (def, refs) => {
	if (refs.pipeStrategy === "input") return parseDef(def.in._def, refs);
	else if (refs.pipeStrategy === "output") return parseDef(def.out._def, refs);
	const a = parseDef(def.in._def, {
		...refs,
		currentPath: [
			...refs.currentPath,
			"allOf",
			"0"
		]
	});
	return { allOf: [a, parseDef(def.out._def, {
		...refs,
		currentPath: [
			...refs.currentPath,
			"allOf",
			a ? "1" : "0"
		]
	})].filter((x) => x !== void 0) };
};
function parsePromiseDef(def, refs) {
	return parseDef(def.type._def, refs);
}
function parseSetDef(def, refs) {
	const schema = {
		type: "array",
		uniqueItems: true,
		items: parseDef(def.valueType._def, {
			...refs,
			currentPath: [...refs.currentPath, "items"]
		})
	};
	if (def.minSize) schema.minItems = def.minSize.value;
	if (def.maxSize) schema.maxItems = def.maxSize.value;
	return schema;
}
function parseTupleDef(def, refs) {
	if (def.rest) return {
		type: "array",
		minItems: def.items.length,
		items: def.items.map((x, i) => parseDef(x._def, {
			...refs,
			currentPath: [
				...refs.currentPath,
				"items",
				`${i}`
			]
		})).reduce((acc, x) => x === void 0 ? acc : [...acc, x], []),
		additionalItems: parseDef(def.rest._def, {
			...refs,
			currentPath: [...refs.currentPath, "additionalItems"]
		})
	};
	else return {
		type: "array",
		minItems: def.items.length,
		maxItems: def.items.length,
		items: def.items.map((x, i) => parseDef(x._def, {
			...refs,
			currentPath: [
				...refs.currentPath,
				"items",
				`${i}`
			]
		})).reduce((acc, x) => x === void 0 ? acc : [...acc, x], [])
	};
}
function parseUndefinedDef() {
	return { not: parseAnyDef() };
}
function parseUnknownDef() {
	return parseAnyDef();
}
var parseReadonlyDef = (def, refs) => {
	return parseDef(def.innerType._def, refs);
};
var selectParser = (def, typeName, refs) => {
	switch (typeName) {
		case ZodFirstPartyTypeKind.ZodString: return parseStringDef(def, refs);
		case ZodFirstPartyTypeKind.ZodNumber: return parseNumberDef(def);
		case ZodFirstPartyTypeKind.ZodObject: return parseObjectDef(def, refs);
		case ZodFirstPartyTypeKind.ZodBigInt: return parseBigintDef(def);
		case ZodFirstPartyTypeKind.ZodBoolean: return parseBooleanDef();
		case ZodFirstPartyTypeKind.ZodDate: return parseDateDef(def, refs);
		case ZodFirstPartyTypeKind.ZodUndefined: return parseUndefinedDef();
		case ZodFirstPartyTypeKind.ZodNull: return parseNullDef();
		case ZodFirstPartyTypeKind.ZodArray: return parseArrayDef(def, refs);
		case ZodFirstPartyTypeKind.ZodUnion:
		case ZodFirstPartyTypeKind.ZodDiscriminatedUnion: return parseUnionDef(def, refs);
		case ZodFirstPartyTypeKind.ZodIntersection: return parseIntersectionDef(def, refs);
		case ZodFirstPartyTypeKind.ZodTuple: return parseTupleDef(def, refs);
		case ZodFirstPartyTypeKind.ZodRecord: return parseRecordDef(def, refs);
		case ZodFirstPartyTypeKind.ZodLiteral: return parseLiteralDef(def);
		case ZodFirstPartyTypeKind.ZodEnum: return parseEnumDef(def);
		case ZodFirstPartyTypeKind.ZodNativeEnum: return parseNativeEnumDef(def);
		case ZodFirstPartyTypeKind.ZodNullable: return parseNullableDef(def, refs);
		case ZodFirstPartyTypeKind.ZodOptional: return parseOptionalDef(def, refs);
		case ZodFirstPartyTypeKind.ZodMap: return parseMapDef(def, refs);
		case ZodFirstPartyTypeKind.ZodSet: return parseSetDef(def, refs);
		case ZodFirstPartyTypeKind.ZodLazy: return () => def.getter()._def;
		case ZodFirstPartyTypeKind.ZodPromise: return parsePromiseDef(def, refs);
		case ZodFirstPartyTypeKind.ZodNaN:
		case ZodFirstPartyTypeKind.ZodNever: return parseNeverDef();
		case ZodFirstPartyTypeKind.ZodEffects: return parseEffectsDef(def, refs);
		case ZodFirstPartyTypeKind.ZodAny: return parseAnyDef();
		case ZodFirstPartyTypeKind.ZodUnknown: return parseUnknownDef();
		case ZodFirstPartyTypeKind.ZodDefault: return parseDefaultDef(def, refs);
		case ZodFirstPartyTypeKind.ZodBranded: return parseBrandedDef(def, refs);
		case ZodFirstPartyTypeKind.ZodReadonly: return parseReadonlyDef(def, refs);
		case ZodFirstPartyTypeKind.ZodCatch: return parseCatchDef(def, refs);
		case ZodFirstPartyTypeKind.ZodPipeline: return parsePipelineDef(def, refs);
		case ZodFirstPartyTypeKind.ZodFunction:
		case ZodFirstPartyTypeKind.ZodVoid:
		case ZodFirstPartyTypeKind.ZodSymbol: return;
		default: return /* @__PURE__ */ ((_) => void 0)(typeName);
	}
};
var getRelativePath = (pathA, pathB) => {
	let i = 0;
	for (; i < pathA.length && i < pathB.length; i++) if (pathA[i] !== pathB[i]) break;
	return [(pathA.length - i).toString(), ...pathB.slice(i)].join("/");
};
function parseDef(def, refs, forceResolution = false) {
	var _a2;
	const seenItem = refs.seen.get(def);
	if (refs.override) {
		const overrideResult = (_a2 = refs.override) == null ? void 0 : _a2.call(refs, def, refs, seenItem, forceResolution);
		if (overrideResult !== ignoreOverride) return overrideResult;
	}
	if (seenItem && !forceResolution) {
		const seenSchema = get$ref(seenItem, refs);
		if (seenSchema !== void 0) return seenSchema;
	}
	const newItem = {
		def,
		path: refs.currentPath,
		jsonSchema: void 0
	};
	refs.seen.set(def, newItem);
	const jsonSchemaOrGetter = selectParser(def, def.typeName, refs);
	const jsonSchema2 = typeof jsonSchemaOrGetter === "function" ? parseDef(jsonSchemaOrGetter(), refs) : jsonSchemaOrGetter;
	if (jsonSchema2) addMeta(def, refs, jsonSchema2);
	if (refs.postProcess) {
		const postProcessResult = refs.postProcess(jsonSchema2, def, refs);
		newItem.jsonSchema = jsonSchema2;
		return postProcessResult;
	}
	newItem.jsonSchema = jsonSchema2;
	return jsonSchema2;
}
var get$ref = (item, refs) => {
	switch (refs.$refStrategy) {
		case "root": return { $ref: item.path.join("/") };
		case "relative": return { $ref: getRelativePath(refs.currentPath, item.path) };
		case "none":
		case "seen":
			if (item.path.length < refs.currentPath.length && item.path.every((value, index) => refs.currentPath[index] === value)) {
				console.warn(`Recursive reference detected at ${refs.currentPath.join("/")}! Defaulting to any`);
				return parseAnyDef();
			}
			return refs.$refStrategy === "seen" ? parseAnyDef() : void 0;
	}
};
var addMeta = (def, refs, jsonSchema2) => {
	if (def.description) jsonSchema2.description = def.description;
	return jsonSchema2;
};
var getRefs = (options) => {
	const _options = getDefaultOptions(options);
	const currentPath = _options.name !== void 0 ? [
		..._options.basePath,
		_options.definitionPath,
		_options.name
	] : _options.basePath;
	return {
		..._options,
		currentPath,
		propertyPath: void 0,
		seen: new Map(Object.entries(_options.definitions).map(([name2, def]) => [def._def, {
			def: def._def,
			path: [
				..._options.basePath,
				_options.definitionPath,
				name2
			],
			jsonSchema: void 0
		}]))
	};
};
var zodToJsonSchema = (schema, options) => {
	var _a2;
	const refs = getRefs(options);
	let definitions = typeof options === "object" && options.definitions ? Object.entries(options.definitions).reduce((acc, [name3, schema2]) => {
		var _a3;
		return {
			...acc,
			[name3]: (_a3 = parseDef(schema2._def, {
				...refs,
				currentPath: [
					...refs.basePath,
					refs.definitionPath,
					name3
				]
			}, true)) != null ? _a3 : parseAnyDef()
		};
	}, {}) : void 0;
	const name2 = typeof options === "string" ? options : (options == null ? void 0 : options.nameStrategy) === "title" ? void 0 : options == null ? void 0 : options.name;
	const main = (_a2 = parseDef(schema._def, name2 === void 0 ? refs : {
		...refs,
		currentPath: [
			...refs.basePath,
			refs.definitionPath,
			name2
		]
	}, false)) != null ? _a2 : parseAnyDef();
	const title = typeof options === "object" && options.name !== void 0 && options.nameStrategy === "title" ? options.name : void 0;
	if (title !== void 0) main.title = title;
	const combined = name2 === void 0 ? definitions ? {
		...main,
		[refs.definitionPath]: definitions
	} : main : {
		$ref: [
			...refs.$refStrategy === "relative" ? [] : refs.basePath,
			refs.definitionPath,
			name2
		].join("/"),
		[refs.definitionPath]: {
			...definitions,
			[name2]: main
		}
	};
	combined.$schema = "http://json-schema.org/draft-07/schema#";
	return combined;
};
var zod_to_json_schema_default = zodToJsonSchema;
function zod3Schema(zodSchema2, options) {
	var _a2;
	const useReferences = (_a2 = options == null ? void 0 : options.useReferences) != null ? _a2 : false;
	return jsonSchema(() => zod_to_json_schema_default(zodSchema2, { $refStrategy: useReferences ? "root" : "none" }), { validate: async (value) => {
		const result = await zodSchema2.safeParseAsync(value);
		return result.success ? {
			success: true,
			value: result.data
		} : {
			success: false,
			error: result.error
		};
	} });
}
function zod4Schema(zodSchema2, options) {
	var _a2;
	const useReferences = (_a2 = options == null ? void 0 : options.useReferences) != null ? _a2 : false;
	return jsonSchema(() => addAdditionalPropertiesToJsonSchema(z4.toJSONSchema(zodSchema2, {
		target: "draft-7",
		io: "input",
		reused: useReferences ? "ref" : "inline"
	})), { validate: async (value) => {
		const result = await z4.safeParseAsync(zodSchema2, value);
		return result.success ? {
			success: true,
			value: result.data
		} : {
			success: false,
			error: result.error
		};
	} });
}
function isZod4Schema(zodSchema2) {
	return "_zod" in zodSchema2;
}
function zodSchema(zodSchema2, options) {
	if (isZod4Schema(zodSchema2)) return zod4Schema(zodSchema2, options);
	else return zod3Schema(zodSchema2, options);
}
function isSchema(value) {
	return typeof value === "object" && value !== null && schemaSymbol in value && value[schemaSymbol] === true && "jsonSchema" in value && "validate" in value;
}
function asSchema(schema) {
	return schema == null ? jsonSchema({
		properties: {},
		additionalProperties: false
	}) : isSchema(schema) ? schema : typeof schema === "function" ? schema() : zodSchema(schema);
}
var { btoa, atob } = globalThis;
function withoutTrailingSlash(url) {
	return url == null ? void 0 : url.replace(/\/$/, "");
}
//#endregion
//#region ../_vendored/ai_v5/dist/index.js
function getContext() {
	return { headers: {} };
}
async function getVercelOidcToken() {
	if (process.env.VERCEL_OIDC_TOKEN) return process.env.VERCEL_OIDC_TOKEN ?? "";
	throw new Error("@vercel/oidc is not available in the vendored @internal AI packages. Provide an API key instead.");
}
var symbol$1 = Symbol.for("vercel.ai.gateway.error");
var _a$1;
var _b;
var GatewayError = class _GatewayError extends (_b = Error, _a$1 = symbol$1, _b) {
	constructor({ message, statusCode = 500, cause }) {
		super(message);
		this[_a$1] = true;
		this.statusCode = statusCode;
		this.cause = cause;
	}
	/**
	* Checks if the given error is a Gateway Error.
	* @param {unknown} error - The error to check.
	* @returns {boolean} True if the error is a Gateway Error, false otherwise.
	*/
	static isInstance(error) {
		return _GatewayError.hasMarker(error);
	}
	static hasMarker(error) {
		return typeof error === "object" && error !== null && symbol$1 in error && error[symbol$1] === true;
	}
};
var name$1 = "GatewayAuthenticationError";
var marker2$1 = `vercel.ai.gateway.error.${name$1}`;
var symbol2$1 = Symbol.for(marker2$1);
var _a2$1;
var _b2;
var GatewayAuthenticationError = class _GatewayAuthenticationError extends (_b2 = GatewayError, _a2$1 = symbol2$1, _b2) {
	constructor({ message = "Authentication failed", statusCode = 401, cause } = {}) {
		super({
			message,
			statusCode,
			cause
		});
		this[_a2$1] = true;
		this.name = name$1;
		this.type = "authentication_error";
	}
	static isInstance(error) {
		return GatewayError.hasMarker(error) && symbol2$1 in error;
	}
	/**
	* Creates a contextual error message when authentication fails
	*/
	static createContextualError({ apiKeyProvided, oidcTokenProvided, message = "Authentication failed", statusCode = 401, cause }) {
		let contextualMessage;
		if (apiKeyProvided) contextualMessage = `AI Gateway authentication failed: Invalid API key.

Create a new API key: https://vercel.com/d?to=%2F%5Bteam%5D%2F%7E%2Fai%2Fapi-keys

Provide via 'apiKey' option or 'AI_GATEWAY_API_KEY' environment variable.`;
		else if (oidcTokenProvided) contextualMessage = `AI Gateway authentication failed: Invalid OIDC token.

Run 'npx vercel link' to link your project, then 'vc env pull' to fetch the token.

Alternatively, use an API key: https://vercel.com/d?to=%2F%5Bteam%5D%2F%7E%2Fai%2Fapi-keys`;
		else contextualMessage = `AI Gateway authentication failed: No authentication provided.

Option 1 - API key:
Create an API key: https://vercel.com/d?to=%2F%5Bteam%5D%2F%7E%2Fai%2Fapi-keys
Provide via 'apiKey' option or 'AI_GATEWAY_API_KEY' environment variable.

Option 2 - OIDC token:
Run 'npx vercel link' to link your project, then 'vc env pull' to fetch the token.`;
		return new _GatewayAuthenticationError({
			message: contextualMessage,
			statusCode,
			cause
		});
	}
};
var name2$1 = "GatewayForbiddenError";
var marker3$1 = `vercel.ai.gateway.error.${name2$1}`;
var symbol3$1 = Symbol.for(marker3$1);
var forbiddenParamSchema = lazyValidator(() => zodSchema(z$1.object({ ruleId: z$1.string() })));
var _a3$1;
var _b3;
var GatewayForbiddenError = class extends (_b3 = GatewayError, _a3$1 = symbol3$1, _b3) {
	constructor({ message = "Forbidden", statusCode = 403, cause, ruleId } = {}) {
		super({
			message,
			statusCode,
			cause
		});
		this[_a3$1] = true;
		this.name = name2$1;
		this.type = "forbidden";
		this.ruleId = ruleId;
	}
	static isInstance(error) {
		return GatewayError.hasMarker(error) && symbol3$1 in error;
	}
};
var name3$1 = "GatewayInvalidRequestError";
var marker4$1 = `vercel.ai.gateway.error.${name3$1}`;
var symbol4$1 = Symbol.for(marker4$1);
var _a4$1;
var _b4;
var GatewayInvalidRequestError = class extends (_b4 = GatewayError, _a4$1 = symbol4$1, _b4) {
	constructor({ message = "Invalid request", statusCode = 400, cause } = {}) {
		super({
			message,
			statusCode,
			cause
		});
		this[_a4$1] = true;
		this.name = name3$1;
		this.type = "invalid_request_error";
	}
	static isInstance(error) {
		return GatewayError.hasMarker(error) && symbol4$1 in error;
	}
};
var name4$1 = "GatewayRateLimitError";
var marker5$1 = `vercel.ai.gateway.error.${name4$1}`;
var symbol5$1 = Symbol.for(marker5$1);
var _a5$1;
var _b5;
var GatewayRateLimitError = class extends (_b5 = GatewayError, _a5$1 = symbol5$1, _b5) {
	constructor({ message = "Rate limit exceeded", statusCode = 429, cause } = {}) {
		super({
			message,
			statusCode,
			cause
		});
		this[_a5$1] = true;
		this.name = name4$1;
		this.type = "rate_limit_exceeded";
	}
	static isInstance(error) {
		return GatewayError.hasMarker(error) && symbol5$1 in error;
	}
};
var name5$1 = "GatewayModelNotFoundError";
var marker6$1 = `vercel.ai.gateway.error.${name5$1}`;
var symbol6$1 = Symbol.for(marker6$1);
var modelNotFoundParamSchema = lazyValidator(() => zodSchema(z$1.object({ modelId: z$1.string() })));
var _a6$1;
var _b6;
var GatewayModelNotFoundError = class extends (_b6 = GatewayError, _a6$1 = symbol6$1, _b6) {
	constructor({ message = "Model not found", statusCode = 404, modelId, cause } = {}) {
		super({
			message,
			statusCode,
			cause
		});
		this[_a6$1] = true;
		this.name = name5$1;
		this.type = "model_not_found";
		this.modelId = modelId;
	}
	static isInstance(error) {
		return GatewayError.hasMarker(error) && symbol6$1 in error;
	}
};
var name6$1 = "GatewayInternalServerError";
var marker7$1 = `vercel.ai.gateway.error.${name6$1}`;
var symbol7$1 = Symbol.for(marker7$1);
var _a7$1;
var _b7;
var GatewayInternalServerError = class extends (_b7 = GatewayError, _a7$1 = symbol7$1, _b7) {
	constructor({ message = "Internal server error", statusCode = 500, cause } = {}) {
		super({
			message,
			statusCode,
			cause
		});
		this[_a7$1] = true;
		this.name = name6$1;
		this.type = "internal_server_error";
	}
	static isInstance(error) {
		return GatewayError.hasMarker(error) && symbol7$1 in error;
	}
};
var name7$1 = "GatewayResponseError";
var marker8$1 = `vercel.ai.gateway.error.${name7$1}`;
var symbol8$1 = Symbol.for(marker8$1);
var _a8$1;
var _b8;
var GatewayResponseError = class extends (_b8 = GatewayError, _a8$1 = symbol8$1, _b8) {
	constructor({ message = "Invalid response from Gateway", statusCode = 502, response, validationError, cause } = {}) {
		super({
			message,
			statusCode,
			cause
		});
		this[_a8$1] = true;
		this.name = name7$1;
		this.type = "response_error";
		this.response = response;
		this.validationError = validationError;
	}
	static isInstance(error) {
		return GatewayError.hasMarker(error) && symbol8$1 in error;
	}
};
async function createGatewayErrorFromResponse({ response, statusCode, defaultMessage = "Gateway request failed", cause, authMethod }) {
	const parseResult = await safeValidateTypes({
		value: response,
		schema: gatewayErrorResponseSchema
	});
	if (!parseResult.success) return new GatewayResponseError({
		message: `Invalid error response format: ${defaultMessage}`,
		statusCode,
		response,
		validationError: parseResult.error,
		cause
	});
	const validatedResponse = parseResult.value;
	const errorType = validatedResponse.error.type;
	const message = validatedResponse.error.message;
	switch (errorType) {
		case "authentication_error": return GatewayAuthenticationError.createContextualError({
			apiKeyProvided: authMethod === "api-key",
			oidcTokenProvided: authMethod === "oidc",
			statusCode,
			cause
		});
		case "invalid_request_error": return new GatewayInvalidRequestError({
			message,
			statusCode,
			cause
		});
		case "rate_limit_exceeded": return new GatewayRateLimitError({
			message,
			statusCode,
			cause
		});
		case "model_not_found": {
			const modelResult = await safeValidateTypes({
				value: validatedResponse.error.param,
				schema: modelNotFoundParamSchema
			});
			return new GatewayModelNotFoundError({
				message,
				statusCode,
				modelId: modelResult.success ? modelResult.value.modelId : void 0,
				cause
			});
		}
		case "internal_server_error": return new GatewayInternalServerError({
			message,
			statusCode,
			cause
		});
		case "forbidden": {
			const ruleResult = await safeValidateTypes({
				value: validatedResponse.error.param,
				schema: forbiddenParamSchema
			});
			return new GatewayForbiddenError({
				message,
				statusCode,
				cause,
				ruleId: ruleResult.success ? ruleResult.value.ruleId : void 0
			});
		}
		default: return new GatewayInternalServerError({
			message,
			statusCode,
			cause
		});
	}
}
var gatewayErrorResponseSchema = lazyValidator(() => zodSchema(z$1.object({ error: z$1.object({
	message: z$1.string(),
	type: z$1.string().nullish(),
	param: z$1.unknown().nullish(),
	code: z$1.union([z$1.string(), z$1.number()]).nullish()
}) })));
function extractApiCallResponse(error) {
	if (error.data !== void 0) return error.data;
	if (error.responseBody != null) try {
		return JSON.parse(error.responseBody);
	} catch (e) {
		return error.responseBody;
	}
	return {};
}
var name8$1 = "GatewayTimeoutError";
var marker9$1 = `vercel.ai.gateway.error.${name8$1}`;
var symbol9$1 = Symbol.for(marker9$1);
var _a9$1;
var _b9;
var GatewayTimeoutError = class _GatewayTimeoutError extends (_b9 = GatewayError, _a9$1 = symbol9$1, _b9) {
	constructor({ message = "Request timed out", statusCode = 408, cause } = {}) {
		super({
			message,
			statusCode,
			cause
		});
		this[_a9$1] = true;
		this.name = name8$1;
		this.type = "timeout_error";
	}
	static isInstance(error) {
		return GatewayError.hasMarker(error) && symbol9$1 in error;
	}
	/**
	* Creates a helpful timeout error message with troubleshooting guidance
	*/
	static createTimeoutError({ originalMessage, statusCode = 408, cause }) {
		const message = `Gateway request timed out: ${originalMessage}

    This is a client-side timeout. To resolve this, increase your timeout configuration: https://vercel.com/docs/ai-gateway/capabilities/video-generation#extending-timeouts-for-node.js`;
		return new _GatewayTimeoutError({
			message,
			statusCode,
			cause
		});
	}
};
function isTimeoutError(error) {
	if (!(error instanceof Error)) return false;
	const errorCode = error.code;
	if (typeof errorCode === "string") return [
		"UND_ERR_HEADERS_TIMEOUT",
		"UND_ERR_BODY_TIMEOUT",
		"UND_ERR_CONNECT_TIMEOUT"
	].includes(errorCode);
	return false;
}
async function asGatewayError(error, authMethod) {
	var _a10;
	if (GatewayError.isInstance(error)) return error;
	if (isTimeoutError(error)) return GatewayTimeoutError.createTimeoutError({
		originalMessage: error instanceof Error ? error.message : "Unknown error",
		cause: error
	});
	if (APICallError.isInstance(error)) {
		if (error.cause && isTimeoutError(error.cause)) return GatewayTimeoutError.createTimeoutError({
			originalMessage: error.message,
			cause: error
		});
		return await createGatewayErrorFromResponse({
			response: extractApiCallResponse(error),
			statusCode: (_a10 = error.statusCode) != null ? _a10 : 500,
			defaultMessage: "Gateway request failed",
			cause: error,
			authMethod
		});
	}
	return await createGatewayErrorFromResponse({
		response: {},
		statusCode: 500,
		defaultMessage: error instanceof Error ? `Gateway request failed: ${error.message}` : "Unknown Gateway error",
		cause: error,
		authMethod
	});
}
var GATEWAY_AUTH_METHOD_HEADER = "ai-gateway-auth-method";
async function parseAuthMethod(headers) {
	const result = await safeValidateTypes({
		value: headers[GATEWAY_AUTH_METHOD_HEADER],
		schema: gatewayAuthMethodSchema
	});
	return result.success ? result.value : void 0;
}
var gatewayAuthMethodSchema = lazyValidator(() => zodSchema(z$1.union([z$1.literal("api-key"), z$1.literal("oidc")])));
var KNOWN_MODEL_TYPES = [
	"embedding",
	"image",
	"language"
];
var GatewayFetchMetadata = class {
	constructor(config) {
		this.config = config;
	}
	async getAvailableModels() {
		try {
			const { value } = await getFromApi({
				url: `${this.config.baseURL}/config`,
				headers: await resolve$1(this.config.headers()),
				successfulResponseHandler: createJsonResponseHandler(gatewayAvailableModelsResponseSchema),
				failedResponseHandler: createJsonErrorResponseHandler({
					errorSchema: z$1.any(),
					errorToMessage: (data) => data
				}),
				fetch: this.config.fetch
			});
			return value;
		} catch (error) {
			throw await asGatewayError(error);
		}
	}
	async getCredits() {
		try {
			const { value } = await getFromApi({
				url: `${new URL(this.config.baseURL).origin}/v1/credits`,
				headers: await resolve$1(this.config.headers()),
				successfulResponseHandler: createJsonResponseHandler(gatewayCreditsResponseSchema),
				failedResponseHandler: createJsonErrorResponseHandler({
					errorSchema: z$1.any(),
					errorToMessage: (data) => data
				}),
				fetch: this.config.fetch
			});
			return value;
		} catch (error) {
			throw await asGatewayError(error);
		}
	}
};
var gatewayAvailableModelsResponseSchema = lazyValidator(() => zodSchema(z$1.object({ models: z$1.array(z$1.object({
	id: z$1.string(),
	name: z$1.string(),
	description: z$1.string().nullish(),
	pricing: z$1.object({
		input: z$1.string(),
		output: z$1.string(),
		input_cache_read: z$1.string().nullish(),
		input_cache_write: z$1.string().nullish()
	}).transform(({ input, output, input_cache_read, input_cache_write }) => ({
		input,
		output,
		...input_cache_read ? { cachedInputTokens: input_cache_read } : {},
		...input_cache_write ? { cacheCreationInputTokens: input_cache_write } : {}
	})).nullish(),
	specification: z$1.object({
		specificationVersion: z$1.literal("v2"),
		provider: z$1.string(),
		modelId: z$1.string()
	}),
	modelType: z$1.string().nullish()
})).transform((models) => models.filter((m) => m.modelType == null || KNOWN_MODEL_TYPES.includes(m.modelType))) })));
var gatewayCreditsResponseSchema = lazyValidator(() => zodSchema(z$1.object({
	balance: z$1.string(),
	total_used: z$1.string()
}).transform(({ balance, total_used }) => ({
	balance,
	totalUsed: total_used
}))));
var GatewaySpendReport = class {
	constructor(config) {
		this.config = config;
	}
	async getSpendReport(params) {
		try {
			const baseUrl = new URL(this.config.baseURL);
			const searchParams = new URLSearchParams();
			searchParams.set("start_date", params.startDate);
			searchParams.set("end_date", params.endDate);
			if (params.groupBy) searchParams.set("group_by", params.groupBy);
			if (params.datePart) searchParams.set("date_part", params.datePart);
			if (params.userId) searchParams.set("user_id", params.userId);
			if (params.model) searchParams.set("model", params.model);
			if (params.provider) searchParams.set("provider", params.provider);
			if (params.credentialType) searchParams.set("credential_type", params.credentialType);
			if (params.tags && params.tags.length > 0) searchParams.set("tags", params.tags.join(","));
			const { value } = await getFromApi({
				url: `${baseUrl.origin}/v1/report?${searchParams.toString()}`,
				headers: await resolve$1(this.config.headers()),
				successfulResponseHandler: createJsonResponseHandler(gatewaySpendReportResponseSchema),
				failedResponseHandler: createJsonErrorResponseHandler({
					errorSchema: z$1.any(),
					errorToMessage: (data) => data
				}),
				fetch: this.config.fetch
			});
			return value;
		} catch (error) {
			throw await asGatewayError(error);
		}
	}
};
var gatewaySpendReportResponseSchema = lazySchema(() => zodSchema(z$1.object({ results: z$1.array(z$1.object({
	day: z$1.string().optional(),
	hour: z$1.string().optional(),
	user: z$1.string().optional(),
	model: z$1.string().optional(),
	tag: z$1.string().optional(),
	provider: z$1.string().optional(),
	credential_type: z$1.enum(["byok", "system"]).optional(),
	total_cost: z$1.number(),
	market_cost: z$1.number().optional(),
	input_tokens: z$1.number().optional(),
	output_tokens: z$1.number().optional(),
	cached_input_tokens: z$1.number().optional(),
	cache_creation_input_tokens: z$1.number().optional(),
	reasoning_tokens: z$1.number().optional(),
	request_count: z$1.number().optional()
}).transform(({ credential_type, total_cost, market_cost, input_tokens, output_tokens, cached_input_tokens, cache_creation_input_tokens, reasoning_tokens, request_count, ...rest }) => ({
	...rest,
	...credential_type !== void 0 ? { credentialType: credential_type } : {},
	totalCost: total_cost,
	...market_cost !== void 0 ? { marketCost: market_cost } : {},
	...input_tokens !== void 0 ? { inputTokens: input_tokens } : {},
	...output_tokens !== void 0 ? { outputTokens: output_tokens } : {},
	...cached_input_tokens !== void 0 ? { cachedInputTokens: cached_input_tokens } : {},
	...cache_creation_input_tokens !== void 0 ? { cacheCreationInputTokens: cache_creation_input_tokens } : {},
	...reasoning_tokens !== void 0 ? { reasoningTokens: reasoning_tokens } : {},
	...request_count !== void 0 ? { requestCount: request_count } : {}
}))) })));
var GatewayGenerationInfoFetcher = class {
	constructor(config) {
		this.config = config;
	}
	async getGenerationInfo(params) {
		try {
			const { value } = await getFromApi({
				url: `${new URL(this.config.baseURL).origin}/v1/generation?id=${encodeURIComponent(params.id)}`,
				headers: await resolve$1(this.config.headers()),
				successfulResponseHandler: createJsonResponseHandler(gatewayGenerationInfoResponseSchema),
				failedResponseHandler: createJsonErrorResponseHandler({
					errorSchema: z$1.any(),
					errorToMessage: (data) => data
				}),
				fetch: this.config.fetch
			});
			return value;
		} catch (error) {
			throw await asGatewayError(error);
		}
	}
};
var gatewayGenerationInfoResponseSchema = lazySchema(() => zodSchema(z$1.object({ data: z$1.object({
	id: z$1.string(),
	total_cost: z$1.number(),
	upstream_inference_cost: z$1.number(),
	usage: z$1.number(),
	created_at: z$1.string(),
	model: z$1.string(),
	is_byok: z$1.boolean(),
	provider_name: z$1.string(),
	streamed: z$1.boolean(),
	finish_reason: z$1.string(),
	latency: z$1.number(),
	generation_time: z$1.number(),
	native_tokens_prompt: z$1.number(),
	native_tokens_completion: z$1.number(),
	native_tokens_reasoning: z$1.number(),
	native_tokens_cached: z$1.number(),
	native_tokens_cache_creation: z$1.number(),
	billable_web_search_calls: z$1.number()
}).transform(({ total_cost, upstream_inference_cost, created_at, is_byok, provider_name, finish_reason, generation_time, native_tokens_prompt, native_tokens_completion, native_tokens_reasoning, native_tokens_cached, native_tokens_cache_creation, billable_web_search_calls, ...rest }) => ({
	...rest,
	totalCost: total_cost,
	upstreamInferenceCost: upstream_inference_cost,
	createdAt: created_at,
	isByok: is_byok,
	providerName: provider_name,
	finishReason: finish_reason,
	generationTime: generation_time,
	promptTokens: native_tokens_prompt,
	completionTokens: native_tokens_completion,
	reasoningTokens: native_tokens_reasoning,
	cachedTokens: native_tokens_cached,
	cacheCreationTokens: native_tokens_cache_creation,
	billableWebSearchCalls: billable_web_search_calls
})) }).transform(({ data }) => data)));
var GatewayLanguageModel = class {
	constructor(modelId, config) {
		this.modelId = modelId;
		this.config = config;
		this.specificationVersion = "v2";
		this.supportedUrls = { "*/*": [/.*/] };
	}
	get provider() {
		return this.config.provider;
	}
	async getArgs(options) {
		const { abortSignal: _abortSignal, ...optionsWithoutSignal } = options;
		return {
			args: this.maybeEncodeFileParts(optionsWithoutSignal),
			warnings: []
		};
	}
	async doGenerate(options) {
		const { args, warnings } = await this.getArgs(options);
		const { abortSignal } = options;
		const resolvedHeaders = await resolve$1(this.config.headers());
		try {
			const { responseHeaders, value: responseBody, rawValue: rawResponse } = await postJsonToApi({
				url: this.getUrl(),
				headers: combineHeaders(resolvedHeaders, options.headers, this.getModelConfigHeaders(this.modelId, false), await resolve$1(this.config.o11yHeaders)),
				body: args,
				successfulResponseHandler: createJsonResponseHandler(z$1.any()),
				failedResponseHandler: createJsonErrorResponseHandler({
					errorSchema: z$1.any(),
					errorToMessage: (data) => data
				}),
				...abortSignal && { abortSignal },
				fetch: this.config.fetch
			});
			return {
				...responseBody,
				request: { body: args },
				response: {
					headers: responseHeaders,
					body: rawResponse
				},
				warnings
			};
		} catch (error) {
			throw await asGatewayError(error, await parseAuthMethod(resolvedHeaders));
		}
	}
	async doStream(options) {
		const { args, warnings } = await this.getArgs(options);
		const { abortSignal } = options;
		const resolvedHeaders = await resolve$1(this.config.headers());
		try {
			const { value: response, responseHeaders } = await postJsonToApi({
				url: this.getUrl(),
				headers: combineHeaders(resolvedHeaders, options.headers, this.getModelConfigHeaders(this.modelId, true), await resolve$1(this.config.o11yHeaders)),
				body: args,
				successfulResponseHandler: createEventSourceResponseHandler(z$1.any()),
				failedResponseHandler: createJsonErrorResponseHandler({
					errorSchema: z$1.any(),
					errorToMessage: (data) => data
				}),
				...abortSignal && { abortSignal },
				fetch: this.config.fetch
			});
			return {
				stream: response.pipeThrough(new TransformStream({
					start(controller) {
						if (warnings.length > 0) controller.enqueue({
							type: "stream-start",
							warnings
						});
					},
					transform(chunk, controller) {
						if (chunk.success) {
							const streamPart = chunk.value;
							if (streamPart.type === "raw" && !options.includeRawChunks) return;
							if (streamPart.type === "response-metadata" && streamPart.timestamp && typeof streamPart.timestamp === "string") streamPart.timestamp = new Date(streamPart.timestamp);
							controller.enqueue(streamPart);
						} else controller.error(chunk.error);
					}
				})),
				request: { body: args },
				response: { headers: responseHeaders }
			};
		} catch (error) {
			throw await asGatewayError(error, await parseAuthMethod(resolvedHeaders));
		}
	}
	isFilePart(part) {
		return part && typeof part === "object" && "type" in part && part.type === "file";
	}
	/**
	* Encodes file parts in the prompt to base64. Mutates the passed options
	* instance directly to avoid copying the file data.
	* @param options - The options to encode.
	* @returns The options with the file parts encoded.
	*/
	maybeEncodeFileParts(options) {
		for (const message of options.prompt) for (const part of message.content) if (this.isFilePart(part)) {
			const filePart = part;
			if (filePart.data instanceof Uint8Array) {
				const buffer = Uint8Array.from(filePart.data);
				const base64Data = Buffer.from(buffer).toString("base64");
				filePart.data = new URL(`data:${filePart.mediaType || "application/octet-stream"};base64,${base64Data}`);
			}
		}
		return options;
	}
	getUrl() {
		return `${this.config.baseURL}/language-model`;
	}
	getModelConfigHeaders(modelId, streaming) {
		return {
			"ai-language-model-specification-version": "2",
			"ai-language-model-id": modelId,
			"ai-language-model-streaming": String(streaming)
		};
	}
};
var GatewayEmbeddingModel = class {
	constructor(modelId, config) {
		this.modelId = modelId;
		this.config = config;
		this.specificationVersion = "v2";
		this.maxEmbeddingsPerCall = 2048;
		this.supportsParallelCalls = true;
	}
	get provider() {
		return this.config.provider;
	}
	async doEmbed({ values, headers, abortSignal, providerOptions }) {
		var _a10;
		const resolvedHeaders = await resolve$1(this.config.headers());
		try {
			const { responseHeaders, value: responseBody, rawValue } = await postJsonToApi({
				url: this.getUrl(),
				headers: combineHeaders(resolvedHeaders, headers != null ? headers : {}, this.getModelConfigHeaders(), await resolve$1(this.config.o11yHeaders)),
				body: {
					input: values.length === 1 ? values[0] : values,
					...providerOptions ? { providerOptions } : {}
				},
				successfulResponseHandler: createJsonResponseHandler(gatewayEmbeddingResponseSchema),
				failedResponseHandler: createJsonErrorResponseHandler({
					errorSchema: z$1.any(),
					errorToMessage: (data) => data
				}),
				...abortSignal && { abortSignal },
				fetch: this.config.fetch
			});
			return {
				embeddings: responseBody.embeddings,
				usage: (_a10 = responseBody.usage) != null ? _a10 : void 0,
				providerMetadata: responseBody.providerMetadata,
				response: {
					headers: responseHeaders,
					body: rawValue
				}
			};
		} catch (error) {
			throw await asGatewayError(error, await parseAuthMethod(resolvedHeaders));
		}
	}
	getUrl() {
		return `${this.config.baseURL}/embedding-model`;
	}
	getModelConfigHeaders() {
		return {
			"ai-embedding-model-specification-version": "2",
			"ai-model-id": this.modelId
		};
	}
};
var gatewayEmbeddingResponseSchema = lazyValidator(() => zodSchema(z$1.object({
	embeddings: z$1.array(z$1.array(z$1.number())),
	usage: z$1.object({ tokens: z$1.number() }).nullish(),
	providerMetadata: z$1.record(z$1.string(), z$1.record(z$1.string(), z$1.unknown())).optional()
})));
var GatewayImageModel = class {
	constructor(modelId, config) {
		this.modelId = modelId;
		this.config = config;
		this.specificationVersion = "v2";
		this.maxImagesPerCall = Number.MAX_SAFE_INTEGER;
	}
	get provider() {
		return this.config.provider;
	}
	async doGenerate({ prompt, n, size, aspectRatio, seed, providerOptions, headers, abortSignal }) {
		var _a10, _b10, _c, _d;
		const resolvedHeaders = await resolve$1(this.config.headers());
		try {
			const { responseHeaders, value: responseBody, rawValue } = await postJsonToApi({
				url: this.getUrl(),
				headers: combineHeaders(resolvedHeaders, headers != null ? headers : {}, this.getModelConfigHeaders(), await resolve$1(this.config.o11yHeaders)),
				body: {
					prompt,
					n,
					...size && { size },
					...aspectRatio && { aspectRatio },
					...seed && { seed },
					...providerOptions && { providerOptions }
				},
				successfulResponseHandler: createJsonResponseHandler(gatewayImageResponseSchema),
				failedResponseHandler: createJsonErrorResponseHandler({
					errorSchema: z$1.any(),
					errorToMessage: (data) => data
				}),
				...abortSignal && { abortSignal },
				fetch: this.config.fetch
			});
			return {
				images: responseBody.images,
				warnings: (_a10 = responseBody.warnings) != null ? _a10 : [],
				providerMetadata: responseBody.providerMetadata,
				response: {
					timestamp: /* @__PURE__ */ new Date(),
					modelId: this.modelId,
					headers: responseHeaders
				},
				...responseBody.usage != null && { usage: {
					inputTokens: (_b10 = responseBody.usage.inputTokens) != null ? _b10 : void 0,
					outputTokens: (_c = responseBody.usage.outputTokens) != null ? _c : void 0,
					totalTokens: (_d = responseBody.usage.totalTokens) != null ? _d : void 0
				} }
			};
		} catch (error) {
			throw await asGatewayError(error, await parseAuthMethod(resolvedHeaders));
		}
	}
	getUrl() {
		return `${this.config.baseURL}/image-model`;
	}
	getModelConfigHeaders() {
		return {
			"ai-image-model-specification-version": "2",
			"ai-model-id": this.modelId
		};
	}
};
var providerMetadataEntrySchema = z$1.object({ images: z$1.array(z$1.unknown()).optional() }).catchall(z$1.unknown());
var gatewayImageUsageSchema = z$1.object({
	inputTokens: z$1.number().nullish(),
	outputTokens: z$1.number().nullish(),
	totalTokens: z$1.number().nullish()
});
var gatewayImageResponseSchema = z$1.object({
	images: z$1.array(z$1.string()),
	warnings: z$1.array(z$1.object({
		type: z$1.literal("other"),
		message: z$1.string()
	})).optional(),
	providerMetadata: z$1.record(z$1.string(), providerMetadataEntrySchema).optional(),
	usage: gatewayImageUsageSchema.optional()
});
var parallelSearchToolFactory = createProviderDefinedToolFactoryWithOutputSchema({
	id: "gateway.parallel_search",
	name: "parallel_search",
	inputSchema: lazySchema(() => zodSchema(z.object({
		objective: z.string().describe("Natural-language description of the web research goal, including source or freshness guidance and broader context from the task. Maximum 5000 characters."),
		search_queries: z.array(z.string()).optional().describe("Optional search queries to supplement the objective. Maximum 200 characters per query."),
		mode: z.enum(["one-shot", "agentic"]).optional().describe("Mode preset: \"one-shot\" for comprehensive results with longer excerpts (default), \"agentic\" for concise, token-efficient results for multi-step workflows."),
		max_results: z.number().optional().describe("Maximum number of results to return (1-20). Defaults to 10 if not specified."),
		source_policy: z.object({
			include_domains: z.array(z.string()).optional().describe("List of domains to include in search results."),
			exclude_domains: z.array(z.string()).optional().describe("List of domains to exclude from search results."),
			after_date: z.string().optional().describe("Only include results published after this date (ISO 8601 format).")
		}).optional().describe("Source policy for controlling which domains to include/exclude and freshness."),
		excerpts: z.object({
			max_chars_per_result: z.number().optional().describe("Maximum characters per result."),
			max_chars_total: z.number().optional().describe("Maximum total characters across all results.")
		}).optional().describe("Excerpt configuration for controlling result length."),
		fetch_policy: z.object({ max_age_seconds: z.number().optional().describe("Maximum age in seconds for cached content. Set to 0 to always fetch fresh content.") }).optional().describe("Fetch policy for controlling content freshness.")
	}))),
	outputSchema: lazySchema(() => zodSchema(z.union([z.object({
		searchId: z.string(),
		results: z.array(z.object({
			url: z.string(),
			title: z.string(),
			excerpt: z.string(),
			publishDate: z.string().nullable().optional(),
			relevanceScore: z.number().optional()
		}))
	}), z.object({
		error: z.enum([
			"api_error",
			"rate_limit",
			"timeout",
			"invalid_input",
			"configuration_error",
			"unknown"
		]),
		statusCode: z.number().optional(),
		message: z.string()
	})])))
});
var parallelSearch = (config = {}) => parallelSearchToolFactory(config);
var perplexitySearchToolFactory = createProviderDefinedToolFactoryWithOutputSchema({
	id: "gateway.perplexity_search",
	name: "perplexity_search",
	inputSchema: lazySchema(() => zodSchema(z.object({
		query: z.union([z.string(), z.array(z.string())]).describe("Search query (string) or multiple queries (array of up to 5 strings). Multi-query searches return combined results from all queries."),
		max_results: z.number().optional().describe("Maximum number of search results to return (1-20, default: 10)"),
		max_tokens_per_page: z.number().optional().describe("Maximum number of tokens to extract per search result page (256-2048, default: 2048)"),
		max_tokens: z.number().optional().describe("Maximum total tokens across all search results (default: 25000, max: 1000000)"),
		country: z.string().optional().describe("Two-letter ISO 3166-1 alpha-2 country code for regional search results (e.g., 'US', 'GB', 'FR')"),
		search_domain_filter: z.array(z.string()).optional().describe("List of domains to include or exclude from search results (max 20). To include: ['nature.com', 'science.org']. To exclude: ['-example.com', '-spam.net']"),
		search_language_filter: z.array(z.string()).optional().describe("List of ISO 639-1 language codes to filter results (max 10, lowercase). Examples: ['en', 'fr', 'de']"),
		search_after_date: z.string().optional().describe("Include only results published after this date. Format: 'MM/DD/YYYY' (e.g., '3/1/2025'). Cannot be used with search_recency_filter."),
		search_before_date: z.string().optional().describe("Include only results published before this date. Format: 'MM/DD/YYYY' (e.g., '3/15/2025'). Cannot be used with search_recency_filter."),
		last_updated_after_filter: z.string().optional().describe("Include only results last updated after this date. Format: 'MM/DD/YYYY' (e.g., '3/1/2025'). Cannot be used with search_recency_filter."),
		last_updated_before_filter: z.string().optional().describe("Include only results last updated before this date. Format: 'MM/DD/YYYY' (e.g., '3/15/2025'). Cannot be used with search_recency_filter."),
		search_recency_filter: z.enum([
			"day",
			"week",
			"month",
			"year"
		]).optional().describe("Filter results by relative time period. Cannot be used with search_after_date or search_before_date.")
	}))),
	outputSchema: lazySchema(() => zodSchema(z.union([z.object({
		results: z.array(z.object({
			title: z.string(),
			url: z.string(),
			snippet: z.string(),
			date: z.string().optional(),
			lastUpdated: z.string().optional()
		})),
		id: z.string()
	}), z.object({
		error: z.enum([
			"api_error",
			"rate_limit",
			"timeout",
			"invalid_input",
			"unknown"
		]),
		statusCode: z.number().optional(),
		message: z.string()
	})])))
});
var perplexitySearch = (config = {}) => perplexitySearchToolFactory(config);
var gatewayTools = {
	/**
	* Search the web using Parallel AI's Search API for LLM-optimized excerpts.
	*
	* Takes a natural language objective and returns relevant excerpts,
	* replacing multiple keyword searches with a single call for broad
	* or complex queries. Supports different search types for depth vs
	* breadth tradeoffs.
	*/
	parallelSearch,
	/**
	* Search the web using Perplexity's Search API for real-time information,
	* news, research papers, and articles.
	*
	* Provides ranked search results with advanced filtering options including
	* domain, language, date range, and recency filters.
	*/
	perplexitySearch
};
async function getVercelRequestId() {
	var _a10;
	return (_a10 = getContext().headers) == null ? void 0 : _a10["x-vercel-id"];
}
var VERSION$2 = "2.0.115";
var AI_GATEWAY_PROTOCOL_VERSION = "0.0.1";
function createGatewayProvider(options = {}) {
	var _a10, _b10;
	let pendingMetadata = null;
	let metadataCache = null;
	const cacheRefreshMillis = (_a10 = options.metadataCacheRefreshMillis) != null ? _a10 : 1e3 * 60 * 5;
	let lastFetchTime = 0;
	const baseURL = (_b10 = withoutTrailingSlash(options.baseURL)) != null ? _b10 : "https://ai-gateway.vercel.sh/v1/ai";
	const getHeaders = async () => {
		const auth = await getGatewayAuthToken(options);
		if (auth) return withUserAgentSuffix({
			Authorization: `Bearer ${auth.token}`,
			"ai-gateway-protocol-version": AI_GATEWAY_PROTOCOL_VERSION,
			[GATEWAY_AUTH_METHOD_HEADER]: auth.authMethod,
			...options.headers
		}, `ai-sdk/gateway/${VERSION$2}`);
		throw GatewayAuthenticationError.createContextualError({
			apiKeyProvided: false,
			oidcTokenProvided: false,
			statusCode: 401
		});
	};
	const createO11yHeaders = () => {
		const deploymentId = loadOptionalSetting({
			settingValue: void 0,
			environmentVariableName: "VERCEL_DEPLOYMENT_ID"
		});
		const environment = loadOptionalSetting({
			settingValue: void 0,
			environmentVariableName: "VERCEL_ENV"
		});
		const region = loadOptionalSetting({
			settingValue: void 0,
			environmentVariableName: "VERCEL_REGION"
		});
		const projectId = loadOptionalSetting({
			settingValue: void 0,
			environmentVariableName: "VERCEL_PROJECT_ID"
		});
		return async () => {
			const requestId = await getVercelRequestId();
			return {
				...deploymentId && { "ai-o11y-deployment-id": deploymentId },
				...environment && { "ai-o11y-environment": environment },
				...region && { "ai-o11y-region": region },
				...requestId && { "ai-o11y-request-id": requestId },
				...projectId && { "ai-o11y-project-id": projectId }
			};
		};
	};
	const createLanguageModel = (modelId) => {
		return new GatewayLanguageModel(modelId, {
			provider: "gateway",
			baseURL,
			headers: getHeaders,
			fetch: options.fetch,
			o11yHeaders: createO11yHeaders()
		});
	};
	const getAvailableModels = async () => {
		var _a11, _b11, _c;
		const now = (_c = (_b11 = (_a11 = options._internal) == null ? void 0 : _a11.currentDate) == null ? void 0 : _b11.call(_a11).getTime()) != null ? _c : Date.now();
		if (!pendingMetadata || now - lastFetchTime > cacheRefreshMillis) {
			lastFetchTime = now;
			pendingMetadata = new GatewayFetchMetadata({
				baseURL,
				headers: getHeaders,
				fetch: options.fetch
			}).getAvailableModels().then((metadata) => {
				metadataCache = metadata;
				return metadata;
			}).catch(async (error) => {
				throw await asGatewayError(error, await parseAuthMethod(await getHeaders()));
			});
		}
		return metadataCache ? Promise.resolve(metadataCache) : pendingMetadata;
	};
	const getCredits = async () => {
		return new GatewayFetchMetadata({
			baseURL,
			headers: getHeaders,
			fetch: options.fetch
		}).getCredits().catch(async (error) => {
			throw await asGatewayError(error, await parseAuthMethod(await getHeaders()));
		});
	};
	const getSpendReport = async (params) => {
		return new GatewaySpendReport({
			baseURL,
			headers: getHeaders,
			fetch: options.fetch
		}).getSpendReport(params).catch(async (error) => {
			throw await asGatewayError(error, await parseAuthMethod(await getHeaders()));
		});
	};
	const getGenerationInfo = async (params) => {
		return new GatewayGenerationInfoFetcher({
			baseURL,
			headers: getHeaders,
			fetch: options.fetch
		}).getGenerationInfo(params).catch(async (error) => {
			throw await asGatewayError(error, await parseAuthMethod(await getHeaders()));
		});
	};
	const provider = function(modelId) {
		if (new.target) throw new Error("The Gateway Provider model function cannot be called with the new keyword.");
		return createLanguageModel(modelId);
	};
	provider.getAvailableModels = getAvailableModels;
	provider.getCredits = getCredits;
	provider.getSpendReport = getSpendReport;
	provider.getGenerationInfo = getGenerationInfo;
	provider.imageModel = (modelId) => {
		return new GatewayImageModel(modelId, {
			provider: "gateway",
			baseURL,
			headers: getHeaders,
			fetch: options.fetch,
			o11yHeaders: createO11yHeaders()
		});
	};
	provider.languageModel = createLanguageModel;
	provider.textEmbeddingModel = (modelId) => {
		return new GatewayEmbeddingModel(modelId, {
			provider: "gateway",
			baseURL,
			headers: getHeaders,
			fetch: options.fetch,
			o11yHeaders: createO11yHeaders()
		});
	};
	provider.tools = gatewayTools;
	return provider;
}
createGatewayProvider();
async function getGatewayAuthToken(options) {
	const apiKey = loadOptionalSetting({
		settingValue: options.apiKey,
		environmentVariableName: "AI_GATEWAY_API_KEY"
	});
	if (apiKey) return {
		token: apiKey,
		authMethod: "api-key"
	};
	try {
		return {
			token: await getVercelOidcToken(),
			authMethod: "oidc"
		};
	} catch (e) {
		return null;
	}
}
/** only globals that common to node and browsers are allowed */
var _globalThis = typeof globalThis === "object" ? globalThis : global;
var VERSION$1 = "1.9.0";
var re = /^(\d+)\.(\d+)\.(\d+)(-(.+))?$/;
/**
* Create a function to test an API version to see if it is compatible with the provided ownVersion.
*
* The returned function has the following semantics:
* - Exact match is always compatible
* - Major versions must match exactly
*    - 1.x package cannot use global 2.x package
*    - 2.x package cannot use global 1.x package
* - The minor version of the API module requesting access to the global API must be less than or equal to the minor version of this API
*    - 1.3 package may use 1.4 global because the later global contains all functions 1.3 expects
*    - 1.4 package may NOT use 1.3 global because it may try to call functions which don't exist on 1.3
* - If the major version is 0, the minor version is treated as the major and the patch is treated as the minor
* - Patch and build tag differences are not considered at this time
*
* @param ownVersion version which should be checked against
*/
function _makeCompatibilityCheck(ownVersion) {
	var acceptedVersions = /* @__PURE__ */ new Set([ownVersion]);
	var rejectedVersions = /* @__PURE__ */ new Set();
	var myVersionMatch = ownVersion.match(re);
	if (!myVersionMatch) return function() {
		return false;
	};
	var ownVersionParsed = {
		major: +myVersionMatch[1],
		minor: +myVersionMatch[2],
		patch: +myVersionMatch[3],
		prerelease: myVersionMatch[4]
	};
	if (ownVersionParsed.prerelease != null) return function isExactmatch(globalVersion) {
		return globalVersion === ownVersion;
	};
	function _reject(v) {
		rejectedVersions.add(v);
		return false;
	}
	function _accept(v) {
		acceptedVersions.add(v);
		return true;
	}
	return function isCompatible(globalVersion) {
		if (acceptedVersions.has(globalVersion)) return true;
		if (rejectedVersions.has(globalVersion)) return false;
		var globalVersionMatch = globalVersion.match(re);
		if (!globalVersionMatch) return _reject(globalVersion);
		var globalVersionParsed = {
			major: +globalVersionMatch[1],
			minor: +globalVersionMatch[2],
			patch: +globalVersionMatch[3],
			prerelease: globalVersionMatch[4]
		};
		if (globalVersionParsed.prerelease != null) return _reject(globalVersion);
		if (ownVersionParsed.major !== globalVersionParsed.major) return _reject(globalVersion);
		if (ownVersionParsed.major === 0) {
			if (ownVersionParsed.minor === globalVersionParsed.minor && ownVersionParsed.patch <= globalVersionParsed.patch) return _accept(globalVersion);
			return _reject(globalVersion);
		}
		if (ownVersionParsed.minor <= globalVersionParsed.minor) return _accept(globalVersion);
		return _reject(globalVersion);
	};
}
/**
* Test an API version to see if it is compatible with this API.
*
* - Exact match is always compatible
* - Major versions must match exactly
*    - 1.x package cannot use global 2.x package
*    - 2.x package cannot use global 1.x package
* - The minor version of the API module requesting access to the global API must be less than or equal to the minor version of this API
*    - 1.3 package may use 1.4 global because the later global contains all functions 1.3 expects
*    - 1.4 package may NOT use 1.3 global because it may try to call functions which don't exist on 1.3
* - If the major version is 0, the minor version is treated as the major and the patch is treated as the minor
* - Patch and build tag differences are not considered at this time
*
* @param version version of the API requesting an instance of the global API
*/
var isCompatible = _makeCompatibilityCheck(VERSION$1);
var major = VERSION$1.split(".")[0];
var GLOBAL_OPENTELEMETRY_API_KEY = Symbol.for("opentelemetry.js.api." + major);
var _global = _globalThis;
function registerGlobal(type, instance, diag, allowOverride) {
	var _a;
	if (allowOverride === void 0) allowOverride = false;
	var api = _global[GLOBAL_OPENTELEMETRY_API_KEY] = (_a = _global[GLOBAL_OPENTELEMETRY_API_KEY]) !== null && _a !== void 0 ? _a : { version: VERSION$1 };
	if (!allowOverride && api[type]) {
		var err = /* @__PURE__ */ new Error("@opentelemetry/api: Attempted duplicate registration of API: " + type);
		diag.error(err.stack || err.message);
		return false;
	}
	if (api.version !== "1.9.0") {
		var err = /* @__PURE__ */ new Error("@opentelemetry/api: Registration of version v" + api.version + " for " + type + " does not match previously registered API v" + VERSION$1);
		diag.error(err.stack || err.message);
		return false;
	}
	api[type] = instance;
	diag.debug("@opentelemetry/api: Registered a global for " + type + " v" + VERSION$1 + ".");
	return true;
}
function getGlobal(type) {
	var _a, _b;
	var globalVersion = (_a = _global[GLOBAL_OPENTELEMETRY_API_KEY]) === null || _a === void 0 ? void 0 : _a.version;
	if (!globalVersion || !isCompatible(globalVersion)) return;
	return (_b = _global[GLOBAL_OPENTELEMETRY_API_KEY]) === null || _b === void 0 ? void 0 : _b[type];
}
function unregisterGlobal(type, diag) {
	diag.debug("@opentelemetry/api: Unregistering a global for " + type + " v" + VERSION$1 + ".");
	var api = _global[GLOBAL_OPENTELEMETRY_API_KEY];
	if (api) delete api[type];
}
var __read$3 = function(o, n) {
	var m = typeof Symbol === "function" && o[Symbol.iterator];
	if (!m) return o;
	var i = m.call(o), r, ar = [], e;
	try {
		while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
	} catch (error) {
		e = { error };
	} finally {
		try {
			if (r && !r.done && (m = i["return"])) m.call(i);
		} finally {
			if (e) throw e.error;
		}
	}
	return ar;
};
var __spreadArray$3 = function(to, from, pack) {
	if (pack || arguments.length === 2) {
		for (var i = 0, l = from.length, ar; i < l; i++) if (ar || !(i in from)) {
			if (!ar) ar = Array.prototype.slice.call(from, 0, i);
			ar[i] = from[i];
		}
	}
	return to.concat(ar || Array.prototype.slice.call(from));
};
/**
* Component Logger which is meant to be used as part of any component which
* will add automatically additional namespace in front of the log message.
* It will then forward all message to global diag logger
* @example
* const cLogger = diag.createComponentLogger({ namespace: '@opentelemetry/instrumentation-http' });
* cLogger.debug('test');
* // @opentelemetry/instrumentation-http test
*/
var DiagComponentLogger = function() {
	function DiagComponentLogger(props) {
		this._namespace = props.namespace || "DiagComponentLogger";
	}
	DiagComponentLogger.prototype.debug = function() {
		var args = [];
		for (var _i = 0; _i < arguments.length; _i++) args[_i] = arguments[_i];
		return logProxy("debug", this._namespace, args);
	};
	DiagComponentLogger.prototype.error = function() {
		var args = [];
		for (var _i = 0; _i < arguments.length; _i++) args[_i] = arguments[_i];
		return logProxy("error", this._namespace, args);
	};
	DiagComponentLogger.prototype.info = function() {
		var args = [];
		for (var _i = 0; _i < arguments.length; _i++) args[_i] = arguments[_i];
		return logProxy("info", this._namespace, args);
	};
	DiagComponentLogger.prototype.warn = function() {
		var args = [];
		for (var _i = 0; _i < arguments.length; _i++) args[_i] = arguments[_i];
		return logProxy("warn", this._namespace, args);
	};
	DiagComponentLogger.prototype.verbose = function() {
		var args = [];
		for (var _i = 0; _i < arguments.length; _i++) args[_i] = arguments[_i];
		return logProxy("verbose", this._namespace, args);
	};
	return DiagComponentLogger;
}();
function logProxy(funcName, namespace, args) {
	var logger = getGlobal("diag");
	if (!logger) return;
	args.unshift(namespace);
	return logger[funcName].apply(logger, __spreadArray$3([], __read$3(args), false));
}
/**
* Defines the available internal logging levels for the diagnostic logger, the numeric values
* of the levels are defined to match the original values from the initial LogLevel to avoid
* compatibility/migration issues for any implementation that assume the numeric ordering.
*/
var DiagLogLevel;
(function(DiagLogLevel) {
	/** Diagnostic Logging level setting to disable all logging (except and forced logs) */
	DiagLogLevel[DiagLogLevel["NONE"] = 0] = "NONE";
	/** Identifies an error scenario */
	DiagLogLevel[DiagLogLevel["ERROR"] = 30] = "ERROR";
	/** Identifies a warning scenario */
	DiagLogLevel[DiagLogLevel["WARN"] = 50] = "WARN";
	/** General informational log message */
	DiagLogLevel[DiagLogLevel["INFO"] = 60] = "INFO";
	/** General debug log message */
	DiagLogLevel[DiagLogLevel["DEBUG"] = 70] = "DEBUG";
	/**
	* Detailed trace level logging should only be used for development, should only be set
	* in a development environment.
	*/
	DiagLogLevel[DiagLogLevel["VERBOSE"] = 80] = "VERBOSE";
	/** Used to set the logging level to include all logging */
	DiagLogLevel[DiagLogLevel["ALL"] = 9999] = "ALL";
})(DiagLogLevel || (DiagLogLevel = {}));
function createLogLevelDiagLogger(maxLevel, logger) {
	if (maxLevel < DiagLogLevel.NONE) maxLevel = DiagLogLevel.NONE;
	else if (maxLevel > DiagLogLevel.ALL) maxLevel = DiagLogLevel.ALL;
	logger = logger || {};
	function _filterFunc(funcName, theLevel) {
		var theFunc = logger[funcName];
		if (typeof theFunc === "function" && maxLevel >= theLevel) return theFunc.bind(logger);
		return function() {};
	}
	return {
		error: _filterFunc("error", DiagLogLevel.ERROR),
		warn: _filterFunc("warn", DiagLogLevel.WARN),
		info: _filterFunc("info", DiagLogLevel.INFO),
		debug: _filterFunc("debug", DiagLogLevel.DEBUG),
		verbose: _filterFunc("verbose", DiagLogLevel.VERBOSE)
	};
}
var __read$2 = function(o, n) {
	var m = typeof Symbol === "function" && o[Symbol.iterator];
	if (!m) return o;
	var i = m.call(o), r, ar = [], e;
	try {
		while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
	} catch (error) {
		e = { error };
	} finally {
		try {
			if (r && !r.done && (m = i["return"])) m.call(i);
		} finally {
			if (e) throw e.error;
		}
	}
	return ar;
};
var __spreadArray$2 = function(to, from, pack) {
	if (pack || arguments.length === 2) {
		for (var i = 0, l = from.length, ar; i < l; i++) if (ar || !(i in from)) {
			if (!ar) ar = Array.prototype.slice.call(from, 0, i);
			ar[i] = from[i];
		}
	}
	return to.concat(ar || Array.prototype.slice.call(from));
};
var API_NAME$2 = "diag";
/**
* Singleton object which represents the entry point to the OpenTelemetry internal
* diagnostic API
*/
var DiagAPI = function() {
	/**
	* Private internal constructor
	* @private
	*/
	function DiagAPI() {
		function _logProxy(funcName) {
			return function() {
				var args = [];
				for (var _i = 0; _i < arguments.length; _i++) args[_i] = arguments[_i];
				var logger = getGlobal("diag");
				if (!logger) return;
				return logger[funcName].apply(logger, __spreadArray$2([], __read$2(args), false));
			};
		}
		var self = this;
		var setLogger = function(logger, optionsOrLogLevel) {
			var _a, _b, _c;
			if (optionsOrLogLevel === void 0) optionsOrLogLevel = { logLevel: DiagLogLevel.INFO };
			if (logger === self) {
				var err = /* @__PURE__ */ new Error("Cannot use diag as the logger for itself. Please use a DiagLogger implementation like ConsoleDiagLogger or a custom implementation");
				self.error((_a = err.stack) !== null && _a !== void 0 ? _a : err.message);
				return false;
			}
			if (typeof optionsOrLogLevel === "number") optionsOrLogLevel = { logLevel: optionsOrLogLevel };
			var oldLogger = getGlobal("diag");
			var newLogger = createLogLevelDiagLogger((_b = optionsOrLogLevel.logLevel) !== null && _b !== void 0 ? _b : DiagLogLevel.INFO, logger);
			if (oldLogger && !optionsOrLogLevel.suppressOverrideMessage) {
				var stack = (_c = (/* @__PURE__ */ new Error()).stack) !== null && _c !== void 0 ? _c : "<failed to generate stacktrace>";
				oldLogger.warn("Current logger will be overwritten from " + stack);
				newLogger.warn("Current logger will overwrite one already registered from " + stack);
			}
			return registerGlobal("diag", newLogger, self, true);
		};
		self.setLogger = setLogger;
		self.disable = function() {
			unregisterGlobal(API_NAME$2, self);
		};
		self.createComponentLogger = function(options) {
			return new DiagComponentLogger(options);
		};
		self.verbose = _logProxy("verbose");
		self.debug = _logProxy("debug");
		self.info = _logProxy("info");
		self.warn = _logProxy("warn");
		self.error = _logProxy("error");
	}
	/** Get the singleton instance of the DiagAPI API */
	DiagAPI.instance = function() {
		if (!this._instance) this._instance = new DiagAPI();
		return this._instance;
	};
	return DiagAPI;
}();
/** Get a key to uniquely identify a context value */
function createContextKey(description) {
	return Symbol.for(description);
}
/** The root context is used as the default parent context when there is no active context */
var ROOT_CONTEXT = new (function() {
	/**
	* Construct a new context which inherits values from an optional parent context.
	*
	* @param parentContext a context from which to inherit values
	*/
	function BaseContext(parentContext) {
		var self = this;
		self._currentContext = parentContext ? new Map(parentContext) : /* @__PURE__ */ new Map();
		self.getValue = function(key) {
			return self._currentContext.get(key);
		};
		self.setValue = function(key, value) {
			var context = new BaseContext(self._currentContext);
			context._currentContext.set(key, value);
			return context;
		};
		self.deleteValue = function(key) {
			var context = new BaseContext(self._currentContext);
			context._currentContext.delete(key);
			return context;
		};
	}
	return BaseContext;
}())();
var __read$1 = function(o, n) {
	var m = typeof Symbol === "function" && o[Symbol.iterator];
	if (!m) return o;
	var i = m.call(o), r, ar = [], e;
	try {
		while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
	} catch (error) {
		e = { error };
	} finally {
		try {
			if (r && !r.done && (m = i["return"])) m.call(i);
		} finally {
			if (e) throw e.error;
		}
	}
	return ar;
};
var __spreadArray$1 = function(to, from, pack) {
	if (pack || arguments.length === 2) {
		for (var i = 0, l = from.length, ar; i < l; i++) if (ar || !(i in from)) {
			if (!ar) ar = Array.prototype.slice.call(from, 0, i);
			ar[i] = from[i];
		}
	}
	return to.concat(ar || Array.prototype.slice.call(from));
};
var NoopContextManager = function() {
	function NoopContextManager() {}
	NoopContextManager.prototype.active = function() {
		return ROOT_CONTEXT;
	};
	NoopContextManager.prototype.with = function(_context, fn, thisArg) {
		var args = [];
		for (var _i = 3; _i < arguments.length; _i++) args[_i - 3] = arguments[_i];
		return fn.call.apply(fn, __spreadArray$1([thisArg], __read$1(args), false));
	};
	NoopContextManager.prototype.bind = function(_context, target) {
		return target;
	};
	NoopContextManager.prototype.enable = function() {
		return this;
	};
	NoopContextManager.prototype.disable = function() {
		return this;
	};
	return NoopContextManager;
}();
var __read = function(o, n) {
	var m = typeof Symbol === "function" && o[Symbol.iterator];
	if (!m) return o;
	var i = m.call(o), r, ar = [], e;
	try {
		while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
	} catch (error) {
		e = { error };
	} finally {
		try {
			if (r && !r.done && (m = i["return"])) m.call(i);
		} finally {
			if (e) throw e.error;
		}
	}
	return ar;
};
var __spreadArray = function(to, from, pack) {
	if (pack || arguments.length === 2) {
		for (var i = 0, l = from.length, ar; i < l; i++) if (ar || !(i in from)) {
			if (!ar) ar = Array.prototype.slice.call(from, 0, i);
			ar[i] = from[i];
		}
	}
	return to.concat(ar || Array.prototype.slice.call(from));
};
var API_NAME$1 = "context";
var NOOP_CONTEXT_MANAGER = new NoopContextManager();
/**
* Singleton object which represents the entry point to the OpenTelemetry Context API
*/
var ContextAPI = function() {
	/** Empty private constructor prevents end users from constructing a new instance of the API */
	function ContextAPI() {}
	/** Get the singleton instance of the Context API */
	ContextAPI.getInstance = function() {
		if (!this._instance) this._instance = new ContextAPI();
		return this._instance;
	};
	/**
	* Set the current context manager.
	*
	* @returns true if the context manager was successfully registered, else false
	*/
	ContextAPI.prototype.setGlobalContextManager = function(contextManager) {
		return registerGlobal(API_NAME$1, contextManager, DiagAPI.instance());
	};
	/**
	* Get the currently active context
	*/
	ContextAPI.prototype.active = function() {
		return this._getContextManager().active();
	};
	/**
	* Execute a function with an active context
	*
	* @param context context to be active during function execution
	* @param fn function to execute in a context
	* @param thisArg optional receiver to be used for calling fn
	* @param args optional arguments forwarded to fn
	*/
	ContextAPI.prototype.with = function(context, fn, thisArg) {
		var _a;
		var args = [];
		for (var _i = 3; _i < arguments.length; _i++) args[_i - 3] = arguments[_i];
		return (_a = this._getContextManager()).with.apply(_a, __spreadArray([
			context,
			fn,
			thisArg
		], __read(args), false));
	};
	/**
	* Bind a context to a target function or event emitter
	*
	* @param context context to bind to the event emitter or function. Defaults to the currently active context
	* @param target function or event emitter to bind
	*/
	ContextAPI.prototype.bind = function(context, target) {
		return this._getContextManager().bind(context, target);
	};
	ContextAPI.prototype._getContextManager = function() {
		return getGlobal(API_NAME$1) || NOOP_CONTEXT_MANAGER;
	};
	/** Disable and remove the global context manager */
	ContextAPI.prototype.disable = function() {
		this._getContextManager().disable();
		unregisterGlobal(API_NAME$1, DiagAPI.instance());
	};
	return ContextAPI;
}();
var TraceFlags;
(function(TraceFlags) {
	/** Represents no flag set. */
	TraceFlags[TraceFlags["NONE"] = 0] = "NONE";
	/** Bit to represent whether trace is sampled in trace flags. */
	TraceFlags[TraceFlags["SAMPLED"] = 1] = "SAMPLED";
})(TraceFlags || (TraceFlags = {}));
var INVALID_SPAN_CONTEXT = {
	traceId: "00000000000000000000000000000000",
	spanId: "0000000000000000",
	traceFlags: TraceFlags.NONE
};
/**
* The NonRecordingSpan is the default {@link Span} that is used when no Span
* implementation is available. All operations are no-op including context
* propagation.
*/
var NonRecordingSpan = function() {
	function NonRecordingSpan(_spanContext) {
		if (_spanContext === void 0) _spanContext = INVALID_SPAN_CONTEXT;
		this._spanContext = _spanContext;
	}
	NonRecordingSpan.prototype.spanContext = function() {
		return this._spanContext;
	};
	NonRecordingSpan.prototype.setAttribute = function(_key, _value) {
		return this;
	};
	NonRecordingSpan.prototype.setAttributes = function(_attributes) {
		return this;
	};
	NonRecordingSpan.prototype.addEvent = function(_name, _attributes) {
		return this;
	};
	NonRecordingSpan.prototype.addLink = function(_link) {
		return this;
	};
	NonRecordingSpan.prototype.addLinks = function(_links) {
		return this;
	};
	NonRecordingSpan.prototype.setStatus = function(_status) {
		return this;
	};
	NonRecordingSpan.prototype.updateName = function(_name) {
		return this;
	};
	NonRecordingSpan.prototype.end = function(_endTime) {};
	NonRecordingSpan.prototype.isRecording = function() {
		return false;
	};
	NonRecordingSpan.prototype.recordException = function(_exception, _time) {};
	return NonRecordingSpan;
}();
/**
* span key
*/
var SPAN_KEY = createContextKey("OpenTelemetry Context Key SPAN");
/**
* Return the span if one exists
*
* @param context context to get span from
*/
function getSpan(context) {
	return context.getValue(SPAN_KEY) || void 0;
}
/**
* Gets the span from the current context, if one exists.
*/
function getActiveSpan() {
	return getSpan(ContextAPI.getInstance().active());
}
/**
* Set the span on a context
*
* @param context context to use as parent
* @param span span to set active
*/
function setSpan(context, span) {
	return context.setValue(SPAN_KEY, span);
}
/**
* Remove current span stored in the context
*
* @param context context to delete span from
*/
function deleteSpan(context) {
	return context.deleteValue(SPAN_KEY);
}
/**
* Wrap span context in a NoopSpan and set as span in a new
* context
*
* @param context context to set active span on
* @param spanContext span context to be wrapped
*/
function setSpanContext(context, spanContext) {
	return setSpan(context, new NonRecordingSpan(spanContext));
}
/**
* Get the span context of the span if it exists.
*
* @param context context to get values from
*/
function getSpanContext(context) {
	var _a;
	return (_a = getSpan(context)) === null || _a === void 0 ? void 0 : _a.spanContext();
}
var VALID_TRACEID_REGEX = /^([0-9a-f]{32})$/i;
var VALID_SPANID_REGEX = /^[0-9a-f]{16}$/i;
function isValidTraceId(traceId) {
	return VALID_TRACEID_REGEX.test(traceId) && traceId !== "00000000000000000000000000000000";
}
function isValidSpanId(spanId) {
	return VALID_SPANID_REGEX.test(spanId) && spanId !== "0000000000000000";
}
/**
* Returns true if this {@link SpanContext} is valid.
* @return true if this {@link SpanContext} is valid.
*/
function isSpanContextValid(spanContext) {
	return isValidTraceId(spanContext.traceId) && isValidSpanId(spanContext.spanId);
}
/**
* Wrap the given {@link SpanContext} in a new non-recording {@link Span}
*
* @param spanContext span context to be wrapped
* @returns a new non-recording {@link Span} with the provided context
*/
function wrapSpanContext(spanContext) {
	return new NonRecordingSpan(spanContext);
}
var contextApi = ContextAPI.getInstance();
/**
* No-op implementations of {@link Tracer}.
*/
var NoopTracer = function() {
	function NoopTracer() {}
	NoopTracer.prototype.startSpan = function(name, options, context) {
		if (context === void 0) context = contextApi.active();
		if (Boolean(options === null || options === void 0 ? void 0 : options.root)) return new NonRecordingSpan();
		var parentFromContext = context && getSpanContext(context);
		if (isSpanContext(parentFromContext) && isSpanContextValid(parentFromContext)) return new NonRecordingSpan(parentFromContext);
		else return new NonRecordingSpan();
	};
	NoopTracer.prototype.startActiveSpan = function(name, arg2, arg3, arg4) {
		var opts;
		var ctx;
		var fn;
		if (arguments.length < 2) return;
		else if (arguments.length === 2) fn = arg2;
		else if (arguments.length === 3) {
			opts = arg2;
			fn = arg3;
		} else {
			opts = arg2;
			ctx = arg3;
			fn = arg4;
		}
		var parentContext = ctx !== null && ctx !== void 0 ? ctx : contextApi.active();
		var span = this.startSpan(name, opts, parentContext);
		var contextWithSpanSet = setSpan(parentContext, span);
		return contextApi.with(contextWithSpanSet, fn, void 0, span);
	};
	return NoopTracer;
}();
function isSpanContext(spanContext) {
	return typeof spanContext === "object" && typeof spanContext["spanId"] === "string" && typeof spanContext["traceId"] === "string" && typeof spanContext["traceFlags"] === "number";
}
var NOOP_TRACER = new NoopTracer();
/**
* Proxy tracer provided by the proxy tracer provider
*/
var ProxyTracer = function() {
	function ProxyTracer(_provider, name, version, options) {
		this._provider = _provider;
		this.name = name;
		this.version = version;
		this.options = options;
	}
	ProxyTracer.prototype.startSpan = function(name, options, context) {
		return this._getTracer().startSpan(name, options, context);
	};
	ProxyTracer.prototype.startActiveSpan = function(_name, _options, _context, _fn) {
		var tracer = this._getTracer();
		return Reflect.apply(tracer.startActiveSpan, tracer, arguments);
	};
	/**
	* Try to get a tracer from the proxy tracer provider.
	* If the proxy tracer provider has no delegate, return a noop tracer.
	*/
	ProxyTracer.prototype._getTracer = function() {
		if (this._delegate) return this._delegate;
		var tracer = this._provider.getDelegateTracer(this.name, this.version, this.options);
		if (!tracer) return NOOP_TRACER;
		this._delegate = tracer;
		return this._delegate;
	};
	return ProxyTracer;
}();
var NOOP_TRACER_PROVIDER = new (function() {
	function NoopTracerProvider() {}
	NoopTracerProvider.prototype.getTracer = function(_name, _version, _options) {
		return new NoopTracer();
	};
	return NoopTracerProvider;
}())();
/**
* Tracer provider which provides {@link ProxyTracer}s.
*
* Before a delegate is set, tracers provided are NoOp.
*   When a delegate is set, traces are provided from the delegate.
*   When a delegate is set after tracers have already been provided,
*   all tracers already provided will use the provided delegate implementation.
*/
var ProxyTracerProvider = function() {
	function ProxyTracerProvider() {}
	/**
	* Get a {@link ProxyTracer}
	*/
	ProxyTracerProvider.prototype.getTracer = function(name, version, options) {
		var _a;
		return (_a = this.getDelegateTracer(name, version, options)) !== null && _a !== void 0 ? _a : new ProxyTracer(this, name, version, options);
	};
	ProxyTracerProvider.prototype.getDelegate = function() {
		var _a;
		return (_a = this._delegate) !== null && _a !== void 0 ? _a : NOOP_TRACER_PROVIDER;
	};
	/**
	* Set the delegate tracer provider
	*/
	ProxyTracerProvider.prototype.setDelegate = function(delegate) {
		this._delegate = delegate;
	};
	ProxyTracerProvider.prototype.getDelegateTracer = function(name, version, options) {
		var _a;
		return (_a = this._delegate) === null || _a === void 0 ? void 0 : _a.getTracer(name, version, options);
	};
	return ProxyTracerProvider;
}();
/**
* An enumeration of status codes.
*/
var SpanStatusCode;
(function(SpanStatusCode) {
	/**
	* The default status.
	*/
	SpanStatusCode[SpanStatusCode["UNSET"] = 0] = "UNSET";
	/**
	* The operation has been validated by an Application developer or
	* Operator to have completed successfully.
	*/
	SpanStatusCode[SpanStatusCode["OK"] = 1] = "OK";
	/**
	* The operation contains an error.
	*/
	SpanStatusCode[SpanStatusCode["ERROR"] = 2] = "ERROR";
})(SpanStatusCode || (SpanStatusCode = {}));
var API_NAME = "trace";
(function() {
	/** Empty private constructor prevents end users from constructing a new instance of the API */
	function TraceAPI() {
		this._proxyTracerProvider = new ProxyTracerProvider();
		this.wrapSpanContext = wrapSpanContext;
		this.isSpanContextValid = isSpanContextValid;
		this.deleteSpan = deleteSpan;
		this.getSpan = getSpan;
		this.getActiveSpan = getActiveSpan;
		this.getSpanContext = getSpanContext;
		this.setSpan = setSpan;
		this.setSpanContext = setSpanContext;
	}
	/** Get the singleton instance of the Trace API */
	TraceAPI.getInstance = function() {
		if (!this._instance) this._instance = new TraceAPI();
		return this._instance;
	};
	/**
	* Set the current global tracer.
	*
	* @returns true if the tracer provider was successfully registered, else false
	*/
	TraceAPI.prototype.setGlobalTracerProvider = function(provider) {
		var success = registerGlobal(API_NAME, this._proxyTracerProvider, DiagAPI.instance());
		if (success) this._proxyTracerProvider.setDelegate(provider);
		return success;
	};
	/**
	* Returns the global tracer provider.
	*/
	TraceAPI.prototype.getTracerProvider = function() {
		return getGlobal(API_NAME) || this._proxyTracerProvider;
	};
	/**
	* Returns a tracer from the global tracer provider.
	*/
	TraceAPI.prototype.getTracer = function(name, version) {
		return this.getTracerProvider().getTracer(name, version);
	};
	/** Remove the global tracer provider */
	TraceAPI.prototype.disable = function() {
		unregisterGlobal(API_NAME, DiagAPI.instance());
		this._proxyTracerProvider = new ProxyTracerProvider();
	};
	return TraceAPI;
})().getInstance();
var __defProp = Object.defineProperty;
var __export = (target, all) => {
	for (var name16 in all) __defProp(target, name16, {
		get: all[name16],
		enumerable: true
	});
};
var name6 = "AI_NoObjectGeneratedError";
var marker6 = `vercel.ai.error.${name6}`;
var symbol6 = Symbol.for(marker6);
var _a6;
var NoObjectGeneratedError = class extends AISDKError {
	constructor({ message = "No object generated.", cause, text: text2, response, usage, finishReason }) {
		super({
			name: name6,
			message,
			cause
		});
		this[_a6] = true;
		this.text = text2;
		this.response = response;
		this.usage = usage;
		this.finishReason = finishReason;
	}
	static isInstance(error) {
		return AISDKError.hasMarker(error, marker6);
	}
};
_a6 = symbol6;
var dataContentSchema = z$1.union([
	z$1.string(),
	z$1.instanceof(Uint8Array),
	z$1.instanceof(ArrayBuffer),
	z$1.custom((value) => {
		var _a16, _b;
		return (_b = (_a16 = globalThis.Buffer) == null ? void 0 : _a16.isBuffer(value)) != null ? _b : false;
	}, { message: "Must be a Buffer" })
]);
var jsonValueSchema = z$1.lazy(() => z$1.union([
	z$1.null(),
	z$1.string(),
	z$1.number(),
	z$1.boolean(),
	z$1.record(z$1.string(), jsonValueSchema),
	z$1.array(jsonValueSchema)
]));
var providerMetadataSchema = z$1.record(z$1.string(), z$1.record(z$1.string(), jsonValueSchema));
var textPartSchema = z$1.object({
	type: z$1.literal("text"),
	text: z$1.string(),
	providerOptions: providerMetadataSchema.optional()
});
var imagePartSchema = z$1.object({
	type: z$1.literal("image"),
	image: z$1.union([dataContentSchema, z$1.instanceof(URL)]),
	mediaType: z$1.string().optional(),
	providerOptions: providerMetadataSchema.optional()
});
var filePartSchema = z$1.object({
	type: z$1.literal("file"),
	data: z$1.union([dataContentSchema, z$1.instanceof(URL)]),
	filename: z$1.string().optional(),
	mediaType: z$1.string(),
	providerOptions: providerMetadataSchema.optional()
});
var reasoningPartSchema = z$1.object({
	type: z$1.literal("reasoning"),
	text: z$1.string(),
	providerOptions: providerMetadataSchema.optional()
});
var toolCallPartSchema = z$1.object({
	type: z$1.literal("tool-call"),
	toolCallId: z$1.string(),
	toolName: z$1.string(),
	input: z$1.unknown(),
	providerOptions: providerMetadataSchema.optional(),
	providerExecuted: z$1.boolean().optional()
});
var outputSchema = z$1.discriminatedUnion("type", [
	z$1.object({
		type: z$1.literal("text"),
		value: z$1.string()
	}),
	z$1.object({
		type: z$1.literal("json"),
		value: jsonValueSchema
	}),
	z$1.object({
		type: z$1.literal("error-text"),
		value: z$1.string()
	}),
	z$1.object({
		type: z$1.literal("error-json"),
		value: jsonValueSchema
	}),
	z$1.object({
		type: z$1.literal("content"),
		value: z$1.array(z$1.union([z$1.object({
			type: z$1.literal("text"),
			text: z$1.string()
		}), z$1.object({
			type: z$1.literal("media"),
			data: z$1.string(),
			mediaType: z$1.string()
		})]))
	})
]);
var toolResultPartSchema = z$1.object({
	type: z$1.literal("tool-result"),
	toolCallId: z$1.string(),
	toolName: z$1.string(),
	output: outputSchema,
	providerOptions: providerMetadataSchema.optional()
});
var systemModelMessageSchema = z$1.object({
	role: z$1.literal("system"),
	content: z$1.string(),
	providerOptions: providerMetadataSchema.optional()
});
var userModelMessageSchema = z$1.object({
	role: z$1.literal("user"),
	content: z$1.union([z$1.string(), z$1.array(z$1.union([
		textPartSchema,
		imagePartSchema,
		filePartSchema
	]))]),
	providerOptions: providerMetadataSchema.optional()
});
var assistantModelMessageSchema = z$1.object({
	role: z$1.literal("assistant"),
	content: z$1.union([z$1.string(), z$1.array(z$1.union([
		textPartSchema,
		filePartSchema,
		reasoningPartSchema,
		toolCallPartSchema,
		toolResultPartSchema
	]))]),
	providerOptions: providerMetadataSchema.optional()
});
var toolModelMessageSchema = z$1.object({
	role: z$1.literal("tool"),
	content: z$1.array(toolResultPartSchema),
	providerOptions: providerMetadataSchema.optional()
});
z$1.union([
	systemModelMessageSchema,
	userModelMessageSchema,
	assistantModelMessageSchema,
	toolModelMessageSchema
]);
function stepCountIs(stepCount) {
	return ({ steps }) => steps.length === stepCount;
}
createIdGenerator({
	prefix: "aitxt",
	size: 24
});
TransformStream;
function fixJson(input) {
	const stack = ["ROOT"];
	let lastValidIndex = -1;
	let literalStart = null;
	function processValueStart(char, i, swapState) {
		switch (char) {
			case "\"":
				lastValidIndex = i;
				stack.pop();
				stack.push(swapState);
				stack.push("INSIDE_STRING");
				break;
			case "f":
			case "t":
			case "n":
				lastValidIndex = i;
				literalStart = i;
				stack.pop();
				stack.push(swapState);
				stack.push("INSIDE_LITERAL");
				break;
			case "-":
				stack.pop();
				stack.push(swapState);
				stack.push("INSIDE_NUMBER");
				break;
			case "0":
			case "1":
			case "2":
			case "3":
			case "4":
			case "5":
			case "6":
			case "7":
			case "8":
			case "9":
				lastValidIndex = i;
				stack.pop();
				stack.push(swapState);
				stack.push("INSIDE_NUMBER");
				break;
			case "{":
				lastValidIndex = i;
				stack.pop();
				stack.push(swapState);
				stack.push("INSIDE_OBJECT_START");
				break;
			case "[":
				lastValidIndex = i;
				stack.pop();
				stack.push(swapState);
				stack.push("INSIDE_ARRAY_START");
				break;
		}
	}
	function processAfterObjectValue(char, i) {
		switch (char) {
			case ",":
				stack.pop();
				stack.push("INSIDE_OBJECT_AFTER_COMMA");
				break;
			case "}":
				lastValidIndex = i;
				stack.pop();
				break;
		}
	}
	function processAfterArrayValue(char, i) {
		switch (char) {
			case ",":
				stack.pop();
				stack.push("INSIDE_ARRAY_AFTER_COMMA");
				break;
			case "]":
				lastValidIndex = i;
				stack.pop();
				break;
		}
	}
	for (let i = 0; i < input.length; i++) {
		const char = input[i];
		switch (stack[stack.length - 1]) {
			case "ROOT":
				processValueStart(char, i, "FINISH");
				break;
			case "INSIDE_OBJECT_START":
				switch (char) {
					case "\"":
						stack.pop();
						stack.push("INSIDE_OBJECT_KEY");
						break;
					case "}":
						lastValidIndex = i;
						stack.pop();
						break;
				}
				break;
			case "INSIDE_OBJECT_AFTER_COMMA":
				switch (char) {
					case "\"":
						stack.pop();
						stack.push("INSIDE_OBJECT_KEY");
						break;
				}
				break;
			case "INSIDE_OBJECT_KEY":
				switch (char) {
					case "\"":
						stack.pop();
						stack.push("INSIDE_OBJECT_AFTER_KEY");
						break;
				}
				break;
			case "INSIDE_OBJECT_AFTER_KEY":
				switch (char) {
					case ":":
						stack.pop();
						stack.push("INSIDE_OBJECT_BEFORE_VALUE");
						break;
				}
				break;
			case "INSIDE_OBJECT_BEFORE_VALUE":
				processValueStart(char, i, "INSIDE_OBJECT_AFTER_VALUE");
				break;
			case "INSIDE_OBJECT_AFTER_VALUE":
				processAfterObjectValue(char, i);
				break;
			case "INSIDE_STRING":
				switch (char) {
					case "\"":
						stack.pop();
						lastValidIndex = i;
						break;
					case "\\":
						stack.push("INSIDE_STRING_ESCAPE");
						break;
					default: lastValidIndex = i;
				}
				break;
			case "INSIDE_ARRAY_START":
				switch (char) {
					case "]":
						lastValidIndex = i;
						stack.pop();
						break;
					default:
						lastValidIndex = i;
						processValueStart(char, i, "INSIDE_ARRAY_AFTER_VALUE");
						break;
				}
				break;
			case "INSIDE_ARRAY_AFTER_VALUE":
				switch (char) {
					case ",":
						stack.pop();
						stack.push("INSIDE_ARRAY_AFTER_COMMA");
						break;
					case "]":
						lastValidIndex = i;
						stack.pop();
						break;
					default:
						lastValidIndex = i;
						break;
				}
				break;
			case "INSIDE_ARRAY_AFTER_COMMA":
				processValueStart(char, i, "INSIDE_ARRAY_AFTER_VALUE");
				break;
			case "INSIDE_STRING_ESCAPE":
				stack.pop();
				lastValidIndex = i;
				break;
			case "INSIDE_NUMBER":
				switch (char) {
					case "0":
					case "1":
					case "2":
					case "3":
					case "4":
					case "5":
					case "6":
					case "7":
					case "8":
					case "9":
						lastValidIndex = i;
						break;
					case "e":
					case "E":
					case "-":
					case ".": break;
					case ",":
						stack.pop();
						if (stack[stack.length - 1] === "INSIDE_ARRAY_AFTER_VALUE") processAfterArrayValue(char, i);
						if (stack[stack.length - 1] === "INSIDE_OBJECT_AFTER_VALUE") processAfterObjectValue(char, i);
						break;
					case "}":
						stack.pop();
						if (stack[stack.length - 1] === "INSIDE_OBJECT_AFTER_VALUE") processAfterObjectValue(char, i);
						break;
					case "]":
						stack.pop();
						if (stack[stack.length - 1] === "INSIDE_ARRAY_AFTER_VALUE") processAfterArrayValue(char, i);
						break;
					default:
						stack.pop();
						break;
				}
				break;
			case "INSIDE_LITERAL": {
				const partialLiteral = input.substring(literalStart, i + 1);
				if (!"false".startsWith(partialLiteral) && !"true".startsWith(partialLiteral) && !"null".startsWith(partialLiteral)) {
					stack.pop();
					if (stack[stack.length - 1] === "INSIDE_OBJECT_AFTER_VALUE") processAfterObjectValue(char, i);
					else if (stack[stack.length - 1] === "INSIDE_ARRAY_AFTER_VALUE") processAfterArrayValue(char, i);
				} else lastValidIndex = i;
				break;
			}
		}
	}
	let result = input.slice(0, lastValidIndex + 1);
	for (let i = stack.length - 1; i >= 0; i--) switch (stack[i]) {
		case "INSIDE_STRING":
			result += "\"";
			break;
		case "INSIDE_OBJECT_KEY":
		case "INSIDE_OBJECT_AFTER_KEY":
		case "INSIDE_OBJECT_AFTER_COMMA":
		case "INSIDE_OBJECT_START":
		case "INSIDE_OBJECT_BEFORE_VALUE":
		case "INSIDE_OBJECT_AFTER_VALUE":
			result += "}";
			break;
		case "INSIDE_ARRAY_START":
		case "INSIDE_ARRAY_AFTER_COMMA":
		case "INSIDE_ARRAY_AFTER_VALUE":
			result += "]";
			break;
		case "INSIDE_LITERAL": {
			const partialLiteral = input.substring(literalStart, input.length);
			if ("true".startsWith(partialLiteral)) result += "true".slice(partialLiteral.length);
			else if ("false".startsWith(partialLiteral)) result += "false".slice(partialLiteral.length);
			else if ("null".startsWith(partialLiteral)) result += "null".slice(partialLiteral.length);
		}
	}
	return result;
}
async function parsePartialJson(jsonText) {
	if (jsonText === void 0) return {
		value: void 0,
		state: "undefined-input"
	};
	let result = await safeParseJSON({ text: jsonText });
	if (result.success) return {
		value: result.value,
		state: "successful-parse"
	};
	result = await safeParseJSON({ text: fixJson(jsonText) });
	if (result.success) return {
		value: result.value,
		state: "repaired-parse"
	};
	return {
		value: void 0,
		state: "failed-parse"
	};
}
createIdGenerator({
	prefix: "aitxt",
	size: 24
});
createIdGenerator({
	prefix: "aiobj",
	size: 24
});
createIdGenerator({
	prefix: "aiobj",
	size: 24
});
__export({}, {
	object: () => object,
	text: () => text
});
var text = () => ({
	type: "text",
	responseFormat: { type: "text" },
	async parsePartial({ text: text2 }) {
		return { partial: text2 };
	},
	async parseOutput({ text: text2 }) {
		return text2;
	}
});
var object = ({ schema: inputSchema }) => {
	const schema = asSchema(inputSchema);
	return {
		type: "object",
		responseFormat: {
			type: "json",
			schema: schema.jsonSchema
		},
		async parsePartial({ text: text2 }) {
			const result = await parsePartialJson(text2);
			switch (result.state) {
				case "failed-parse":
				case "undefined-input": return;
				case "repaired-parse":
				case "successful-parse": return { partial: result.value };
				default: {
					const _exhaustiveCheck = result.state;
					throw new Error(`Unsupported parse state: ${_exhaustiveCheck}`);
				}
			}
		},
		async parseOutput({ text: text2 }, context) {
			const parseResult = await safeParseJSON({ text: text2 });
			if (!parseResult.success) throw new NoObjectGeneratedError({
				message: "No object generated: could not parse the response.",
				cause: parseResult.error,
				text: text2,
				response: context.response,
				usage: context.usage,
				finishReason: context.finishReason
			});
			const validationResult = await safeValidateTypes({
				value: parseResult.value,
				schema
			});
			if (!validationResult.success) throw new NoObjectGeneratedError({
				message: "No object generated: response did not match schema.",
				cause: validationResult.error,
				text: text2,
				response: context.response,
				usage: context.usage,
				finishReason: context.finishReason
			});
			return validationResult.value;
		}
	};
};
//#endregion
//#region src/workflows/shared/schema.ts
const TaskSchema = z.array(z.object({
	id: z.string().describe("Unique task ID using kebab-case"),
	content: z.string().describe("Specific, actionable task description"),
	status: z.enum([
		"pending",
		"in_progress",
		"completed",
		"blocked"
	]).default("pending"),
	priority: z.enum([
		"high",
		"medium",
		"low"
	]).describe("Task priority"),
	dependencies: z.array(z.string()).optional().describe("IDs of tasks this depends on"),
	notes: z.string().describe("Detailed implementation notes and specifics")
}));
const QuestionSchema = z.array(z.object({
	id: z.string().describe("Unique question ID"),
	question: z.string().describe("Clear, specific question for the user"),
	type: z.enum([
		"choice",
		"text",
		"boolean"
	]).describe("Type of answer expected"),
	options: z.array(z.string()).optional().describe("Options for choice questions"),
	context: z.string().optional().describe("Additional context or explanation")
}));
const PlanningIterationResultSchema = z.object({
	success: z.boolean(),
	tasks: TaskSchema,
	questions: QuestionSchema,
	reasoning: z.string(),
	planComplete: z.boolean(),
	message: z.string(),
	error: z.string().optional(),
	allPreviousQuestions: z.array(z.any()).optional(),
	allPreviousAnswers: z.record(z.string(), z.string()).optional()
});
//#endregion
//#region src/workflows/task-planning/prompts.ts
const taskPlanningPrompts = {
	planningAgent: {
		instructions: (context) => `You are a Mastra workflow planning expert. Your task is to create a detailed, executable task plan.

PLANNING RESPONSIBILITIES:
1. **Analyze Requirements**: Review the user's description and requirements thoroughly
2. **Identify Decision Points**: Find any choices that require user input (email providers, databases, APIs, etc.)
3. **Create Specific Tasks**: Generate concrete, actionable tasks with clear implementation notes
4. **Ask Clarifying Questions**: If any decisions are unclear, formulate specific questions for the user 
- do not ask about package managers
- Assume the user is going to use zod for validation
- You do not need to ask questions if you have none
- NEVER ask questions that have already been answered before
5. **Incorporate Feedback**: Use any previous answers or feedback to refine the plan

${context.storedQAPairs.length > 0 ? `PREVIOUS QUESTION-ANSWER PAIRS (${context.storedQAPairs.length} total):\n${context.storedQAPairs.map((pair, index) => `${index + 1}. Q: ${pair.question.question}\n   A: ${pair.answer || "NOT ANSWERED YET"}\n   Type: ${pair.question.type}\n   Asked: ${pair.askedAt}\n   ${pair.answer ? `Answered: ${pair.answeredAt}` : ""}`).join("\n\n")}\n\nIMPORTANT: DO NOT ASK ANY QUESTIONS THAT HAVE ALREADY BEEN ASKED!` : ""}

Based on the context and any user answers, create or refine the task plan.`,
		refinementPrompt: (context) => `Refine the existing task plan based on all user answers collected so far. 

ANSWERED QUESTIONS AND RESPONSES:
${context.storedQAPairs.filter((pair) => pair.answer).map((pair, index) => `${index + 1}. Q: ${pair.question.question}\n   A: ${pair.answer}\n   Context: ${pair.question.context || "None"}`).join("\n\n")}

REQUIREMENTS:
- Action: ${context.action}
- Workflow Name: ${context.workflowName || "To be determined"}
- Description: ${context.description || "Not specified"}
- Requirements: ${context.requirements || "Not specified"}

PROJECT CONTEXT:
- Discovered Workflows: ${JSON.stringify(context.discoveredWorkflows, null, 2)}
- Project Structure: ${JSON.stringify(context.projectStructure, null, 2)}
- Research: ${JSON.stringify(context.research, null, 2)}

${context.hasTaskFeedback ? `\nUSER FEEDBACK ON PREVIOUS TASK LIST:\n${context.userAnswers?.taskFeedback}\n\nPLEASE INCORPORATE THIS FEEDBACK INTO THE REFINED TASK LIST.` : ""}

Refine the task list and determine if any additional questions are needed.`,
		initialPrompt: (context) => `Create an initial task plan for ${context.action}ing a Mastra workflow.

REQUIREMENTS:
- Action: ${context.action}
- Workflow Name: ${context.workflowName || "To be determined"}
- Description: ${context.description || "Not specified"}  
- Requirements: ${context.requirements || "Not specified"}

PROJECT CONTEXT:
- Discovered Workflows: ${JSON.stringify(context.discoveredWorkflows, null, 2)}
- Project Structure: ${JSON.stringify(context.projectStructure, null, 2)}
- Research: ${JSON.stringify(context.research, null, 2)}

Create specific tasks and identify any questions that need user clarification.`
	},
	taskApproval: {
		message: (questionsCount) => `Please answer ${questionsCount} question(s) to finalize the workflow plan:`,
		approvalMessage: (tasksCount) => `Please review and approve the ${tasksCount} task(s) for execution:`
	}
};
//#endregion
//#region src/workflows/workflow-builder/schema.ts
const WorkflowBuilderInputSchema = z.object({
	workflowName: z.string().optional().describe("Name of the workflow to create or edit"),
	action: z.enum(["create", "edit"]).describe("Action to perform: create new or edit existing workflow"),
	description: z.string().optional().describe("Description of what the workflow should do"),
	requirements: z.string().optional().describe("Detailed requirements for the workflow"),
	projectPath: z.string().optional().describe("Path to the Mastra project (defaults to current directory)")
});
const DiscoveredWorkflowSchema = z.object({
	name: z.string(),
	file: z.string(),
	description: z.string().optional(),
	inputSchema: z.any().optional(),
	outputSchema: z.any().optional(),
	steps: z.array(z.string()).optional()
});
const WorkflowDiscoveryResultSchema = z.object({
	success: z.boolean(),
	workflows: z.array(DiscoveredWorkflowSchema),
	mastraIndexExists: z.boolean(),
	message: z.string(),
	error: z.string().optional()
});
const ProjectDiscoveryResultSchema = z.object({
	success: z.boolean(),
	structure: z.object({
		hasWorkflowsDir: z.boolean(),
		hasAgentsDir: z.boolean(),
		hasToolsDir: z.boolean(),
		hasMastraIndex: z.boolean(),
		existingWorkflows: z.array(z.string()),
		existingAgents: z.array(z.string()),
		existingTools: z.array(z.string())
	}),
	dependencies: z.record(z.string(), z.string()),
	message: z.string(),
	error: z.string().optional()
});
const WorkflowResearchResultSchema = z.object({
	success: z.boolean(),
	documentation: z.object({
		workflowPatterns: z.array(z.string()),
		stepExamples: z.array(z.string()),
		bestPractices: z.array(z.string())
	}),
	webResources: z.array(z.object({
		title: z.string(),
		url: z.string(),
		snippet: z.string(),
		relevance: z.number()
	})),
	message: z.string(),
	error: z.string().optional()
});
const TaskManagementResultSchema = z.object({
	success: z.boolean(),
	tasks: TaskSchema,
	message: z.string(),
	error: z.string().optional()
});
const TaskExecutionInputSchema = z.object({
	action: z.enum(["create", "edit"]),
	workflowName: z.string().optional(),
	description: z.string().optional(),
	requirements: z.string().optional(),
	tasks: TaskSchema,
	discoveredWorkflows: z.array(z.any()),
	projectStructure: z.any(),
	research: z.any(),
	projectPath: z.string().optional()
});
const TaskExecutionSuspendSchema = z.object({
	questions: QuestionSchema,
	currentProgress: z.string(),
	completedTasks: z.array(z.string()),
	message: z.string()
});
const TaskExecutionResumeSchema = z.object({ answers: z.array(z.object({
	questionId: z.string(),
	answer: z.string()
})) });
const TaskExecutionResultSchema = z.object({
	success: z.boolean(),
	filesModified: z.array(z.string()),
	validationResults: z.object({
		passed: z.boolean(),
		errors: z.array(z.string()),
		warnings: z.array(z.string())
	}),
	completedTasks: z.array(z.string()),
	message: z.string(),
	error: z.string().optional()
});
z.object({ questions: QuestionSchema });
z.object({
	answers: z.record(z.string(), z.string()),
	hasAnswers: z.boolean()
});
const WorkflowBuilderResultSchema = z.object({
	success: z.boolean(),
	action: z.enum(["create", "edit"]),
	workflowName: z.string().optional(),
	workflowFile: z.string().optional(),
	discovery: WorkflowDiscoveryResultSchema.optional(),
	projectStructure: ProjectDiscoveryResultSchema.optional(),
	research: WorkflowResearchResultSchema.optional(),
	planning: PlanningIterationResultSchema.optional(),
	taskManagement: TaskManagementResultSchema.optional(),
	execution: TaskExecutionResultSchema.optional(),
	needsUserInput: z.boolean().optional(),
	questions: QuestionSchema.optional(),
	message: z.string(),
	nextSteps: z.array(z.string()).optional(),
	error: z.string().optional()
});
const TaskExecutionIterationInputSchema = (taskLength) => z.object({
	status: z.enum([
		"in_progress",
		"completed",
		"needs_clarification"
	]).describe("Status - only use \"completed\" when ALL remaining tasks are finished"),
	progress: z.string().describe("Current progress description"),
	completedTasks: z.array(z.string()).describe("List of ALL completed task IDs (including previously completed ones)"),
	totalTasksRequired: z.number().describe(`Total number of tasks that must be completed (should be ${taskLength})`),
	tasksRemaining: z.array(z.string()).describe("List of task IDs that still need to be completed"),
	filesModified: z.array(z.string()).describe("List of files that were created or modified - use these exact paths for validateCode tool"),
	questions: QuestionSchema.optional().describe("Questions for user if clarification is needed"),
	message: z.string().describe("Summary of work completed or current status"),
	error: z.string().optional().describe("Any errors encountered")
});
//#endregion
//#region src/workflows/task-planning/schema.ts
const PlanningIterationInputSchema = z.object({
	action: z.enum(["create", "edit"]),
	workflowName: z.string().optional(),
	description: z.string().optional(),
	requirements: z.string().optional(),
	discoveredWorkflows: z.array(DiscoveredWorkflowSchema),
	projectStructure: ProjectDiscoveryResultSchema,
	research: WorkflowResearchResultSchema,
	userAnswers: z.record(z.string(), z.string()).optional()
});
const PlanningIterationSuspendSchema = z.object({
	questions: QuestionSchema,
	message: z.string(),
	currentPlan: z.object({
		tasks: TaskSchema,
		reasoning: z.string()
	})
});
const PlanningIterationResumeSchema = z.object({ answers: z.record(z.string(), z.string()) });
const PlanningAgentOutputSchema = z.object({
	tasks: TaskSchema,
	questions: QuestionSchema.optional(),
	reasoning: z.string().describe("Explanation of the plan and any questions"),
	planComplete: z.boolean().describe("Whether the plan is ready for execution (no more questions)")
});
const TaskApprovalOutputSchema = z.object({
	approved: z.boolean(),
	tasks: TaskSchema,
	message: z.string(),
	userFeedback: z.string().optional()
});
const TaskApprovalSuspendSchema = z.object({
	taskList: TaskSchema,
	summary: z.string(),
	message: z.string()
});
const TaskApprovalResumeSchema = z.object({
	approved: z.boolean(),
	modifications: z.string().optional()
});
//#endregion
//#region src/workflows/task-planning/task-planning.ts
const planningIterationStep = createStep({
	id: "planning-iteration",
	description: "Create or refine task plan with user input",
	inputSchema: PlanningIterationInputSchema,
	outputSchema: PlanningIterationResultSchema,
	suspendSchema: PlanningIterationSuspendSchema,
	resumeSchema: PlanningIterationResumeSchema,
	execute: async ({ inputData, resumeData, suspend, requestContext }) => {
		const { action, workflowName, description, requirements, discoveredWorkflows, projectStructure, research, userAnswers } = inputData;
		console.info("Starting planning iteration...");
		const qaKey = "workflow-builder-qa";
		let storedQAPairs = requestContext.get(qaKey) || [];
		const newAnswers = {
			...userAnswers || {},
			...resumeData?.answers || {}
		};
		if (Object.keys(newAnswers).length > 0) {
			storedQAPairs = storedQAPairs.map((pair) => {
				const answerValue = newAnswers[pair.question.id];
				if (answerValue) return {
					...pair,
					answer: String(answerValue) || null,
					answeredAt: (/* @__PURE__ */ new Date()).toISOString()
				};
				return pair;
			});
			requestContext.set(qaKey, storedQAPairs);
		}
		try {
			const planningAgent = new Agent({
				id: "workflow-planning-agent",
				model: await resolveModel({ requestContext }),
				instructions: taskPlanningPrompts.planningAgent.instructions({ storedQAPairs }),
				name: "Workflow Planning Agent"
			});
			const hasTaskFeedback = Boolean(userAnswers && userAnswers.taskFeedback);
			const planningPrompt = storedQAPairs.some((pair) => pair.answer) ? taskPlanningPrompts.planningAgent.refinementPrompt({
				action,
				workflowName,
				description,
				requirements,
				discoveredWorkflows,
				projectStructure,
				research,
				storedQAPairs,
				hasTaskFeedback,
				userAnswers
			}) : taskPlanningPrompts.planningAgent.initialPrompt({
				action,
				workflowName,
				description,
				requirements,
				discoveredWorkflows,
				projectStructure,
				research
			});
			const planResult = await (await planningAgent.generate(planningPrompt, { structuredOutput: { schema: PlanningAgentOutputSchema } })).object;
			if (!planResult) return {
				tasks: [],
				success: false,
				questions: [],
				reasoning: "Planning agent failed to generate a valid response",
				planComplete: false,
				message: "Planning failed"
			};
			if (planResult.questions && planResult.questions.length > 0 && !planResult.planComplete) {
				console.info(`Planning needs user clarification: ${planResult.questions.length} questions`);
				console.info(planResult.questions);
				const newQAPairs = planResult.questions.map((question) => ({
					question,
					answer: null,
					askedAt: (/* @__PURE__ */ new Date()).toISOString(),
					answeredAt: null
				}));
				storedQAPairs = [...storedQAPairs, ...newQAPairs];
				requestContext.set(qaKey, storedQAPairs);
				console.info(`Updated Q&A state: ${storedQAPairs.length} total question-answer pairs, ${storedQAPairs.filter((p) => p.answer).length} answered`);
				return suspend({
					questions: planResult.questions,
					message: taskPlanningPrompts.taskApproval.message(planResult.questions.length),
					currentPlan: {
						tasks: planResult.tasks,
						reasoning: planResult.reasoning
					}
				});
			}
			console.info(`Planning complete with ${planResult.tasks.length} tasks`);
			requestContext.set(qaKey, storedQAPairs);
			console.info(`Final Q&A state: ${storedQAPairs.length} total question-answer pairs, ${storedQAPairs.filter((p) => p.answer).length} answered`);
			return {
				tasks: planResult.tasks,
				success: true,
				questions: [],
				reasoning: planResult.reasoning,
				planComplete: true,
				message: `Successfully created ${planResult.tasks.length} tasks`,
				allPreviousQuestions: storedQAPairs.map((pair) => pair.question),
				allPreviousAnswers: Object.fromEntries(storedQAPairs.filter((pair) => pair.answer).map((pair) => [pair.question.id, pair.answer]))
			};
		} catch (error) {
			console.error("Planning iteration failed:", error);
			return {
				tasks: [],
				success: false,
				questions: [],
				reasoning: `Planning failed: ${error instanceof Error ? error.message : String(error)}`,
				planComplete: false,
				message: "Planning iteration failed",
				error: error instanceof Error ? error.message : String(error),
				allPreviousQuestions: storedQAPairs.map((pair) => pair.question),
				allPreviousAnswers: Object.fromEntries(storedQAPairs.filter((pair) => pair.answer).map((pair) => [pair.question.id, pair.answer]))
			};
		}
	}
});
const taskApprovalStep = createStep({
	id: "task-approval",
	description: "Get user approval for the final task list",
	inputSchema: PlanningIterationResultSchema,
	outputSchema: TaskApprovalOutputSchema,
	suspendSchema: TaskApprovalSuspendSchema,
	resumeSchema: TaskApprovalResumeSchema,
	execute: async ({ inputData, resumeData, suspend }) => {
		const { tasks } = inputData;
		if (!resumeData?.approved && resumeData?.approved !== false) {
			console.info(`Requesting user approval for ${tasks.length} tasks`);
			return suspend({
				taskList: tasks,
				summary: `Task List for Approval:

${tasks.length} tasks planned:
${tasks.map((task, i) => `${i + 1}. [${task.priority.toUpperCase()}] ${task.content}${task.dependencies?.length ? ` (depends on: ${task.dependencies.join(", ")})` : ""}\n   Notes: ${task.notes || "None"}`).join("\n")}`,
				message: taskPlanningPrompts.taskApproval.approvalMessage(tasks.length)
			});
		}
		if (resumeData.approved) {
			console.info("Task list approved by user");
			return {
				approved: true,
				tasks,
				message: "Task list approved, ready for execution"
			};
		} else {
			console.info("Task list rejected by user");
			return {
				approved: false,
				tasks,
				message: "Task list rejected",
				userFeedback: resumeData.modifications
			};
		}
	}
});
const planningAndApprovalWorkflow = createWorkflow({
	id: "planning-and-approval",
	description: "Handle iterative planning with questions and task list approval",
	inputSchema: PlanningIterationInputSchema,
	outputSchema: TaskApprovalOutputSchema,
	steps: [planningIterationStep, taskApprovalStep]
}).dountil(planningIterationStep, async ({ inputData }) => {
	console.info(`Sub-workflow planning check: planComplete=${inputData.planComplete}`);
	return inputData.planComplete === true;
}).map(async ({ inputData }) => {
	return {
		tasks: inputData.tasks || [],
		success: inputData.success || false,
		questions: inputData.questions || [],
		reasoning: inputData.reasoning || "",
		planComplete: inputData.planComplete || false,
		message: inputData.message || ""
	};
}).then(taskApprovalStep).commit();
//#endregion
//#region src/workflows/workflow-builder/prompts.ts
const workflowResearch = `
## 🔍 **COMPREHENSIVE MASTRA WORKFLOW RESEARCH SUMMARY**

Based on extensive research of Mastra documentation and examples, here's essential information for building effective Mastra workflows:

### **📋 WORKFLOW FUNDAMENTALS**

**Core Components:**
- **\`createWorkflow()\`**: Main factory function that creates workflow instances
- **\`createStep()\`**: Creates individual workflow steps with typed inputs/outputs  
- **\`.commit()\`**: Finalizes workflow definition (REQUIRED to make workflows executable)
- **Zod schemas**: Used for strict input/output typing and validation

**Basic Structure:**
\`\`\`typescript
import { createWorkflow, createStep } from "@mastra/core/workflows";
import { z } from "zod";

const workflow = createWorkflow({
  id: "unique-workflow-id",           // Required: kebab-case recommended
  description: "What this workflow does", // Optional but recommended
  inputSchema: z.object({...}),       // Required: Defines workflow inputs
  outputSchema: z.object({...})       // Required: Defines final outputs
})
  .then(step1)                       // Chain steps sequentially
  .then(step2)
  .commit();                         // CRITICAL: Makes workflow executable
\`\`\`

### **🔧 STEP CREATION PATTERNS**

**Standard Step Definition:**
\`\`\`typescript
const myStep = createStep({
  id: "step-id",                     // Required: unique identifier
  description: "Step description",    // Recommended for clarity
  inputSchema: z.object({...}),       // Required: input validation
  outputSchema: z.object({...}),      // Required: output validation
  execute: async ({ inputData, mastra, getStepResult, getInitData }) => {
    // Step logic here
    return { /* matches outputSchema */ };
  }
});
\`\`\`

**Execute Function Parameters:**
- \`inputData\`: Validated input matching inputSchema
- \`mastra\`: Access to Mastra instance (agents, tools, other workflows)
- \`getStepResult(stepInstance)\`: Get results from previous steps
- \`getInitData()\`: Access original workflow input data
- \`requestContext\`: Runtime dependency injection context
- \`runCount\`: Number of times this step has run (useful for retries)

### **🔄 CONTROL FLOW METHODS**

**Sequential Execution:**
- \`.then(step)\`: Execute steps one after another
- Data flows automatically if schemas match

**Parallel Execution:**
- \`.parallel([step1, step2])\`: Run steps simultaneously
- All parallel steps complete before continuing

**Conditional Logic:**
- \`.branch([[condition, step], [condition, step]])\`: Execute different steps based on conditions
- Conditions evaluated sequentially, matching steps run in parallel

**Loops:**
- \`.dountil(step, condition)\`: Repeat until condition becomes true
- \`.dowhile(step, condition)\`: Repeat while condition is true  
- \`.foreach(step, {concurrency: N})\`: Execute step for each array item

**Data Transformation:**
- \`.map(({ inputData, getStepResult, getInitData }) => transformedData)\`: Transform data between steps

### **⏸️ SUSPEND & RESUME CAPABILITIES**

**For Human-in-the-Loop Workflows:**
\`\`\`typescript
const userInputStep = createStep({
  id: "user-input",
  suspendSchema: z.object({}),        // Schema for suspension payload
  resumeSchema: z.object({            // Schema for resume data
    userResponse: z.string()
  }),
  execute: async ({ resumeData, suspend }) => {
    if (!resumeData?.userResponse) {
      await suspend({});  // Pause workflow
      return { response: "" };
    }
    return { response: resumeData.userResponse };
  }
});
\`\`\`

**Resume Workflow:**
\`\`\`typescript
const result = await run.start({ inputData: {...} });
if (result.status === "suspended") {
  await run.resume({
    step: result.suspended[0],        // Or specific step ID
    resumeData: { userResponse: "answer" }
  });
}
\`\`\`

### **🛠️ INTEGRATING AGENTS & TOOLS**

**Using Agents in Steps:**
\`\`\`typescript
// Method 1: Agent as step
const agentStep = createStep(myAgent);

// Method 2: Call agent in execute function
const step = createStep({
  execute: async ({ inputData }) => {
    const result = await myAgent.generate(prompt);
    return { output: result.text };
  }
});
\`\`\`

**Using Tools in Steps:**
\`\`\`typescript
// Method 1: Tool as step  
const toolStep = createStep(myTool);

// Method 2: Call tool in execute function
const step = createStep({
  execute: async ({ inputData, requestContext }) => {
    const result = await myTool.execute({
      context: inputData,
      requestContext
    });
    return result;
  }
});
\`\`\`

### **🗂️ PROJECT ORGANIZATION PATTERNS**

**MANDATORY Workflow Organization:**
Each workflow MUST be organized in its own dedicated folder with separated concerns:

\`\`\`
src/mastra/workflows/
├── my-workflow-name/         # Kebab-case folder name
│   ├── types.ts             # All Zod schemas and TypeScript types
│   ├── steps.ts             # All individual step definitions
│   ├── workflow.ts          # Main workflow composition and export
│   └── utils.ts             # Helper functions (if needed)
├── another-workflow/
│   ├── types.ts
│   ├── steps.ts
│   ├── workflow.ts
│   └── utils.ts
└── index.ts                 # Export all workflows
\`\`\`

**CRITICAL File Organization Rules:**
- **ALWAYS create a dedicated folder** for each workflow
- **Folder names MUST be kebab-case** version of workflow name
- **types.ts**: Define all input/output schemas, validation types, and interfaces
- **steps.ts**: Create all individual step definitions using createStep()
- **workflow.ts**: Compose steps into workflow using createWorkflow() and export the final workflow
- **utils.ts**: Any helper functions, constants, or utilities (create only if needed)
- **NEVER put everything in one file** - always separate concerns properly

**Workflow Registration:**
\`\`\`typescript
// src/mastra/index.ts
export const mastra = new Mastra({
  workflows: {
    sendEmailWorkflow,      // Use camelCase for keys
    dataProcessingWorkflow
  },
  storage: new LibSQLStore({ id: 'mastra-storage', url: 'file:./mastra.db' }), // Required for suspend/resume
});
\`\`\`

### **📦 ESSENTIAL DEPENDENCIES**

**Required Packages:**
\`\`\`json
{
  "dependencies": {
    "@mastra/core": "latest",
    "zod": "^3.25.67"
  }
}
\`\`\`

**Additional Packages (as needed):**
- \`@mastra/libsql\`: For workflow state persistence
- \`@ai-sdk/openai\`: For AI model integration
- \`ai\`: For AI SDK functionality

### **✅ WORKFLOW BEST PRACTICES**

**Schema Design:**
- Use descriptive property names in schemas
- Make schemas as specific as possible (avoid \`z.any()\`)
- Include validation for required business logic

**Error Handling:**
- Use \`try/catch\` blocks in step execute functions
- Return meaningful error messages
- Consider using \`bail()\` for early successful exits

**Step Organization:**
- Keep steps focused on single responsibilities
- Use descriptive step IDs (kebab-case recommended)
- Create reusable steps for common operations

**Data Flow:**
- Use \`.map()\` when schemas don't align between steps
- Access previous step results with \`getStepResult(stepInstance)\`
- Use \`getInitData()\` to access original workflow input

### **🚀 EXECUTION PATTERNS**

**Running Workflows:**
\`\`\`typescript
// Create and start run
const run = await workflow.createRun();
const result = await run.start({ inputData: {...} });

// Stream execution for real-time monitoring
const stream = await run.streamVNext({ inputData: {...} });
for await (const chunk of stream) {
  console.log(chunk);
}

// Watch for events
run.watch((event) => console.log(event));
\`\`\`

**Workflow Status Types:**
- \`"success"\`: Completed successfully
- \`"suspended"\`: Paused awaiting input
- \`"failed"\`: Encountered error

### **🔗 ADVANCED FEATURES**

**Nested Workflows:**
- Use workflows as steps: \`.then(otherWorkflow)\`
- Enable complex workflow composition

**Request Context:**
- Pass shared data across all steps
- Enable dependency injection patterns

**Streaming & Events:**
- Real-time workflow monitoring
- Integration with external event systems

**Cloning:**
- \`cloneWorkflow(original, {id: "new-id"})\`: Reuse workflow structure
- \`cloneStep(original, {id: "new-id"})\`: Reuse step logic

This comprehensive research provides the foundation for creating robust, maintainable Mastra workflows with proper typing, error handling, and architectural patterns.
`;
const workflowBuilderPrompts = {
	researchAgent: {
		instructions: `You are a Mastra workflow research expert. Your task is to gather relevant information about creating Mastra workflows.

RESEARCH OBJECTIVES:
1. **Core Concepts**: Understand how Mastra workflows work
2. **Best Practices**: Learn workflow patterns and conventions  
3. **Code Examples**: Find relevant implementation examples
4. **Technical Details**: Understand schemas, steps, and configuration

Use the available documentation and examples tools to gather comprehensive information about Mastra workflows.`,
		prompt: (context) => `Research everything about Mastra workflows to help create or edit them effectively.

PROJECT CONTEXT:
- Project Structure: ${JSON.stringify(context.projectStructure, null, 2)}
- Dependencies: ${JSON.stringify(context.dependencies, null, 2)}
- Has Workflows Directory: ${context.hasWorkflowsDir}

Focus on:
1. How to create workflows using createWorkflow()
2. How to create and chain workflow steps
3. Best practices for workflow organization
4. Common workflow patterns and examples
5. Schema definitions and types
6. Error handling and debugging

Use the docs and examples tools to gather comprehensive information.`
	},
	executionAgent: {
		instructions: (context) => `You are executing a workflow ${context.action} task for: "${context.workflowName}"

CRITICAL WORKFLOW EXECUTION REQUIREMENTS:
1. **EXPLORE PROJECT STRUCTURE FIRST**: Use listDirectory and readFile tools to understand the existing project layout, folder structure, and conventions before creating any files
2. **FOLLOW PROJECT CONVENTIONS**: Look at existing workflows, agents, and file structures to understand where new files should be placed (typically src/mastra/workflows/, src/mastra/agents/, etc.)
3. **USE PRE-LOADED TASK LIST**: Your task list has been pre-populated in the taskManager tool. Use taskManager with action 'list' to see all tasks, and action 'update' to mark progress
4. **COMPLETE EVERY SINGLE TASK**: You MUST complete ALL ${context.tasksLength} tasks that are already in the taskManager. Do not stop until every task is marked as 'completed'
5. **Follow Task Dependencies**: Execute tasks in the correct order, respecting dependencies
6. **Request User Input When Needed**: If you encounter choices (like email providers, databases, etc.) that require user decision, return questions for clarification
7. **STRICT WORKFLOW ORGANIZATION**: When creating or editing workflows, you MUST follow this exact structure

MANDATORY WORKFLOW FOLDER STRUCTURE:
When ${context.action === "create" ? "creating a new workflow" : "editing a workflow"}, you MUST organize files as follows:

📁 src/mastra/workflows/${context.workflowName?.toLowerCase().replace(/[^a-z0-9]/g, "-") || "new-workflow"}/
├── 📄 types.ts          # All Zod schemas and TypeScript types
├── 📄 steps.ts          # All individual step definitions  
├── 📄 workflow.ts       # Main workflow composition and export
└── 📄 utils.ts          # Helper functions (if needed)

CRITICAL FILE ORGANIZATION RULES:
- **ALWAYS create a dedicated folder** for the workflow in src/mastra/workflows/
- **Folder name MUST be kebab-case** version of workflow name
- **types.ts**: Define all input/output schemas, validation types, and interfaces
- **steps.ts**: Create all individual step definitions using createStep()
- **workflow.ts**: Compose steps into workflow using createWorkflow() and export the final workflow
- **utils.ts**: Any helper functions, constants, or utilities (create only if needed)
- **NEVER put everything in one file** - always separate concerns properly

CRITICAL COMPLETION REQUIREMENTS: 
- ALWAYS explore the directory structure before creating files to understand where they should go
- You MUST complete ALL ${context.tasksLength} tasks before returning status='completed'
- Use taskManager tool with action 'list' to see your current task list and action 'update' to mark tasks as 'in_progress' or 'completed'
- If you need to make any decisions during implementation (choosing providers, configurations, etc.), return questions for user clarification
- DO NOT make assumptions about file locations - explore first!
- You cannot finish until ALL tasks in the taskManager are marked as 'completed'

PROJECT CONTEXT:
- Action: ${context.action}
- Workflow Name: ${context.workflowName}
- Project Path: ${context.currentProjectPath}
- Discovered Workflows: ${JSON.stringify(context.discoveredWorkflows, null, 2)}
- Project Structure: ${JSON.stringify(context.projectStructure, null, 2)}

AVAILABLE RESEARCH:
${JSON.stringify(context.research, null, 2)}

PRE-LOADED TASK LIST (${context.tasksLength} tasks already in taskManager):
${context.tasks.map((task) => `- ${task.id}: ${task.content} (Priority: ${task.priority})`).join("\n")}

${context.resumeData ? `USER PROVIDED ANSWERS: ${JSON.stringify(context.resumeData.answers, null, 2)}` : ""}

Start by exploring the project structure, then use 'taskManager' with action 'list' to see your pre-loaded tasks, and work through each task systematically.`,
		prompt: (context) => context.resumeData ? `Continue working on the task list. The user has provided answers to your questions: ${JSON.stringify(context.resumeData.answers, null, 2)}. 

CRITICAL: You must complete ALL ${context.tasks.length} tasks that are pre-loaded in the taskManager. Use the taskManager tool with action 'list' to check your progress and continue with the next tasks. Do not stop until every single task is marked as 'completed'.` : `Begin executing the pre-loaded task list to ${context.action} the workflow "${context.workflowName}". 

CRITICAL REQUIREMENTS:
- Your ${context.tasks.length} tasks have been PRE-LOADED into the taskManager tool
- Start by exploring the project directory structure using listDirectory and readFile tools to understand:
  - Where workflows are typically stored (look for src/mastra/workflows/ or similar)
  - What the existing file structure looks like
  - How other workflows are organized and named
  - Where agent files are stored if needed
- Then use taskManager with action 'list' to see your pre-loaded tasks
- Use taskManager with action 'update' to mark tasks as 'in_progress' or 'completed'

CRITICAL FILE ORGANIZATION RULES:
- **ALWAYS create a dedicated folder** for the workflow in src/mastra/workflows/
- **Folder name MUST be kebab-case** version of workflow name  
- **NEVER put everything in one file** - separate types, steps, and workflow composition
- Follow the 4-file structure above for maximum maintainability and clarity

- DO NOT return status='completed' until ALL ${context.tasks.length} tasks are marked as 'completed' in the taskManager

PRE-LOADED TASKS (${context.tasks.length} total tasks in taskManager):
${context.tasks.map((task, index) => `${index + 1}. [${task.id}] ${task.content}`).join("\n")}

Use taskManager with action 'list' to see the current status of all tasks. You must complete every single one before finishing.`,
		iterationPrompt: (context) => `Continue working on the remaining tasks. You have already completed these tasks: [${context.completedTasks.map((t) => t.id).join(", ")}]

REMAINING TASKS TO COMPLETE (${context.pendingTasks.length} tasks):
${context.pendingTasks.map((task, index) => `${index + 1}. [${task.id}] ${task.content}`).join("\n")}

CRITICAL: You must complete ALL of these remaining ${context.pendingTasks.length} tasks. Use taskManager with action 'list' to check current status and action 'update' to mark tasks as completed.

${context.resumeData ? `USER PROVIDED ANSWERS: ${JSON.stringify(context.resumeData.answers, null, 2)}` : ""}`
	},
	validation: { instructions: `CRITICAL VALIDATION INSTRUCTIONS:
- When using the validateCode tool, ALWAYS pass the specific files you created or modified using the 'files' parameter
- The tool uses a hybrid validation approach: fast syntax checking → semantic type checking → ESLint
- This is much faster than full project compilation and only shows errors from your specific files
- Example: validateCode({ validationType: ['types', 'lint'], files: ['src/workflows/my-workflow.ts', 'src/agents/my-agent.ts'] })
- ALWAYS validate after creating or modifying files to ensure they compile correctly` }
};
//#endregion
//#region src/workflows/workflow-builder/tools.ts
const restrictedTaskManager = createTool({
	id: "task-manager",
	description: "View and update your pre-loaded task list. You can only mark tasks as in_progress or completed, not create new tasks.",
	inputSchema: z.object({
		action: z.enum([
			"list",
			"update",
			"complete"
		]).describe("List tasks, update status, or mark complete - tasks are pre-loaded"),
		tasks: z.array(z.object({
			id: z.string().describe("Task ID - must match existing task"),
			content: z.string().optional().describe("Task content (read-only)"),
			status: z.enum([
				"pending",
				"in_progress",
				"completed",
				"blocked"
			]).describe("Task status"),
			priority: z.enum([
				"high",
				"medium",
				"low"
			]).optional().describe("Task priority (read-only)"),
			dependencies: z.array(z.string()).optional().describe("Task dependencies (read-only)"),
			notes: z.string().optional().describe("Additional notes or progress updates")
		})).optional().describe("Tasks to update (status and notes only)"),
		taskId: z.string().optional().describe("Specific task ID for single task operations")
	}),
	outputSchema: z.object({
		success: z.boolean(),
		tasks: z.array(z.object({
			id: z.string(),
			content: z.string(),
			status: z.string(),
			priority: z.string(),
			dependencies: z.array(z.string()).optional(),
			notes: z.string().optional(),
			createdAt: z.string(),
			updatedAt: z.string()
		})),
		message: z.string()
	}),
	execute: async (input) => {
		const adaptedContext = {
			...input,
			action: input.action,
			tasks: input.tasks?.map((task) => ({
				...task,
				priority: task.priority || "medium"
			}))
		};
		return await AgentBuilderDefaults.manageTaskList(adaptedContext);
	}
});
//#endregion
//#region src/workflows/workflow-builder/workflow-builder.ts
const workflowDiscoveryStep = createStep({
	id: "workflow-discovery",
	description: "Discover existing workflows in the project",
	inputSchema: WorkflowBuilderInputSchema,
	outputSchema: WorkflowDiscoveryResultSchema,
	execute: async ({ inputData, requestContext: _requestContext }) => {
		console.info("Starting workflow discovery...");
		const { projectPath = process.cwd() } = inputData;
		try {
			const workflowsPath = join(projectPath, "src/mastra/workflows");
			if (!existsSync(workflowsPath)) {
				console.info("No workflows directory found");
				return {
					success: true,
					workflows: [],
					mastraIndexExists: existsSync(join(projectPath, "src/mastra/index.ts")),
					message: "No existing workflows found in the project"
				};
			}
			const workflowFiles = await readdir(workflowsPath);
			const workflows = [];
			for (const fileName of workflowFiles) if (fileName.endsWith(".ts") && !fileName.endsWith(".test.ts")) {
				const filePath = join(workflowsPath, fileName);
				try {
					const content = await readFile(filePath, "utf-8");
					const nameMatch = content.match(/createWorkflow\s*\(\s*{\s*id:\s*['"]([^'"]+)['"]/);
					const descMatch = content.match(/description:\s*['"]([^'"]*)['"]/);
					if (nameMatch && nameMatch[1]) workflows.push({
						name: nameMatch[1],
						file: filePath,
						description: descMatch?.[1] ?? "No description available"
					});
				} catch (error) {
					console.warn(`Failed to read workflow file ${filePath}:`, error);
				}
			}
			console.info(`Discovered ${workflows.length} existing workflows`);
			return {
				success: true,
				workflows,
				mastraIndexExists: existsSync(join(projectPath, "src/mastra/index.ts")),
				message: workflows.length > 0 ? `Found ${workflows.length} existing workflow(s): ${workflows.map((w) => w.name).join(", ")}` : "No existing workflows found in the project"
			};
		} catch (error) {
			console.error("Workflow discovery failed:", error);
			return {
				success: false,
				workflows: [],
				mastraIndexExists: false,
				message: `Workflow discovery failed: ${error instanceof Error ? error.message : String(error)}`,
				error: error instanceof Error ? error.message : String(error)
			};
		}
	}
});
const projectDiscoveryStep = createStep({
	id: "project-discovery",
	description: "Analyze the project structure and setup",
	inputSchema: WorkflowDiscoveryResultSchema,
	outputSchema: ProjectDiscoveryResultSchema,
	execute: async ({ inputData: _inputData, requestContext: _requestContext }) => {
		console.info("Starting project discovery...");
		try {
			const projectPath = process.cwd();
			const projectStructure = {
				hasPackageJson: existsSync(join(projectPath, "package.json")),
				hasMastraConfig: existsSync(join(projectPath, "mastra.config.js")) || existsSync(join(projectPath, "mastra.config.ts")),
				hasSrcDirectory: existsSync(join(projectPath, "src")),
				hasMastraDirectory: existsSync(join(projectPath, "src/mastra")),
				hasWorkflowsDirectory: existsSync(join(projectPath, "src/mastra/workflows")),
				hasToolsDirectory: existsSync(join(projectPath, "src/mastra/tools")),
				hasAgentsDirectory: existsSync(join(projectPath, "src/mastra/agents"))
			};
			let packageInfo = null;
			if (projectStructure.hasPackageJson) try {
				const packageContent = await readFile(join(projectPath, "package.json"), "utf-8");
				packageInfo = JSON.parse(packageContent);
			} catch (error) {
				console.warn("Failed to read package.json:", error);
			}
			console.info("Project discovery completed");
			return {
				success: true,
				structure: {
					hasWorkflowsDir: projectStructure.hasWorkflowsDirectory,
					hasAgentsDir: projectStructure.hasAgentsDirectory,
					hasToolsDir: projectStructure.hasToolsDirectory,
					hasMastraIndex: existsSync(join(projectPath, "src/mastra/index.ts")),
					existingWorkflows: [],
					existingAgents: [],
					existingTools: []
				},
				dependencies: packageInfo?.dependencies || {},
				message: "Project discovery completed successfully"
			};
		} catch (error) {
			console.error("Project discovery failed:", error);
			return {
				success: false,
				structure: {
					hasWorkflowsDir: false,
					hasAgentsDir: false,
					hasToolsDir: false,
					hasMastraIndex: false,
					existingWorkflows: [],
					existingAgents: [],
					existingTools: []
				},
				dependencies: {},
				message: "Project discovery failed",
				error: error instanceof Error ? error.message : String(error)
			};
		}
	}
});
const workflowResearchStep = createStep({
	id: "workflow-research",
	description: "Research Mastra workflows and gather relevant documentation",
	inputSchema: ProjectDiscoveryResultSchema,
	outputSchema: WorkflowResearchResultSchema,
	execute: async ({ inputData, requestContext }) => {
		console.info("Starting workflow research...");
		try {
			const researchAgent = new Agent({
				id: "workflow-research-agent",
				model: await resolveModel({ requestContext }),
				instructions: workflowBuilderPrompts.researchAgent.instructions,
				name: "Workflow Research Agent"
			});
			const researchPrompt = workflowBuilderPrompts.researchAgent.prompt({
				projectStructure: inputData.structure,
				dependencies: inputData.dependencies,
				hasWorkflowsDir: inputData.structure.hasWorkflowsDir
			});
			const researchResult = await (await researchAgent.generate(researchPrompt, { structuredOutput: { schema: WorkflowResearchResultSchema } })).object;
			if (!researchResult) return {
				success: false,
				documentation: {
					workflowPatterns: [],
					stepExamples: [],
					bestPractices: []
				},
				webResources: [],
				message: "Research agent failed to generate valid response",
				error: "Research agent failed to generate valid response"
			};
			console.info("Research completed successfully");
			return {
				success: true,
				documentation: {
					workflowPatterns: researchResult.documentation.workflowPatterns,
					stepExamples: researchResult.documentation.stepExamples,
					bestPractices: researchResult.documentation.bestPractices
				},
				webResources: researchResult.webResources,
				message: "Research completed successfully"
			};
		} catch (error) {
			console.error("Workflow research failed:", error);
			return {
				success: false,
				documentation: {
					workflowPatterns: [],
					stepExamples: [],
					bestPractices: []
				},
				webResources: [],
				message: "Research failed",
				error: error instanceof Error ? error.message : String(error)
			};
		}
	}
});
const taskExecutionStep = createStep({
	id: "task-execution",
	description: "Execute the approved task list to create or edit the workflow",
	inputSchema: TaskExecutionInputSchema,
	outputSchema: TaskExecutionResultSchema,
	suspendSchema: TaskExecutionSuspendSchema,
	resumeSchema: TaskExecutionResumeSchema,
	execute: async ({ inputData, resumeData, suspend, requestContext }) => {
		const { action, workflowName, description: _description, requirements: _requirements, tasks, discoveredWorkflows, projectStructure, research, projectPath } = inputData;
		console.info(`Starting task execution for ${action}ing workflow: ${workflowName}`);
		console.info(`Executing ${tasks.length} tasks using AgentBuilder stream...`);
		try {
			const model = await resolveModel({ requestContext });
			const currentProjectPath = projectPath || process.cwd();
			console.info("Pre-populating taskManager with planned tasks...");
			const taskManagerContext = {
				action: "create",
				tasks: tasks.map((task) => ({
					id: task.id,
					content: task.content,
					status: "pending",
					priority: task.priority,
					dependencies: task.dependencies,
					notes: task.notes
				}))
			};
			const taskManagerResult = await AgentBuilderDefaults.manageTaskList(taskManagerContext);
			console.info(`Task manager initialized with ${taskManagerResult.tasks.length} tasks`);
			if (!taskManagerResult.success) throw new Error(`Failed to initialize task manager: ${taskManagerResult.message}`);
			const executionAgent = new AgentBuilder({
				projectPath: currentProjectPath,
				model,
				tools: { "task-manager": restrictedTaskManager },
				instructions: `${workflowBuilderPrompts.executionAgent.instructions({
					action,
					workflowName,
					tasksLength: tasks.length,
					currentProjectPath,
					discoveredWorkflows,
					projectStructure,
					research,
					tasks,
					resumeData
				})}

${workflowBuilderPrompts.validation.instructions}`
			});
			const executionPrompt = workflowBuilderPrompts.executionAgent.prompt({
				action,
				workflowName,
				tasks,
				resumeData
			});
			const originalInstructions = await executionAgent.getInstructions({ requestContext });
			const enhancedOptions = {
				stopWhen: stepCountIs(100),
				temperature: .3,
				instructions: originalInstructions
			};
			let finalResult = null;
			let allTasksCompleted = false;
			let iterationCount = 0;
			const maxIterations = 5;
			const expectedTaskIds = tasks.map((task) => task.id);
			while (!allTasksCompleted && iterationCount < maxIterations) {
				iterationCount++;
				const currentTaskStatus = await AgentBuilderDefaults.manageTaskList({ action: "list" });
				const completedTasks = currentTaskStatus.tasks.filter((task) => task.status === "completed");
				const pendingTasks = currentTaskStatus.tasks.filter((task) => task.status !== "completed");
				console.info(`\n=== EXECUTION ITERATION ${iterationCount} ===`);
				console.info(`Completed tasks: ${completedTasks.length}/${expectedTaskIds.length}`);
				console.info(`Remaining tasks: ${pendingTasks.map((t) => t.id).join(", ")}`);
				allTasksCompleted = pendingTasks.length === 0;
				if (allTasksCompleted) {
					console.info("All tasks completed! Breaking execution loop.");
					break;
				}
				const iterationPrompt = iterationCount === 1 ? executionPrompt : `${workflowBuilderPrompts.executionAgent.iterationPrompt({
					completedTasks,
					pendingTasks,
					workflowName,
					resumeData
				})}

${workflowBuilderPrompts.validation.instructions}`;
				const stream = await executionAgent.stream(iterationPrompt, {
					structuredOutput: {
						schema: TaskExecutionIterationInputSchema(tasks.length),
						model
					},
					...enhancedOptions
				});
				let finalMessage = "";
				for await (const chunk of stream.fullStream) {
					if (chunk.type === "text-delta") finalMessage += chunk.payload.text;
					if (chunk.type === "step-finish") {
						console.info(finalMessage);
						finalMessage = "";
					}
					if (chunk.type === "tool-result") console.info(JSON.stringify(chunk, null, 2));
					if (chunk.type === "finish") console.info(chunk);
				}
				await stream.consumeStream();
				finalResult = await stream.object;
				console.info(`Iteration ${iterationCount} result:`, { finalResult });
				if (!finalResult) throw new Error(`No result received from agent execution on iteration ${iterationCount}`);
				const postIterationTaskStatus = await AgentBuilderDefaults.manageTaskList({ action: "list" });
				const postCompletedTasks = postIterationTaskStatus.tasks.filter((task) => task.status === "completed");
				const postPendingTasks = postIterationTaskStatus.tasks.filter((task) => task.status !== "completed");
				allTasksCompleted = postPendingTasks.length === 0;
				console.info(`After iteration ${iterationCount}: ${postCompletedTasks.length}/${expectedTaskIds.length} tasks completed in taskManager`);
				if (finalResult.status === "needs_clarification" && finalResult.questions && finalResult.questions.length > 0) {
					console.info(`Agent needs clarification on iteration ${iterationCount}: ${finalResult.questions.length} questions`);
					break;
				}
				if (finalResult.status === "completed" && !allTasksCompleted) console.info(`Agent claimed completion but taskManager shows pending tasks: ${postPendingTasks.map((t) => t.id).join(", ")}`);
			}
			if (iterationCount >= maxIterations && !allTasksCompleted) {
				finalResult.error = `Maximum iterations (${maxIterations}) reached but not all tasks completed`;
				finalResult.status = "in_progress";
			}
			if (!finalResult) throw new Error("No result received from agent execution");
			if (finalResult.status === "needs_clarification" && finalResult.questions && finalResult.questions.length > 0) {
				console.info(`Agent needs clarification: ${finalResult.questions.length} questions`);
				console.info("finalResult", JSON.stringify(finalResult, null, 2));
				return suspend({
					questions: finalResult.questions,
					currentProgress: finalResult.progress,
					completedTasks: finalResult.completedTasks || [],
					message: finalResult.message
				});
			}
			const finalTaskStatus = await AgentBuilderDefaults.manageTaskList({ action: "list" });
			const finalCompletedTasks = finalTaskStatus.tasks.filter((task) => task.status === "completed");
			const finalPendingTasks = finalTaskStatus.tasks.filter((task) => task.status !== "completed");
			const tasksCompleted = finalCompletedTasks.length;
			const tasksExpected = expectedTaskIds.length;
			const finalAllTasksCompleted = finalPendingTasks.length === 0;
			const success = finalAllTasksCompleted && !finalResult.error;
			const message = success ? `Successfully completed workflow ${action} - all ${tasksExpected} tasks completed after ${iterationCount} iteration(s): ${finalResult.message}` : `Workflow execution finished with issues after ${iterationCount} iteration(s): ${finalResult.message}. Completed: ${tasksCompleted}/${tasksExpected} tasks`;
			console.info(message);
			const missingTasks = finalPendingTasks.map((task) => task.id);
			const validationErrors = [];
			if (finalResult.error) validationErrors.push(finalResult.error);
			if (!finalAllTasksCompleted) validationErrors.push(`Incomplete tasks: ${missingTasks.join(", ")} (${tasksCompleted}/${tasksExpected} completed)`);
			return {
				success,
				completedTasks: finalCompletedTasks.map((task) => task.id),
				filesModified: finalResult.filesModified || [],
				validationResults: {
					passed: success,
					errors: validationErrors,
					warnings: finalAllTasksCompleted ? [] : [`Missing ${missingTasks.length} tasks: ${missingTasks.join(", ")}`]
				},
				message,
				error: finalResult.error
			};
		} catch (error) {
			console.error("Task execution failed:", error);
			return {
				success: false,
				completedTasks: [],
				filesModified: [],
				validationResults: {
					passed: false,
					errors: [`Task execution failed: ${error instanceof Error ? error.message : String(error)}`],
					warnings: []
				},
				message: `Task execution failed: ${error instanceof Error ? error.message : String(error)}`,
				error: error instanceof Error ? error.message : String(error)
			};
		}
	}
});
const workflowBuilderWorkflow = createWorkflow({
	id: "workflow-builder",
	description: "Create or edit Mastra workflows using AI-powered assistance with iterative planning",
	inputSchema: WorkflowBuilderInputSchema,
	outputSchema: WorkflowBuilderResultSchema,
	steps: [
		workflowDiscoveryStep,
		projectDiscoveryStep,
		workflowResearchStep,
		planningAndApprovalWorkflow,
		taskExecutionStep
	]
}).then(workflowDiscoveryStep).then(projectDiscoveryStep).then(workflowResearchStep).map(async ({ getStepResult, getInitData }) => {
	const initData = getInitData();
	const discoveryResult = getStepResult(workflowDiscoveryStep);
	const projectResult = getStepResult(projectDiscoveryStep);
	return {
		action: initData.action,
		workflowName: initData.workflowName,
		description: initData.description,
		requirements: initData.requirements,
		discoveredWorkflows: discoveryResult.workflows,
		projectStructure: projectResult,
		research: workflowResearch,
		userAnswers: void 0
	};
}).dountil(planningAndApprovalWorkflow, async ({ inputData }) => {
	console.info(`Sub-workflow check: approved=${inputData.approved}`);
	return inputData.approved === true;
}).map(async ({ getStepResult, getInitData }) => {
	const initData = getInitData();
	const discoveryResult = getStepResult(workflowDiscoveryStep);
	const projectResult = getStepResult(projectDiscoveryStep);
	const subWorkflowResult = getStepResult(planningAndApprovalWorkflow);
	return {
		action: initData.action,
		workflowName: initData.workflowName,
		description: initData.description,
		requirements: initData.requirements,
		tasks: subWorkflowResult.tasks,
		discoveredWorkflows: discoveryResult.workflows,
		projectStructure: projectResult,
		research: workflowResearch,
		projectPath: initData.projectPath || process.cwd()
	};
}).then(taskExecutionStep).commit();
//#endregion
//#region src/workflows/workflow-map.ts
const agentBuilderWorkflows = {
	"merge-template": agentBuilderTemplateWorkflow,
	"workflow-builder": workflowBuilderWorkflow
};
//#endregion
export { AgentBuilder, AgentBuilderDefaults, agentBuilderTemplateWorkflow, agentBuilderWorkflows, mergeTemplateBySlug, planningAndApprovalWorkflow, workflowBuilderWorkflow };

//# sourceMappingURL=index.js.map